import { useState, useEffect, useCallback, useRef } from "react";
import * as Ably from "ably";

const API = process.env.REACT_APP_API_URL || '/api';
const ABLY_AUTH_URL = (process.env.REACT_APP_API_URL || '/api') + '/ably/token';

// ─── Utility ─────────────────────────────────────────────────────────────────
// The server requires the shared ADMIN_TOKEN on all mutations + admin GETs.
// Stored in localStorage; on a 401 we prompt once and retry.
export function adminHeaders() {
  const t = localStorage.getItem('adminToken');
  return t ? { 'X-Admin-Token': t } : {};
}

async function apiFetch(path, opts = {}) {
  const doFetch = () => fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...adminHeaders(), ...opts.headers },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const usedToken = localStorage.getItem('adminToken') || '';
  let res = await doFetch();
  if (res.status === 401) {
    // Parallel requests 401 together on first load — if another request's
    // prompt already stored a fresh token, retry silently instead of re-asking.
    if ((localStorage.getItem('adminToken') || '') !== usedToken) {
      res = await doFetch();
    } else {
      const entered = window.prompt('Admin token required (server ADMIN_TOKEN):');
      if (entered && entered.trim()) {
        localStorage.setItem('adminToken', entered.trim());
        res = await doFetch();
      }
    }
  }
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// CSV downloads can't be plain <a href> links: the admin token travels in a
// HEADER, and a browser navigation sends none — the server answered every CSV
// click with 401 "admin token required". So fetch it like any other admin call,
// then hand the browser a Blob to save. Mirrors apiFetch's 401 prompt+retry.
async function downloadCsv(path, filename) {
  const doFetch = () => fetch(`${API}${path}`, { headers: adminHeaders() });
  const usedToken = localStorage.getItem('adminToken') || '';
  let res = await doFetch();
  if (res.status === 401) {
    if ((localStorage.getItem('adminToken') || '') !== usedToken) {
      res = await doFetch();
    } else {
      const entered = window.prompt('Admin token required (server ADMIN_TOKEN):');
      if (entered && entered.trim()) {
        localStorage.setItem('adminToken', entered.trim());
        res = await doFetch();
      }
    }
  }
  if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`);

  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ─── Small UI primitives ──────────────────────────────────────────────────────
// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  bg:       '#080B14',
  surface:  '#0D1320',
  raised:   '#111827',
  border:   'rgba(255,255,255,.07)',
  borderHi: 'rgba(255,255,255,.13)',
  text:     '#E2E8F0',
  muted:    '#4A5568',
  accent:   '#6366F1',   // indigo
  green:    '#10B981',
  amber:    '#F59E0B',
  red:      '#EF4444',
  blue:     '#3B82F6',
};

function Card({ children, style, accent }) {
  return (
    <div style={{
      background: T.surface,
      border: `1px solid ${accent ? accent + '44' : T.border}`,
      borderRadius: 16,
      padding: '22px 26px',
      boxShadow: accent
        ? `0 0 0 1px ${accent}14, 0 4px 32px rgba(0,0,0,.5)`
        : '0 4px 24px rgba(0,0,0,.4)',
      ...style,
    }}>{children}</div>
  );
}

function Btn({ children, onClick, variant = 'primary', disabled, style }) {
  const variants = {
    primary: { bg: `linear-gradient(135deg, ${T.green}, #059669)`, color: T.text, shadow: `0 4px 14px ${T.green}40` },
    danger:  { bg: `linear-gradient(135deg, ${T.red}, #DC2626)`,   color: T.text, shadow: `0 4px 14px ${T.red}40`   },
    ghost:   { bg: 'rgba(255,255,255,.05)', color: T.muted, border: `1px solid ${T.border}`, shadow: 'none' },
    amber:   { bg: `linear-gradient(135deg, ${T.amber}, #D97706)`, color: '#000', shadow: `0 4px 14px ${T.amber}40` },
    blue:    { bg: `linear-gradient(135deg, ${T.blue}, #2563EB)`,  color: T.text, shadow: `0 4px 14px ${T.blue}40`  },
    indigo:  { bg: `linear-gradient(135deg, ${T.accent}, #4F46E5)`, color: T.text, shadow: `0 4px 14px ${T.accent}40` },
  };
  const v = variants[variant] || variants.primary;
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background:   disabled ? T.raised : v.bg,
      color:        disabled ? T.muted  : v.color,
      border:       v.border || 'none',
      borderRadius: 9,
      padding:      '8px 18px',
      fontWeight:   700,
      fontSize:     13,
      cursor:       disabled ? 'not-allowed' : 'pointer',
      transition:   'all .15s ease',
      boxShadow:    disabled ? 'none' : v.shadow,
      fontFamily:   'inherit',
      letterSpacing: '.3px',
      ...style,
    }}>{children}</button>
  );
}

function Input({ label, value, onChange, placeholder, type = 'text', style }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, ...style }}>
      {label && <label style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase' }}>{label}</label>}
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          background:  T.raised,
          border:      `1px solid ${T.border}`,
          borderRadius: 9,
          padding:     '9px 13px',
          color:       T.text,
          fontSize:    14,
          outline:     'none',
          transition:  'border-color .15s',
          fontFamily:  'inherit',
        }}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, style }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, ...style }}>
      {label && <label style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase' }}>{label}</label>}
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          background:   T.raised,
          border:       `1px solid ${T.border}`,
          borderRadius: 9,
          padding:      '9px 13px',
          color:        T.text,
          fontSize:     14,
          outline:      'none',
          fontFamily:   'inherit',
        }}
      >
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function Badge({ children, color = T.muted }) {
  return (
    <span style={{
      background: color + '22',
      color,
      border: `1px solid ${color}44`,
      borderRadius: 20,
      padding: '3px 11px',
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '.6px',
    }}>{children}</span>
  );
}

// ─── Toggle Switch ────────────────────────────────────────────────────────────
function ToggleSwitch({ value, onChange, label, disabled }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? .5 : 1 }}
      onClick={() => !disabled && onChange(!value)}>
      <div style={{
        width: 36, height: 20, borderRadius: 10,
        background: value ? T.green : T.raised,
        border: `1px solid ${value ? T.green : T.border}`,
        position: 'relative', transition: 'all .2s', flexShrink: 0,
      }}>
        <div style={{
          position: 'absolute', top: 3, left: value ? 19 : 3,
          width: 14, height: 14, borderRadius: '50%',
          background: value ? '#fff' : T.muted,
          transition: 'left .2s',
        }} />
      </div>
      {label && <span style={{ fontSize: 11, color: value ? T.green : T.muted, fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase', transition: 'color .2s' }}>{label}</span>}
    </div>
  );
}

// ─── Broadcast Panel (global) ─────────────────────────────────────────────────
function BroadcastPanel() {
  const [message,     setMessage]     = useState('');
  const [durationSec, setDurationSec] = useState(10);
  const [sending,     setSending]     = useState(false);
  const [feedback,    setFeedback]    = useState('');

  const send = async () => {
    if (!message.trim()) return;
    setSending(true); setFeedback('');
    try {
      await apiFetch('/admin/broadcast', { method: 'POST', body: { message: message.trim(), durationMs: Number(durationSec) * 1000 } });
      setFeedback(`✓ Sent (${durationSec}s)`);
      setMessage('');
      setTimeout(() => setFeedback(''), 3000);
    } catch (e) { setFeedback(`✗ ${e.message}`); }
    setSending(false);
  };

  return (
    <Card accent={T.amber} style={{ marginBottom: 20 }}>
      <div style={{ fontWeight: 700, color: T.amber, fontSize: 13, letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: 14 }}>
        📢 Broadcast Notification
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: 3, minWidth: 200 }}>
          <label style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Message</label>
          <input
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Type a message to show on all screens…"
            onKeyDown={e => e.key === 'Enter' && send()}
            style={{ width: '100%', background: T.raised, border: `1px solid ${T.border}`, borderRadius: 9, padding: '9px 13px', color: T.text, fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
          />
        </div>
        <div style={{ minWidth: 100 }}>
          <label style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Duration (s)</label>
          <input
            type="number" min={2} max={120} value={durationSec}
            onChange={e => setDurationSec(Number(e.target.value) || 10)}
            style={{ width: '100%', background: T.raised, border: `1px solid ${T.border}`, borderRadius: 9, padding: '9px 13px', color: T.text, fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
          />
        </div>
        <Btn variant="amber" onClick={send} disabled={sending || !message.trim()} style={{ opacity: (sending || !message.trim()) ? 0.5 : 1 }}>
          {sending ? '…' : '📢 Broadcast'}
        </Btn>
        {feedback && <span style={{ fontSize: 12, color: feedback.startsWith('✓') ? T.green : T.red, fontWeight: 700, alignSelf: 'center' }}>{feedback}</span>}
      </div>
    </Card>
  );
}

// ─── Team Roster Panel ────────────────────────────────────────────────────────
function TeamRosterPanel({ game, gameId, gameType, onRefresh }) {
  const [saving, setSaving] = useState(false);
  const teams  = game.teams || [];
  const scores = game.scores || {};
  const gameBase = gameType === 'droppan' ? '/droppan' : gameType === 'americasays' ? '/americasays' : '/games';

  const grouped = {};
  teams.forEach(t => { grouped[t.id] = { team: t, players: [] }; });
  const unassigned = [];

  Object.entries(scores).forEach(([key, s]) => {
    const tid = s.teamId;
    if (tid && grouped[tid]) {
      grouped[tid].players.push({ _key: key, ...s });
    } else {
      unassigned.push({ _key: key, ...s });
    }
  });

  const reassign = async (playerKey, newTeamId) => {
    setSaving(true);
    try {
      await apiFetch(`${gameBase}/${gameId}/players/${encodeURIComponent(playerKey)}/team`, {
        method: 'PUT',
        body: { teamId: newTeamId },
      });
      onRefresh();
    } catch (e) {
      alert(`Error: ${e.message}`);
    }
    setSaving(false);
  };

  if (Object.keys(scores).length === 0 && teams.length === 0) return null;

  return (
    <div style={{
      marginTop: 10, padding: '10px 14px',
      background: 'rgba(255,255,255,.02)', borderRadius: 8,
      border: `1px solid ${T.border}`,
    }}>
      <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: '.8px', textTransform: 'uppercase', marginBottom: 8 }}>
        Team Roster {saving && <span style={{ color: T.amber }}>Saving…</span>}
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {Object.values(grouped).map(({ team, players }) => (
          <div key={team.id} style={{ minWidth: 160, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: team.color, flexShrink: 0 }} />
              <span style={{ color: team.color, fontSize: 11, fontWeight: 700 }}>{team.name}</span>
              <span style={{ color: T.muted, fontSize: 10 }}>({players.length})</span>
            </div>
            {players.length === 0
              ? <div style={{ fontSize: 10, color: T.muted, fontStyle: 'italic', paddingLeft: 13 }}>—</div>
              : players.map(p => (
                <div key={p._key} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3, paddingLeft: 13 }}>
                  <span style={{ fontSize: 11, color: T.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.name || p._key}
                  </span>
                  <span style={{ fontSize: 10, color: T.green, flexShrink: 0 }}>{p.score}pts</span>
                  <select
                    value={p.teamId || ''}
                    onChange={e => reassign(p._key, e.target.value)}
                    disabled={saving}
                    style={{
                      background: T.raised, border: `1px solid ${T.border}`, borderRadius: 5,
                      padding: '2px 5px', color: T.text, fontSize: 10, cursor: 'pointer', flexShrink: 0,
                    }}
                  >
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              ))
            }
          </div>
        ))}
        {unassigned.length > 0 && (
          <div style={{ minWidth: 160 }}>
            <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, marginBottom: 5 }}>Unassigned</div>
            {unassigned.map(p => (
              <div key={p._key} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
                <span style={{ fontSize: 11, color: T.text, flex: 1 }}>{p.name || p._key}</span>
                <select
                  value=""
                  onChange={e => reassign(p._key, e.target.value)}
                  disabled={saving}
                  style={{
                    background: T.raised, border: `1px solid ${T.border}`, borderRadius: 5,
                    padding: '2px 5px', color: T.text, fontSize: 10, cursor: 'pointer',
                  }}
                >
                  <option value="">— assign —</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Section: Dashboard ───────────────────────────────────────────────────────
function Dashboard({ games, bridgeConnected, onRefresh }) {
  const active    = Object.values(games).filter(g => g.status === 'active').length;
  const waiting   = Object.values(games).filter(g => g.status === 'waiting').length;
  const completed = Object.values(games).filter(g => g.status === 'completed').length;
  const asCount   = Object.values(games).filter(g => g.gameType === 'americasays').length;
  const ggCount   = Object.values(games).filter(g => g.gameType === 'geoguess').length;
  const cwCount   = Object.values(games).filter(g => g.gameType === 'crossword').length;
  const dpCount   = Object.values(games).filter(g => g.gameType === 'droppan').length;
  const crCount   = Object.values(games).filter(g => g.gameType === 'chainreaction').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="adm-grid-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 12 }}>
        {[
          { label: 'Bridge Status',      value: bridgeConnected ? 'Online' : 'Offline', icon: bridgeConnected ? '🟢' : '🔴', color: bridgeConnected ? T.green : T.red },
          { label: 'Active Games',       value: active,    icon: '⚡', color: T.green  },
          { label: 'Waiting',            value: waiting,   icon: '⏳', color: T.amber  },
          { label: '✏️ Crossword',       value: cwCount,   icon: null, color: T.blue   },
          { label: '🎯 America Says',    value: asCount,   icon: null, color: T.accent },
          { label: '🗺️ GeoGuess',       value: ggCount,   icon: null, color: T.green  },
          { label: '🎴 Drop Pan',        value: dpCount,   icon: null, color: '#FFB300' },
          { label: '🔗 Chain Reaction',  value: crCount,   icon: null, color: '#A78BFA' },
        ].map(stat => (
          <Card key={stat.label} style={{ padding: '18px 20px' }}>
            <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 10 }}>{stat.label}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: stat.color, display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'Sora', sans-serif" }}>
              {stat.icon && <span style={{ fontSize: 16 }}>{stat.icon}</span>}
              {stat.value}
            </div>
          </Card>
        ))}
      </div>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h2 style={{ fontSize: 15, color: T.text, fontWeight: 700, letterSpacing: '.3px' }}>All Games</h2>
          <Btn variant="ghost" onClick={onRefresh} style={{ padding: '6px 12px', fontSize: 12 }}>↻ Refresh</Btn>
        </div>
        {Object.values(games).length === 0 ? (
          <p style={{ color: T.muted, fontSize: 14 }}>No games yet. Create one below.</p>
        ) : (
          <div className="adm-tablewrap">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                {['Game ID', 'Name', 'Type / Mode', 'Status', 'Players', 'Actions'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 10, color: T.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.values(games).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)).map(game => (
                <GameRow key={game.id} game={game} onRefresh={onRefresh} />
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function GameRow({ game, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const playerCount = Object.keys(game.scores || {}).length;
  const statusColors = { waiting: T.amber, active: T.green, completed: T.muted };
  const isAS = game.gameType === 'americasays';
  const isGG = game.gameType === 'geoguess';
  const isDP = game.gameType === 'droppan';
  const isCR = game.gameType === 'chainreaction';
  const gameBase = isCR ? '/chainreaction' : isDP ? '/droppan' : isGG ? '/geoguess' : isAS ? '/americasays' : '/games';
  const gameType = isCR ? 'chainreaction' : isDP ? 'droppan' : isGG ? 'geoguess' : isAS ? 'americasays' : 'crossword';
  const isTeamGame = (isDP && game.teamMode) || (isAS && game.mode === 'teams') || (isCR && game.teams?.length > 0) || (!isAS && !isGG && !isDP && !isCR && game.mode !== 'solo');
  const showRoster = isTeamGame && (game.teams?.length > 0);

  const action = async (endpoint, method = 'POST') => {
    setLoading(true);
    try {
      await apiFetch(`${gameBase}/${game.id}/${endpoint}`, { method });
      onRefresh();
    } catch (e) {
      const msg = e.message || '';
      if (msg.includes('intro overlay') || msg.includes('hold phase')) {
        alert('⏳ Cannot reveal yet — the question intro is still showing.\nWait for the countdown to finish before revealing answers.');
      } else {
        alert(`Error: ${msg}`);
      }
    }
    setLoading(false);
  };

  const deleteGame = async () => {
    if (!window.confirm(`Delete game "${game.name || game.id}"? This cannot be undone.`)) return;
    setLoading(true);
    try {
      await apiFetch(`${gameBase}/${game.id}`, { method: 'DELETE' });
      onRefresh();
    } catch (e) {
      alert(`Error: ${e.message}`);
    }
    setLoading(false);
  };

  const displayUrl = isCR
    ? `/?game=${game.id}&type=chainreaction&view=display`
    : isDP
      ? `/?game=${game.id}&type=droppan&view=display`
      : isGG
        ? `/?game=${game.id}&type=geoguess&view=display`
        : isAS
          ? `/?game=${game.id}&type=americasays&view=display`
          : `/?game=${game.id}&view=display`;

  const badgeColor = isCR ? '#A78BFA' : isDP ? '#FFB300' : isGG ? '#10B981' : isAS ? T.amber : T.blue;
  const badgeLabel = isCR ? '🔗 Chain Reaction' : isDP ? '🎴 Drop Pan' : isGG ? '🗺️ GeoGuess' : isAS ? '🎯 America Says' : '✏️ Crossword';
  const idColor    = isCR ? '#A78BFA' : isDP ? '#FFB300' : isGG ? T.green : isAS ? T.amber : T.green;

  return (
    <>
    <tr style={{ borderBottom: showRoster ? 'none' : `1px solid ${T.border}`, transition: 'background .15s' }}>
      <td style={{ padding: '11px 12px' }}>
        <code style={{
          color: idColor, fontSize: 12,
          background: idColor + '14',
          padding: '3px 9px', borderRadius: 6, fontWeight: 700,
        }}>{game.id}</code>
      </td>
      <td style={{ padding: '11px 12px', color: T.text, fontSize: 14, fontWeight: 600 }}>{game.name || '—'}</td>
      <td style={{ padding: '11px 12px' }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Badge color={badgeColor}>{badgeLabel}</Badge>
          {!isGG && !isDP && !isCR && <Badge color={T.muted}>{game.mode || 'solo'}</Badge>}
          {isDP && game.mode && <Badge color={T.muted}>{game.mode}</Badge>}
          {isCR && game.mode && <Badge color={T.muted}>{game.mode}</Badge>}
          {isTeamGame && game.teams?.length > 0 && <Badge color={T.amber}>🏆 {game.teams.length} teams</Badge>}
        </div>
      </td>
      <td style={{ padding: '11px 12px' }}>
        <Badge color={statusColors[game.status] || T.muted}>{game.status}</Badge>
      </td>
      <td style={{ padding: '11px 12px', color: T.muted, fontSize: 13 }}>{playerCount}</td>
      <td style={{ padding: '11px 12px' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {game.status === 'waiting' && (
            <Btn variant="primary" onClick={() => action('start')} disabled={loading} style={{ padding: '5px 12px', fontSize: 12 }}>▶ Start</Btn>
          )}
          {game.status === 'active' && (isGG || isDP) && (
            <Btn variant="blue" onClick={() => action('next')} disabled={loading} style={{ padding: '5px 12px', fontSize: 12 }}>⏭ Next</Btn>
          )}
          {game.status === 'active' && (isGG || isDP) && (
            <Btn variant="amber" onClick={() => action('reveal')} disabled={loading} style={{ padding: '5px 12px', fontSize: 12 }}>👁 Reveal</Btn>
          )}
          {game.status === 'active' && isAS && (
            <Btn variant="amber" onClick={() => action('next')} disabled={loading} style={{ padding: '5px 12px', fontSize: 12 }}>⏭ Next Q</Btn>
          )}
          {game.status === 'active' && isAS && (
            <Btn variant="blue" onClick={() => action('reveal-unanswered')} disabled={loading} style={{ padding: '5px 12px', fontSize: 12 }} title="Reveal all unanswered answers for current question">👁 Reveal All</Btn>
          )}
          {game.status === 'active' && !isAS && !isGG && !isDP && !isCR && (
            <Btn variant="blue" onClick={() => action('reveal-all')} disabled={loading} style={{ padding: '5px 12px', fontSize: 12 }} title="Reveal all unfilled answers on the board">👁 Reveal All</Btn>
          )}
          {game.status === 'active' && (
            <Btn variant="danger" onClick={() => action('end')} disabled={loading} style={{ padding: '5px 12px', fontSize: 12 }}>■ End</Btn>
          )}
          <Btn variant="ghost" onClick={() => window.open(displayUrl, '_blank')} style={{ padding: '5px 12px', fontSize: 12 }}>📺 Display</Btn>
          <Btn variant="ghost" onClick={() => window.open('/tv', '_blank')} style={{ padding: '5px 12px', fontSize: 12 }} title="Open dedicated TV/projector screen (auto-switches between games)">📡 TV</Btn>
          {game.status !== 'active' && (
            <Btn variant="danger" onClick={deleteGame} disabled={loading} style={{ padding: '5px 10px', fontSize: 12, opacity: 0.7 }}>🗑</Btn>
          )}
        </div>
      </td>
    </tr>
    {showRoster && (
      <tr style={{ borderBottom: `1px solid ${T.border}` }}>
        <td colSpan={6} style={{ padding: '0 12px 10px', background: 'rgba(255,255,255,.01)' }}>
          <TeamRosterPanel game={game} gameId={game.id} gameType={gameType} onRefresh={onRefresh} />
        </td>
      </tr>
    )}
    </>
  );
}

// ─── Section: Create Game ─────────────────────────────────────────────────────
function CreateGame({ puzzles, onCreated }) {
  const [form, setForm] = useState({
    puzzleId: '',
    name: '',
    mode: 'solo',
    pointsPerAnswer: 10,
    timerDuration: '',
    revealMode: 'manual',
  });
  const [teams, setTeams]   = useState([
    { id: 'team-a', name: 'Team A', color: T.blue },
    { id: 'team-b', name: 'Team B', color: T.red },
  ]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [created, setCreated] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.puzzleId) return setError('Select a puzzle');
    setLoading(true);
    setError('');
    try {
      const payload = {
        ...form,
        pointsPerAnswer: parseInt(form.pointsPerAnswer, 10),
        teams: form.mode !== 'solo' ? teams : [],
        timerDuration: form.timerDuration ? parseInt(form.timerDuration, 10) : null,
        revealMode: form.revealMode || 'manual',
      };
      const res = await apiFetch('/games', { method: 'POST', body: payload });
      setCreated(res.gameId);
      onCreated();
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const addTeam = () => setTeams(t => [...t, { id: `team-${Date.now()}`, name: `Team ${t.length + 1}`, color: T.muted }]);
  const removeTeam = (id) => setTeams(t => t.filter(x => x.id !== id));
  const updateTeam = (id, field, value) => setTeams(t => t.map(x => x.id === id ? { ...x, [field]: value } : x));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Card>
        <h2 style={{ fontSize: 15, color: T.text, fontWeight: 800, marginBottom: 20 }}>Create New Game</h2>
        <div className="adm-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <Select
            label="Puzzle"
            value={form.puzzleId}
            onChange={v => set('puzzleId', v)}
            options={[
              { value: '', label: '— Select puzzle —' },
              ...puzzles.map(p => ({ value: p.id, label: `${p.title} (${p.id})` })),
            ]}
          />
          <Input label="Game Name" value={form.name} onChange={v => set('name', v)} placeholder="e.g. Friday Night Crossword" />
          <Select
            label="Mode"
            value={form.mode}
            onChange={v => set('mode', v)}
            options={[
              { value: 'solo',  label: 'Solo — individuals compete' },
              { value: 'teams', label: 'Teams — grouped competition' },
              { value: 'mixed', label: 'Mixed — individuals in teams' },
            ]}
          />
          <Input label="Points Per Answer" value={form.pointsPerAnswer} onChange={v => set('pointsPerAnswer', v)} type="number" />
          <Input
            label="Timer (seconds, optional)"
            value={form.timerDuration}
            onChange={v => set('timerDuration', v)}
            type="number"
            placeholder="e.g. 300 = 5 min, blank = no timer"
          />
          <Select
            label="Reveal Mode"
            value={form.revealMode}
            onChange={v => set('revealMode', v)}
            options={[
              { value: 'manual', label: 'Manual — admin presses Reveal All' },
              { value: 'auto',   label: 'Auto — reveal when timer expires' },
            ]}
          />
        </div>

        {form.mode !== 'solo' && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <span style={{ fontSize: 13, color: T.muted, fontWeight: 600 }}>TEAMS</span>
              <Btn variant="ghost" onClick={addTeam} style={{ padding: '4px 10px', fontSize: 11 }}>+ Add Team</Btn>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {teams.map(team => (
                <div key={team.id} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input
                    type="color"
                    value={team.color}
                    onChange={e => updateTeam(team.id, 'color', e.target.value)}
                    style={{ width: 36, height: 36, border: 'none', borderRadius: 6, cursor: 'pointer', background: 'none' }}
                  />
                  <input
                    value={team.name}
                    onChange={e => updateTeam(team.id, 'name', e.target.value)}
                    style={{
                      flex: 1, background: T.raised, border: `1px solid ${T.border}`,
                      borderRadius: 8, padding: '8px 12px', color: T.text, fontSize: 14, outline: 'none',
                    }}
                  />
                  {teams.length > 1 && (
                    <Btn variant="ghost" onClick={() => removeTeam(team.id)} style={{ padding: '6px 10px', fontSize: 13 }}>✕</Btn>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {error && <p style={{ color: T.red, fontSize: 13, marginBottom: 12 }}>⚠ {error}</p>}

        {created && (
          <div style={{
            background: `${T.green}14`, border: `1px solid ${T.green}44`, borderRadius: 8,
            padding: '12px 16px', marginBottom: 12, fontSize: 14, color: T.green,
          }}>
            ✅ Game <strong>{created}</strong> created! Players text <code>/join {created}</code> on WhatsApp.
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <Btn variant="primary" onClick={() => window.open(`/?game=${created}&mode=display`, '_blank')} style={{ fontSize: 12, padding: '6px 14px' }}>
                📺 Open Display Board
              </Btn>
            </div>
          </div>
        )}

        <Btn onClick={submit} disabled={loading || !form.puzzleId}>
          {loading ? 'Creating…' : '+ Create Game'}
        </Btn>
      </Card>
    </div>
  );
}

// ─── Puzzle row with delete ───────────────────────────────────────────────────
function PuzzleRow({ puzzle: p, onRefresh }) {
  const [loading, setLoading] = useState(false);

  const deletePuzzle = async () => {
    if (!window.confirm(`Delete puzzle "${p.title}"? This cannot be undone.`)) return;
    setLoading(true);
    try {
      await apiFetch(`/puzzles/${p.id}`, { method: 'DELETE' });
      onRefresh();
    } catch (e) {
      alert(`Error: ${e.message}`);
    }
    setLoading(false);
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16,
      padding: '12px 16px', background: T.bg, borderRadius: 8,
      border: `1px solid ${T.border}`,
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ color: T.text, fontWeight: 600, fontSize: 14 }}>{p.title}</div>
        <div style={{ color: T.muted, fontSize: 12, marginTop: 2 }}>
          By {p.author} · {p.width}×{p.height} · {p.clues?.length || 0} clues
        </div>
      </div>
      <code style={{ color: T.green, fontSize: 12, background: '#0d1a0d', padding: '2px 8px', borderRadius: 4 }}>{p.id}</code>
      <Btn variant="danger" onClick={deletePuzzle} disabled={loading} style={{ padding: '5px 10px', fontSize: 12, opacity: 0.7 }}>🗑</Btn>
    </div>
  );
}

// ─── Section: Puzzles ─────────────────────────────────────────────────────────
function Puzzles({ puzzles, onRefresh }) {
  const [tab,    setTab]    = useState('list');   // list | json | generate | import
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  // JSON editor state
  const [jsonText, setJsonText] = useState(JSON.stringify({
    title: "My Puzzle",
    author: "Admin",
    width: 5,
    height: 5,
    grid: ["  .  ", "     ", "....." , "     ", "  .  "],
    clues: [
      { number: 1, direction: "across", clue: "Quick brown animal", answer: "FOX", startRow: 0, startCol: 0, cells: [{ row: 0, col: 0 }, { row: 0, col: 1 }] }
    ]
  }, null, 2));

  // AI gen state
  const [aiForm, setAiForm] = useState({ topic: '', width: 10, height: 10, difficulty: 'medium' });

  const submitJson = async () => {
    setLoading(true); setError(''); setSuccess('');
    try {
      const body  = JSON.parse(jsonText);
      const res   = await apiFetch('/puzzles', { method: 'POST', body });
      setSuccess(`Puzzle saved! ID: ${res.id}`);
      onRefresh();
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const generateAi = async () => {
    if (!aiForm.topic) return setError('Enter a topic');
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await apiFetch('/puzzles/generate', { method: 'POST', body: aiForm });
      setSuccess(`AI puzzle generated! ID: ${res.id} — "${res.puzzle.title}"`);
      onRefresh();
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const tabs = [
    { id: 'list',     label: '📋 Library'    },
    { id: 'json',     label: '{ } JSON'      },
    { id: 'generate', label: '🤖 AI Generate' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Tab bar */}
      <div className="adm-tabs" style={{ display: 'flex', gap: 4, background: T.surface, borderRadius: 10, padding: 4, width: 'fit-content', border: `1px solid ${T.border}` }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setError(''); setSuccess(''); }}
            style={{
              background: tab === t.id ? '#222' : 'transparent',
              color: tab === t.id ? '#fff' : '#666',
              border: 'none', borderRadius: 8, padding: '7px 16px',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>{t.label}</button>
        ))}
      </div>

      {error   && <div style={{ background: `${T.red}14`, border: `1px solid ${T.red}44`, borderRadius: 8, padding: '10px 14px', color: T.red, fontSize: 13 }}>⚠ {error}</div>}
      {success && <div style={{ background: `${T.green}14`, border: `1px solid ${T.green}44`, borderRadius: 8, padding: '10px 14px', color: T.green, fontSize: 13 }}>✅ {success}</div>}

      {/* Library */}
      {tab === 'list' && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ fontSize: 15, color: T.text, fontWeight: 800 }}>Puzzle Library</h2>
            <Btn variant="ghost" onClick={onRefresh} style={{ padding: '6px 12px', fontSize: 12 }}>↻ Refresh</Btn>
          </div>
          {puzzles.length === 0 ? (
            <p style={{ color: T.muted, fontSize: 14 }}>No puzzles yet. Create one using the tabs above.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {puzzles.map(p => (
                <PuzzleRow key={p.id} puzzle={p} onRefresh={onRefresh} />
              ))}
            </div>
          )}
        </Card>
      )}

      {/* JSON editor */}
      {tab === 'json' && (
        <Card>
          <h2 style={{ fontSize: 15, color: T.text, fontWeight: 800, marginBottom: 4 }}>Import JSON Puzzle</h2>
          <p style={{ color: T.muted, fontSize: 13, marginBottom: 16 }}>
            Paste or edit a puzzle in the normalised JSON format.
          </p>
          <textarea
            value={jsonText}
            onChange={e => setJsonText(e.target.value)}
            rows={20}
            style={{
              width: '100%', background: T.bg, border: `1px solid ${T.border}`,
              borderRadius: 8, padding: '12px', color: T.text, fontSize: 12,
              fontFamily: 'monospace', outline: 'none', resize: 'vertical',
            }}
          />
          <div style={{ marginTop: 12 }}>
            <Btn onClick={submitJson} disabled={loading}>{loading ? 'Saving…' : '💾 Save Puzzle'}</Btn>
          </div>
        </Card>
      )}

      {/* AI generation */}
      {tab === 'generate' && (
        <Card>
          <h2 style={{ fontSize: 15, color: T.text, fontWeight: 800, marginBottom: 4 }}>🤖 AI Puzzle Generator</h2>
          <p style={{ color: T.muted, fontSize: 13, marginBottom: 20 }}>
            Describe a topic and Claude will generate a full crossword puzzle with clues.
          </p>
          <div className="adm-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <Input
              label="Topic"
              value={aiForm.topic}
              onChange={v => setAiForm(f => ({ ...f, topic: v }))}
              placeholder="e.g. The Jamaican Bobsled Team, Caribbean Food, Reggae Music"
              style={{ gridColumn: '1 / -1' }}
            />
            <Input label="Width"  value={aiForm.width}  onChange={v => setAiForm(f => ({ ...f, width:  +v }))} type="number" />
            <Input label="Height" value={aiForm.height} onChange={v => setAiForm(f => ({ ...f, height: +v }))} type="number" />
            <Select
              label="Difficulty"
              value={aiForm.difficulty}
              onChange={v => setAiForm(f => ({ ...f, difficulty: v }))}
              options={[
                { value: 'easy',   label: 'Easy' },
                { value: 'medium', label: 'Medium' },
                { value: 'hard',   label: 'Hard' },
              ]}
              style={{ gridColumn: '1 / -1' }}
            />
          </div>
          <Btn onClick={generateAi} disabled={loading || !aiForm.topic}>
            {loading ? '⏳ Generating puzzle…' : '✨ Generate Puzzle'}
          </Btn>
          {loading && (
            <p style={{ color: T.muted, fontSize: 12, marginTop: 12 }}>
              This may take 15–30 seconds. Claude is constructing your puzzle…
            </p>
          )}
        </Card>
      )}
    </div>
  );
}

// ─── Section: Players ─────────────────────────────────────────────────────────
function Players({ games, ablyClientRef }) {
  const [players, setPlayers] = useState([]);

  const loadPlayers = useCallback(() => {
    apiFetch('/players')
      .then(data => {
        const list = Object.values(data.players || {}).sort((a, b) =>
          (a.name || '').localeCompare(b.name || '')
        );
        setPlayers(list);
      })
      .catch(() => {});
  }, []);

  // Initial load + live refresh. Subscribing here (not in the parent) keeps the
  // update scoped to THIS section: a registry change re-fetches only the player
  // list and re-renders only this table — the rest of the page never re-renders,
  // so there's no full-page refresh or flicker.
  useEffect(() => {
    loadPlayers();
    const client = ablyClientRef?.current;
    if (!client) return;
    const ch = client.channels.get('crossword:admin');
    const onChange = () => loadPlayers();
    ch.subscribe('player-registry-changed', onChange);
    return () => { try { ch.unsubscribe('player-registry-changed', onChange); } catch {} };
  }, [loadPlayers, ablyClientRef]);

  // Merge scores from games so we can show best score per player
  const scoreMap = {};
  Object.values(games).forEach(game => {
    Object.entries(game.scores || {}).forEach(([, s]) => {
      const key = s.name;
      if (!scoreMap[key] || scoreMap[key] < s.score) scoreMap[key] = s.score;
    });
  });

  return (
    <Card>
      <h2 style={{ fontSize: 15, color: T.text, fontWeight: 800, marginBottom: 16 }}>Player Registry</h2>
      {players.length === 0 ? (
        <p style={{ color: T.muted, fontSize: 14 }}>No players yet. They join by texting <code style={{ color: T.green }}>/join</code> on WhatsApp.</p>
      ) : (
        <div className="adm-tablewrap">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
              {['Player', 'Initials', 'Active Game', 'Best Score', 'Registered'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, color: T.muted, fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {players.map((p, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                <td style={{ padding: '10px 12px', color: T.text, fontSize: 14, fontWeight: 600 }}>{p.name || p.jid}</td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{
                    background: '#333', color: T.text,
                    fontWeight: 700, borderRadius: 4, padding: '2px 8px', fontSize: 12,
                  }}>{p.initials || '??'}</span>
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <code style={{ color: T.green, fontSize: 12 }}>{p.activeGameId || '—'}</code>
                </td>
                <td style={{ padding: '10px 12px', color: T.green, fontSize: 14, fontWeight: 700 }}>
                  {scoreMap[p.name] != null ? scoreMap[p.name] : '—'}
                </td>
                <td style={{ padding: '10px 12px', color: T.muted, fontSize: 12 }}>
                  {p.registeredAt ? new Date(p.registeredAt).toLocaleDateString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </Card>
  );
}

// ─── Section: Live Monitor ────────────────────────────────────────────────────
function LiveMonitor({ events }) {
  const typeLabel = (ev) => {
    if (ev.type === 'correct')       return { icon: '✅', color: '#22c55e', label: `answered ${ev.clue} — ` };
    if (ev.type === 'incorrect')     return { icon: '❌', color: '#ef4444', label: `tried ` };
    if (ev.type === 'as-answer')     return { icon: '🎯', color: '#22c55e', label: `answered — ` };
    if (ev.type === 'question')      return { icon: '📋', color: '#FFB300', label: `New question: ` };
    if (ev.type === 'player-joined') return { icon: '👋', color: '#60a5fa', label: `joined game ` };
    if (ev.type === 'player-left')   return { icon: '🚪', color: '#f59e0b', label: `left game ` };
    if (ev.type === 'droppan-win')   return { icon: '🎴', color: '#FFD700', label: `won (${ev.answer}) — ` };
    if (ev.type === 'droppan-bet')   return { icon: '💰', color: '#FFB300', label: `` };
    return { icon: '⚡', color: T.muted, label: '' };
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontSize: 15, color: T.text, fontWeight: 800 }}>📡 Live Event Feed</h2>
          {events.length > 0 && (
            <span style={{ fontSize: 11, color: T.muted, letterSpacing: 2 }}>
              {events.length} event{events.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {events.length === 0 ? (
          <p style={{ color: T.muted, fontSize: 14 }}>
            Waiting for game events… Start a game and WhatsApp answers will appear here in real time.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 520, overflowY: 'auto' }}>
            {events.map((ev, i) => {
              const { icon, color, label } = typeLabel(ev);
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '9px 14px', background: T.raised, borderRadius: 10,
                  border: `1px solid ${ev.teamColor || T.border}2a`,
                  opacity: i > 40 ? 0.3 : 1,
                  animation: i === 0 ? 'liveIn .3s ease' : 'none',
                }}>
                  {/* Initials / icon badge */}
                  <span style={{
                    background: ev.type === 'question' ? 'rgba(255,179,0,.15)' : (ev.teamColor || T.raised),
                    color: ev.type === 'question' ? '#FFB300' : '#fff',
                    fontWeight: 800, borderRadius: 6, padding: '2px 9px', fontSize: 12,
                    flexShrink: 0, minWidth: 32, textAlign: 'center',
                    border: `1px solid ${ev.teamColor || T.border}44`,
                  }}>
                    {ev.type === 'question' ? icon : (ev.initials || '?')}
                  </span>

                  {/* Game type tag */}
                  <span style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: 2, flexShrink: 0,
                    color: ev.gameType === 'americasays' ? 'rgba(255,179,0,.5)' : 'rgba(100,200,255,.4)',
                    textTransform: 'uppercase',
                  }}>
                    {ev.gameType === 'americasays' ? 'AS' : 'CW'}
                  </span>

                  <span style={{ color: T.muted, fontSize: 13, flexShrink: 0 }}>{icon}</span>

                  <span style={{ color: T.text, fontSize: 13, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ev.type !== 'question' && (
                      <strong style={{ color: ev.teamColor || T.text }}>{ev.player} </strong>
                    )}
                    <span style={{ color: T.muted }}>{label}</span>
                    <span style={{ color }}>
                      {ev.type === 'player-joined' || ev.type === 'player-left' ? (
                        <code style={{ color: T.muted, fontSize: 11 }}>{ev.gameId}</code>
                      ) : ev.answer}
                    </span>
                  </span>

                  {ev.pts && (
                    <span style={{ color: '#22c55e', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                      +{ev.pts} pts
                    </span>
                  )}
                  <span style={{ color: T.muted, fontSize: 11, flexShrink: 0 }}>
                    {new Date(ev.ts).toLocaleTimeString()}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
// ─── Drop Pan Admin ───────────────────────────────────────────────────────────

const TIER_COLORS = { symbol: '#FFD700', main: '#C0C0C0', minor: '#CD7F32' };

function DropPanAdmin({ onRefresh }) {
  const [subTab,    setSubTab]    = useState('sets');
  const [sets,      setSets]      = useState([]);
  const [dpGames,   setDpGames]   = useState([]);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [liveRound, setLiveRound] = useState(null);

  const loadSets  = useCallback(async () => {
    try { const d = await apiFetch('/droppan/question-sets'); setSets(d.sets || []); } catch {}
  }, []);
  const loadGames = useCallback(async () => {
    try { const d = await apiFetch('/droppan'); setDpGames(d.games || []); } catch {}
  }, []);

  useEffect(() => { loadSets(); loadGames(); }, [loadSets, loadGames]);

  // Ably subscription for live round updates
  useEffect(() => {
    const activeGame = dpGames.find(g => g.status === 'active');
    if (!activeGame) return;
    const client = new Ably.Realtime({ authUrl: ABLY_AUTH_URL, autoConnect: true });
    const ch = client.channels.get(`droppan:game:${activeGame.id}`);
    const onWinner = (msg) => {
      setLiveRound(prev => ({
        ...(prev || {}),
        winners: [...(prev?.winners || []), msg.data],
      }));
    };
    const onRoundStart = (msg) => {
      setLiveRound({ ...msg.data, winners: [] });
      loadGames();
    };
    const onRoundEnd = (msg) => {
      setLiveRound(msg.data);
      loadGames();
    };
    ch.subscribe('round-winner', onWinner);
    ch.subscribe('round-start', onRoundStart);
    ch.subscribe('round-end', onRoundEnd);
    ch.subscribe('game-end', () => { loadGames(); setLiveRound(null); });
    return () => client.close();
  }, [dpGames, loadGames]);

  const tabs = [
    { id: 'sets',   label: '📚 Question Sets' },
    { id: 'games',  label: '🎮 Games' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 4 }}>🎴 Drop Pan</h1>
        <p style={{ color: T.muted, fontSize: 13 }}>Jamaica Dream Book number game — three modes, 5-second winner window, dynamic scoring.</p>
      </div>

      {error   && <div style={{ background: T.red + '22', border: `1px solid ${T.red}44`, borderRadius: 10, padding: '10px 16px', color: T.red, fontSize: 13 }}>{error}</div>}
      {success && <div style={{ background: T.green + '22', border: `1px solid ${T.green}44`, borderRadius: 10, padding: '10px 16px', color: T.green, fontSize: 13 }}>{success}</div>}

      {/* Sub-tab bar */}
      <div className="adm-tabs" style={{ display: 'flex', gap: 6, borderBottom: `1px solid ${T.border}`, paddingBottom: 14 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} style={{
            padding: '7px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: subTab === t.id ? `${T.accent}22` : 'transparent',
            color: subTab === t.id ? T.text : T.muted,
            fontWeight: subTab === t.id ? 700 : 500,
            fontSize: 13, borderBottom: subTab === t.id ? `2px solid ${T.accent}` : '2px solid transparent',
            fontFamily: 'inherit',
          }}>{t.label}</button>
        ))}
      </div>

      {subTab === 'sets'  && <DropPanSets  sets={sets}  onRefresh={loadSets}  setError={setError} setSuccess={setSuccess} />}
      {subTab === 'games' && <DropPanGames sets={sets} games={dpGames} onRefresh={loadGames} setError={setError} setSuccess={setSuccess} liveRound={liveRound} />}
    </div>
  );
}

function DropPanSets({ sets, onRefresh, setError, setSuccess }) {
  const [name,      setName]      = useState('');
  const [count,     setCount]     = useState(36);
  const [tierS,     setTierS]     = useState(1);
  const [tierM,     setTierM]     = useState(2);
  const [tierm,     setTierm]     = useState(1);
  const [creating,  setCreating]  = useState(false);

  const create = async () => {
    if (!name.trim()) return setError('Name is required');
    setCreating(true); setError('');
    try {
      await apiFetch('/droppan/question-sets', {
        method: 'POST',
        body: { name: name.trim(), autoGenerate: true, count: Number(count),
          tierDist: { symbol: Number(tierS), main: Number(tierM), minor: Number(tierm) } },
      });
      setSuccess(`Question set "${name.trim()}" created!`);
      setName('');
      onRefresh();
    } catch (e) { setError(e.message); }
    finally { setCreating(false); }
  };

  const del = async (id, nm) => {
    if (!window.confirm(`Delete "${nm}"?`)) return;
    try {
      await apiFetch(`/droppan/question-sets/${id}`, { method: 'DELETE' });
      onRefresh();
    } catch (e) { setError(e.message); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Card>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Create Question Set</h2>
        <div className="adm-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <Input label="Set Name" value={name} onChange={setName} placeholder="e.g. Classic Drop Pan 36" />
          <Input label="Rounds" value={count} onChange={v => setCount(Math.min(36, Math.max(1, parseInt(v)||1)))} type="number" />
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
            Tier Distribution (weights)
          </label>
          <div className="adm-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            {[
              { label: '✦ Symbol', val: tierS, set: setTierS, color: TIER_COLORS.symbol },
              { label: '● Main',   val: tierM, set: setTierM, color: TIER_COLORS.main },
              { label: '○ Minor',  val: tierm, set: setTierm, color: TIER_COLORS.minor },
            ].map(({ label, val, set, color }) => (
              <div key={label}>
                <label style={{ fontSize: 11, color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', display: 'block', marginBottom: 4 }}>{label}</label>
                <input type="range" min={0} max={5} step={1} value={val} onChange={e => set(Number(e.target.value))}
                  style={{ width: '100%', accentColor: color }} />
                <span style={{ fontSize: 12, color }}>{val}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>
            Higher weight = more rounds use that tier as the clue. Symbol clues are hardest to guess, worth most points.
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <Btn onClick={create} disabled={creating}>{creating ? 'Generating…' : '+ Generate Set'}</Btn>
        </div>
      </Card>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700 }}>Question Sets ({sets.length})</h2>
          <Btn variant="ghost" onClick={onRefresh}>↻ Refresh</Btn>
        </div>
        {sets.length === 0 ? (
          <div style={{ color: T.muted, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No sets yet — create one above.</div>
        ) : sets.map(s => {
          const tierCounts = (s.rounds || []).reduce((acc, r) => {
            acc[r.tier] = (acc[r.tier] || 0) + 1; return acc;
          }, {});
          return (
            <div key={s.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '12px 14px', borderRadius: 10, marginBottom: 6,
              background: T.raised, border: `1px solid ${T.border}`,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{s.name}</div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>
                  {s.rounds?.length || 0} rounds
                  {Object.entries(tierCounts).map(([tier, cnt]) => (
                    <span key={tier} style={{ marginLeft: 8, color: TIER_COLORS[tier] || T.muted }}>
                      {tier}: {cnt}
                    </span>
                  ))}
                </div>
              </div>
              <Badge color="#666">{s.id}</Badge>
              <Btn variant="danger" onClick={() => del(s.id, s.name)}>Delete</Btn>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

function DropPanGames({ sets, games, onRefresh, setError, setSuccess, liveRound }) {
  const [form, setForm] = useState({
    questionSetId: '', mode: 'dreamnumber', name: '', teamMode: false,
    autoAdvance: false, autoAdvanceDelay: 4,
    auctionBettingSecs: 30, roundTimerSecs: 0,
  });
  const [dpTeams, setDpTeams] = useState([
    { id: 'team-a', name: 'Team A', color: T.blue },
    { id: 'team-b', name: 'Team B', color: T.red },
  ]);
  const [creating, setCreating] = useState(false);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addDpTeam    = () => setDpTeams(t => [...t, { id: `team-${Date.now()}`, name: `Team ${t.length + 1}`, color: T.muted }]);
  const removeDpTeam = (id) => setDpTeams(t => t.filter(x => x.id !== id));
  const updateDpTeam = (id, field, val) => setDpTeams(t => t.map(x => x.id === id ? { ...x, [field]: val } : x));

  const createGame = async () => {
    if (!form.questionSetId) return setError('Select a question set');
    setCreating(true); setError('');
    try {
      const d = await apiFetch('/droppan', {
        method: 'POST',
        body: {
          questionSetId: form.questionSetId,
          mode: form.mode,
          name: form.name || undefined,
          teams: form.teamMode ? dpTeams : [],
          settings: {
            autoAdvance: form.autoAdvance,
            autoAdvanceDelay: Number(form.autoAdvanceDelay),
            auctionBettingSecs: Number(form.auctionBettingSecs),
            roundTimerSecs: Number(form.roundTimerSecs),
          },
        },
      });
      setSuccess(`Game created: ${d.gameId}`);
      onRefresh();
    } catch (e) { setError(e.message); }
    finally { setCreating(false); }
  };

  const action = async (id, endpoint, label) => {
    try {
      await apiFetch(`/droppan/${id}/${endpoint}`, { method: 'POST' });
      setSuccess(`${label} — ${id}`);
      onRefresh();
    } catch (e) { setError(e.message); }
  };

  const modeLabels = {
    dreamnumber:  '🔢 Dream Number',
    namethemark:  '🎴 Name the Mark',
    dreamauction: '💰 Dream Auction',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Create game form */}
      <Card>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Create Game</h2>
        <div className="adm-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Select label="Mode" value={form.mode} onChange={v => upd('mode', v)} options={[
            { value: 'dreamnumber',  label: '🔢 Dream Number — see word, text number' },
            { value: 'namethemark',  label: '🎴 Name the Mark — see number, text name' },
            { value: 'dreamauction', label: '💰 Dream Auction — see clue, bet points' },
          ]} />
          <Select label="Question Set" value={form.questionSetId} onChange={v => upd('questionSetId', v)} options={[
            { value: '', label: '— Select a set —' },
            ...sets.map(s => ({ value: s.id, label: `${s.name} (${s.rounds?.length || 0} rounds)` })),
          ]} />
        </div>
        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'end' }}>
          <Input label="Game Name (optional)" value={form.name} onChange={v => upd('name', v)} placeholder="e.g. Friday Night Drop Pan" />
          <div>
            <label style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Team Mode</label>
            <button onClick={() => upd('teamMode', !form.teamMode)} style={{
              padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: form.teamMode ? T.amber + '22' : T.raised, fontFamily: 'inherit',
              color: form.teamMode ? T.amber : T.muted, fontSize: 12, fontWeight: 700,
              border: `1px solid ${form.teamMode ? T.amber + '55' : T.border}`,
            }}>{form.teamMode ? '🏆 Teams ON' : 'Solo'}</button>
          </div>
        </div>

        {form.teamMode && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              <span style={{ fontSize: 13, color: T.muted, fontWeight: 600 }}>TEAMS</span>
              <Btn variant="ghost" onClick={addDpTeam} style={{ padding: '4px 10px', fontSize: 11 }}>+ Add Team</Btn>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {dpTeams.map(team => (
                <div key={team.id} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input type="color" value={team.color}
                    onChange={e => updateDpTeam(team.id, 'color', e.target.value)}
                    style={{ width: 34, height: 34, border: 'none', borderRadius: 6, cursor: 'pointer', background: 'none' }} />
                  <input value={team.name} onChange={e => updateDpTeam(team.id, 'name', e.target.value)}
                    style={{ flex: 1, background: T.raised, border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 12px', color: T.text, fontSize: 14, outline: 'none' }} />
                  {dpTeams.length > 1 && (
                    <Btn variant="ghost" onClick={() => removeDpTeam(team.id)} style={{ padding: '6px 10px', fontSize: 13 }}>✕</Btn>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="adm-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginTop: 12 }}>
          <div>
            <label style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Auto-Advance</label>
            <button onClick={() => upd('autoAdvance', !form.autoAdvance)} style={{
              padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: form.autoAdvance ? T.green + '22' : T.raised,
              color: form.autoAdvance ? T.green : T.muted, fontFamily: 'inherit',
              border: `1px solid ${form.autoAdvance ? T.green + '55' : T.border}`,
              fontSize: 12, fontWeight: 700,
            }}>{form.autoAdvance ? '✓ On' : 'Off'}</button>
          </div>
          {form.autoAdvance && <Input label="Advance Delay (s)" type="number" value={form.autoAdvanceDelay} onChange={v => upd('autoAdvanceDelay', v)} />}
          {form.mode === 'dreamauction' && <Input label="Betting Window (s)" type="number" value={form.auctionBettingSecs} onChange={v => upd('auctionBettingSecs', v)} />}
        </div>
        <div style={{ marginTop: 16 }}>
          <Btn onClick={createGame} disabled={creating}>{creating ? 'Creating…' : '+ Create Game'}</Btn>
        </div>
      </Card>

      {/* Live round feed */}
      {liveRound && (
        <Card accent={T.green}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: T.green }}>⚡ LIVE ROUND {(liveRound.roundIdx ?? 0) + 1}</div>
              <div style={{ fontSize: 12, color: T.muted }}>
                {liveRound.clue
                  ? <>Clue: <strong style={{ color: '#fff' }}>{liveRound.clue}</strong> ({liveRound.tier})</>
                  : <>Number: <strong style={{ color: '#fff' }}>{liveRound.number}</strong></>}
              </div>
            </div>
            <Badge color={T.green}>{(liveRound.winners || []).length} winner(s)</Badge>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {(liveRound.winners || []).map((w, i) => (
              <div key={w.jid || i} style={{
                background: T.raised, borderRadius: 8, padding: '5px 12px',
                border: `1px solid ${TIER_COLORS[w.tier] || T.border}44`,
                fontSize: 12,
              }}>
                <span style={{ color: TIER_COLORS[w.tier], fontWeight: 700 }}>#{w.rank}</span>
                <span style={{ marginLeft: 6, color: '#fff' }}>{w.name || w.playerName}</span>
                <span style={{ marginLeft: 6, color: T.muted }}>+{w.points}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Games list */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700 }}>Games ({games.length})</h2>
          <Btn variant="ghost" onClick={onRefresh}>↻ Refresh</Btn>
        </div>
        {games.length === 0 ? (
          <div style={{ color: T.muted, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No games yet.</div>
        ) : games.map(g => {
          const statusColor = g.status === 'active' ? T.green : g.status === 'waiting' ? T.amber : T.muted;
          return (
            <div key={g.id} style={{
              padding: '14px 16px', borderRadius: 12, marginBottom: 8,
              background: T.raised, border: `1px solid ${g.status === 'active' ? T.green + '33' : T.border}`,
              boxShadow: g.status === 'active' ? `0 0 12px ${T.green}18` : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{g.name || g.id}</div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                    <span style={{ color: statusColor, fontWeight: 700, textTransform: 'uppercase' }}>{g.status}</span>
                    <span style={{ marginLeft: 8 }}>{modeLabels[g.mode] || g.mode}</span>
                    {g.status !== 'waiting' && <span style={{ marginLeft: 8 }}>Round {(g.currentRound ?? -1) + 1}/{g.totalRounds}</span>}
                  </div>
                </div>
                <Badge color="#666">{g.id}</Badge>

                {/* Controls */}
                <div style={{ display: 'flex', gap: 6 }}>
                  {g.status === 'waiting' && (
                    <Btn variant="primary" onClick={() => action(g.id, 'start', 'Game started')}>▶ Start</Btn>
                  )}
                  {g.status === 'active' && (
                    <>
                      <Btn variant="blue" onClick={() => action(g.id, 'next', 'Next round')}>⏭ Next</Btn>
                      <Btn variant="amber" onClick={() => action(g.id, 'reveal', 'Answer revealed')}>👁 Reveal</Btn>
                      <Btn variant="danger" onClick={() => action(g.id, 'end', 'Game ended')}>■ End</Btn>
                    </>
                  )}
                  {g.status !== 'active' && (
                    <Btn variant="ghost" onClick={async () => {
                      if (!window.confirm(`Delete game ${g.id}?`)) return;
                      try {
                        await apiFetch(`/droppan/${g.id}`, { method: 'DELETE' });
                        onRefresh();
                      } catch (e) { setError(e.message); }
                    }}>🗑</Btn>
                  )}
                </div>
              </div>

              {/* Score summary */}
              {g.scores && Object.keys(g.scores).length > 0 && (
                <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {Object.values(g.scores).sort((a,b)=>b.score-a.score).slice(0,5).map((s,i)=>(
                    <div key={s.name+i} style={{
                      background: 'rgba(255,255,255,.04)', borderRadius: 6,
                      padding: '3px 10px', fontSize: 11,
                    }}>
                      <span style={{ color: i===0?'#FFD700':T.muted }}>{['🥇','🥈','🥉'][i]||`#${i+1}`}</span>
                      <span style={{ marginLeft: 5, color: T.text }}>{s.name}</span>
                      <span style={{ marginLeft: 5, color: T.amber }}>{s.score}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Team roster for team-mode Drop Pan games */}
              {g.teamMode && g.teams?.length > 0 && (
                <TeamRosterPanel game={g} gameId={g.id} gameType="droppan" onRefresh={onRefresh} />
              )}
            </div>
          );
        })}
      </Card>
    </div>
  );
}

function Settings({ ablyClientRef }) {
  const [settings,  setSettings]  = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [error,     setError]     = useState('');
  // Players management
  const [players,   setPlayers]   = useState(null);
  const [clearing,  setClearing]  = useState(false);
  const [clearOk,   setClearOk]   = useState(false);

  const loadPlayers = useCallback(() => {
    apiFetch('/players').then(r => setPlayers(Object.values(r.players || {}))).catch(() => {});
  }, []);

  useEffect(() => {
    // Default quietReplies to ON unless the server has an explicit value.
    apiFetch('/settings').then(s => setSettings({ quietReplies: true, ...s })).catch(e => setError(e.message));
  }, []);

  // Live-refresh only the players list when the registry changes — scoped to
  // this section so nothing else re-renders/flickers.
  useEffect(() => {
    loadPlayers();
    const client = ablyClientRef?.current;
    if (!client) return;
    const ch = client.channels.get('crossword:admin');
    const onChange = () => loadPlayers();
    ch.subscribe('player-registry-changed', onChange);
    return () => { try { ch.unsubscribe('player-registry-changed', onChange); } catch {} };
  }, [loadPlayers, ablyClientRef]);

  const save = async (overrideSettings) => {
    const s = overrideSettings || settings;
    setSaving(true); setSaved(false); setError('');
    try {
      await apiFetch('/settings', { method: 'PUT', body: s });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  // Toggle immediately saves to bridge
  const toggle = (key) => {
    const next = { ...settings, [key]: !settings[key] };
    setSettings(next);
    save(next);
  };

  const deletePlayer = async (jid) => {
    try {
      await apiFetch(`/players/${encodeURIComponent(jid)}`, { method: 'DELETE' });
      setPlayers(prev => prev.filter(p => p.jid !== jid));
    } catch (e) { setError(e.message); }
  };

  const clearAllPlayers = async () => {
    if (!window.confirm('Permanently delete ALL players from the registry? This cannot be undone.')) return;
    setClearing(true);
    try {
      await apiFetch('/players', { method: 'DELETE' });
      setPlayers([]);
      setClearOk(true);
      setTimeout(() => setClearOk(false), 2500);
    } catch (e) { setError(e.message); }
    setClearing(false);
  };

  if (!settings) return <Card><p style={{ color: T.muted }}>Loading settings…</p></Card>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Bridge settings */}
      <Card>
        <h2 style={{ fontSize: 15, color: T.text, fontWeight: 800, marginBottom: 20 }}>Bridge Settings</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 480 }}>
          {/* Note: no Auto-Approve toggle — game input is always approved unless
              blocked by the filters below. There is no pending-message queue, so
              un-approved messages would be silently lost (dead gameplay). */}
          {[
            { key: 'profanityFilter', label: 'Profanity Filter',     desc: 'Block messages containing profanity before they reach the game.' },
            { key: 'quietReplies',   label: 'Quiet Mode (no per-answer replies)', desc: 'Suppress WhatsApp confirmations for each answer — players rely on the TV. Recommended for large events to avoid WhatsApp rate limits.' },
          ].map(({ key, label, desc }) => (
            <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16,
              padding: '14px 16px', background: T.bg, borderRadius: 8, border: `1px solid ${T.border}` }}>
              <div>
                <div style={{ color: T.text, fontWeight: 600, fontSize: 14 }}>{label}</div>
                <div style={{ color: T.muted, fontSize: 12, marginTop: 3 }}>{desc}</div>
              </div>
              <button onClick={() => toggle(key)} disabled={saving} style={{
                flexShrink: 0, width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                background: settings[key] ? '#22c55e' : '#333',
                position: 'relative', transition: 'background 0.2s', opacity: saving ? 0.6 : 1,
              }}>
                <div style={{
                  position: 'absolute', top: 2, left: settings[key] ? 22 : 2,
                  width: 20, height: 20, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.2s',
                }}/>
              </button>
            </div>
          ))}
          {error && <div style={{ color: T.red, fontSize: 13 }}>⚠ {error}</div>}
          {saved && <div style={{ color: T.green, fontSize: 13 }}>✅ Settings saved &amp; pushed to bridge</div>}
          {saving && <div style={{ color: T.muted, fontSize: 12 }}>Saving…</div>}
        </div>
      </Card>

      {/* Player management */}
      <Card>
        <h2 style={{ fontSize: 15, color: T.text, fontWeight: 800, marginBottom: 6 }}>Player Management</h2>
        <p style={{ color: T.muted, fontSize: 12, marginBottom: 16 }}>
          Permanently remove players from the registry. Deleted players must re-register by messaging the game number.
        </p>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <Btn onClick={clearAllPlayers} disabled={clearing} style={{ background: '#7f1d1d', borderColor: '#ef4444' }}>
            {clearing ? 'Deleting…' : '🗑 Delete All Players'}
          </Btn>
          {clearOk && <span style={{ color: T.green, fontSize: 13, alignSelf: 'center' }}>✅ All players removed</span>}
        </div>
        {players && players.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto' }}>
            {players.map(p => (
              <div key={p.jid} style={{ display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 12px', background: T.bg, borderRadius: 7, border: `1px solid ${T.border}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: T.text, fontWeight: 600, fontSize: 13 }}>{p.name || p.jid}</div>
                  {p.activeGameId
                    ? <div style={{ color: '#22c55e', fontSize: 11 }}>In game: {p.activeGameId}</div>
                    : <div style={{ color: T.muted, fontSize: 11 }}>Not in a game</div>
                  }
                </div>
                <button onClick={() => deletePlayer(p.jid)} style={{
                  padding: '4px 10px', borderRadius: 6, border: `1px solid ${T.red}44`,
                  background: `${T.red}11`, color: T.red, cursor: 'pointer', fontSize: 11,
                }} title="Permanently remove this player">🗑 Delete</button>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: T.muted, fontSize: 12 }}>{players === null ? 'Loading…' : 'No players registered yet.'}</p>
        )}
      </Card>
    </div>
  );
}

// ─── America Says: Survey Builder ────────────────────────────────────────────

function SurveyBuilder({ surveys, onRefresh }) {
  const [tab, setTab]         = useState('list');   // list | build | json | generate
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  // Builder state
  const [title, setTitle]         = useState('');
  const [questions, setQuestions] = useState([
    { prompt: '', answers: [{ text: '', points: 40 }, { text: '', points: 30 }, { text: '', points: 20 }, { text: '', points: 10 }] }
  ]);
  const [editingId, setEditingId] = useState(null); // set when editing an existing survey

  // JSON import state
  const [jsonText, setJsonText] = useState(JSON.stringify({
    title: "My Survey",
    questions: [
      {
        prompt: "NAME SOMETHING YOU FIND AT A BIRTHDAY PARTY",
        answers: [
          { text: "CAKE", points: 40 },
          { text: "BALLOONS", points: 30 },
          { text: "PRESENTS", points: 20 },
          { text: "CANDLES", points: 10 }
        ]
      }
    ]
  }, null, 2));

  // AI generate state
  const [aiForm, setAiForm] = useState({ topic: '', numQuestions: 5, numAnswers: 6 });

  const addQuestion = () => setQuestions(q => [...q, {
    prompt: '',
    answers: [{ text: '', points: 40 }, { text: '', points: 30 }, { text: '', points: 20 }, { text: '', points: 10 }],
  }]);
  const removeQuestion = (qi) => setQuestions(q => q.filter((_, i) => i !== qi));
  const setQPrompt = (qi, v) => setQuestions(q => q.map((qn, i) => i === qi ? { ...qn, prompt: v } : qn));
  const setAnswer  = (qi, ai, field, v) => setQuestions(q => q.map((qn, i) => {
    if (i !== qi) return qn;
    const ans = qn.answers.map((a, j) => j === ai ? { ...a, [field]: field === 'points' ? parseInt(v, 10) || 0 : v } : a);
    return { ...qn, answers: ans };
  }));
  const addAnswer    = (qi) => setQuestions(q => q.map((qn, i) => i === qi ? { ...qn, answers: [...qn.answers, { text: '', points: 5 }] } : qn));
  const removeAnswer = (qi, ai) => setQuestions(q => q.map((qn, i) => i === qi ? { ...qn, answers: qn.answers.filter((_, j) => j !== ai) } : qn));

  const submitJson = async () => {
    setLoading(true); setError(''); setSuccess('');
    try {
      const body = JSON.parse(jsonText);
      if (!body.title || !Array.isArray(body.questions) || body.questions.length === 0)
        throw new Error('JSON must have a title and at least one question');
      const res = await apiFetch('/surveys', { method: 'POST', body });
      setSuccess(`Survey saved! ID: ${res.id}`);
      onRefresh();
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const generateAi = async () => {
    if (!aiForm.topic) return setError('Enter a topic');
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await apiFetch('/surveys/generate', { method: 'POST', body: aiForm });
      setSuccess(`AI survey generated! ID: ${res.id} — "${res.survey.title}" (${res.survey.questions.length} questions)`);
      onRefresh();
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const saveSurvey = async () => {
    if (!title) return setError('Enter a survey title');
    const valid = questions.every(q => q.prompt && q.answers.some(a => a.text));
    if (!valid) return setError('Every question needs a prompt and at least one answer');
    setLoading(true); setError(''); setSuccess('');
    try {
      const body = { title, questions: questions.map(q => ({
        prompt: q.prompt,
        answers: q.answers.filter(a => a.text).map(a => ({ text: a.text.toUpperCase(), points: a.points })),
      })) };
      if (editingId) body.id = editingId; // update in place
      const res = await apiFetch('/surveys', { method: 'POST', body });
      setEditingId(res.id);
      setSuccess(editingId ? `Survey updated! (${res.id})` : `Survey saved! ID: ${res.id}`);
      onRefresh();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  // Load an existing survey into the Builder for editing (saves back to same id).
  const startEdit = (s) => {
    setEditingId(s.id);
    setTitle(s.title || '');
    setQuestions((s.questions && s.questions.length ? s.questions : [{ prompt: '', answers: [{ text: '', points: 10 }] }])
      .map(q => ({
        prompt: q.prompt || '',
        answers: (q.answers && q.answers.length ? q.answers : [{ text: '', points: 10 }])
          .map(a => ({ text: a.text || '', points: a.points ?? 0 })),
      })));
    setTab('build'); setError(''); setSuccess('');
  };

  const resetForm = () => {
    setEditingId(null);
    setTitle('');
    setQuestions([{ prompt: '', answers: [{ text: '', points: 40 }, { text: '', points: 30 }, { text: '', points: 20 }, { text: '', points: 10 }] }]);
    setError(''); setSuccess('');
  };

  const deleteSurvey = async (id) => {
    if (!window.confirm('Delete this survey?')) return;
    try { await apiFetch(`/surveys/${id}`, { method: 'DELETE' }); onRefresh(); }
    catch (e) { alert(e.message); }
  };

  const tabs = [
    { id: 'list',     label: '📋 Library'    },
    { id: 'build',    label: '✏️ Builder'    },
    { id: 'json',     label: '{ } JSON'      },
    { id: 'generate', label: '🤖 AI Generate' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="adm-tabs" style={{ display: 'flex', gap: 4, background: T.surface, borderRadius: 10, padding: 4, width: 'fit-content', border: `1px solid ${T.border}` }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setError(''); setSuccess(''); }}
            style={{ background: tab === t.id ? '#222' : 'transparent', color: tab === t.id ? '#fff' : '#666',
              border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >{t.label}</button>
        ))}
      </div>

      {error   && <div style={{ background: `${T.red}14`, border: `1px solid ${T.red}44`, borderRadius: 8, padding: '10px 14px', color: T.red, fontSize: 13 }}>⚠ {error}</div>}
      {success && <div style={{ background: `${T.green}14`, border: `1px solid ${T.green}44`, borderRadius: 8, padding: '10px 14px', color: T.green, fontSize: 13 }}>✅ {success}</div>}

      {tab === 'list' && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ fontSize: 15, color: T.text, fontWeight: 800 }}>Survey Library</h2>
            <Btn variant="ghost" onClick={onRefresh} style={{ padding: '6px 12px', fontSize: 12 }}>↻ Refresh</Btn>
          </div>
          {surveys.length === 0 ? (
            <p style={{ color: T.muted, fontSize: 14 }}>No surveys yet. Use the Builder tab to create one.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {surveys.map(s => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 16px', background: T.bg, borderRadius: 8, border: `1px solid ${T.border}` }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: T.text, fontWeight: 600, fontSize: 14 }}>{s.title}</div>
                    <div style={{ color: T.muted, fontSize: 12, marginTop: 2 }}>{s.questions?.length || 0} questions</div>
                  </div>
                  <code style={{ color: T.amber, fontSize: 12, background: '#1a1500', padding: '2px 8px', borderRadius: 4 }}>{s.id}</code>
                  <Btn variant="ghost" onClick={() => startEdit(s)} style={{ padding: '5px 10px', fontSize: 12 }}>✏️ Edit</Btn>
                  <Btn variant="danger" onClick={() => deleteSurvey(s.id)} style={{ padding: '5px 10px', fontSize: 12, opacity: 0.7 }}>🗑</Btn>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === 'build' && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <h2 style={{ fontSize: 15, color: T.text, fontWeight: 800 }}>
              {editingId ? 'Edit Survey' : 'Survey Builder'}
              {editingId && <code style={{ marginLeft: 10, color: T.amber, fontSize: 12, background: '#1a1500', padding: '2px 8px', borderRadius: 4 }}>{editingId}</code>}
            </h2>
            {editingId && <Btn variant="ghost" onClick={resetForm} style={{ fontSize: 12 }}>+ New survey</Btn>}
          </div>
          <Input label="Survey Title" value={title} onChange={setTitle} placeholder="e.g. At a Wedding Reception…" style={{ marginBottom: 20 }} />

          {questions.map((q, qi) => (
            <div key={qi} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ color: T.amber, fontWeight: 700, fontSize: 13 }}>Question {qi + 1}</span>
                {questions.length > 1 && (
                  <Btn variant="ghost" onClick={() => removeQuestion(qi)} style={{ padding: '3px 8px', fontSize: 11 }}>✕ Remove</Btn>
                )}
              </div>
              <Input
                label="Prompt (the fill-in-the-blank or question)"
                value={q.prompt}
                onChange={v => setQPrompt(qi, v)}
                placeholder="e.g. AT A WEDDING RECEPTION, PEOPLE USUALLY WAIT FOR THE COUPLE TO ___ FIRST."
                style={{ marginBottom: 12 }}
              />
              <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                Answers (ranked #1 = most popular, highest points)
              </div>
              {q.answers.map((a, ai) => (
                <div key={ai} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ width: 22, textAlign: 'center', color: ai === 0 ? '#f59e0b' : '#555', fontSize: 13, fontWeight: 700 }}>#{ai + 1}</span>
                  <input
                    value={a.text}
                    onChange={e => setAnswer(qi, ai, 'text', e.target.value)}
                    placeholder={`Answer ${ai + 1}…`}
                    style={{ flex: 2, background: T.raised, border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 10px', color: T.text, fontSize: 13, outline: 'none' }}
                  />
                  <input
                    type="number"
                    value={a.points}
                    onChange={e => setAnswer(qi, ai, 'points', e.target.value)}
                    style={{ width: 70, background: T.raised, border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 10px', color: T.amber, fontSize: 13, outline: 'none', textAlign: 'center' }}
                  />
                  <span style={{ color: T.muted, fontSize: 11 }}>pts</span>
                  {q.answers.length > 2 && (
                    <Btn variant="ghost" onClick={() => removeAnswer(qi, ai)} style={{ padding: '4px 8px', fontSize: 11 }}>✕</Btn>
                  )}
                </div>
              ))}
              <Btn variant="ghost" onClick={() => addAnswer(qi)} style={{ padding: '5px 12px', fontSize: 11, marginTop: 6 }}>+ Add Answer</Btn>
            </div>
          ))}

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <Btn variant="ghost" onClick={addQuestion}>+ Add Question</Btn>
            <Btn onClick={saveSurvey} disabled={loading}>{loading ? 'Saving…' : editingId ? '💾 Update Survey' : '💾 Save Survey'}</Btn>
          </div>
        </Card>
      )}

      {/* JSON import */}
      {tab === 'json' && (
        <Card>
          <h2 style={{ fontSize: 15, color: T.text, fontWeight: 800, marginBottom: 4 }}>Import JSON Survey</h2>
          <p style={{ color: T.muted, fontSize: 13, marginBottom: 16 }}>
            Paste a survey in JSON format. Each question needs a <code style={{ color: T.amber }}>prompt</code> and an <code style={{ color: T.amber }}>answers</code> array with <code style={{ color: T.amber }}>text</code> and <code style={{ color: T.amber }}>points</code>.
          </p>
          <textarea
            value={jsonText}
            onChange={e => setJsonText(e.target.value)}
            rows={22}
            style={{
              width: '100%', background: T.bg, border: `1px solid ${T.border}`,
              borderRadius: 8, padding: '12px', color: T.text, fontSize: 12,
              fontFamily: 'monospace', outline: 'none', resize: 'vertical',
            }}
          />
          <div style={{ marginTop: 12 }}>
            <Btn onClick={submitJson} disabled={loading}>{loading ? 'Saving…' : '💾 Save Survey'}</Btn>
          </div>
        </Card>
      )}

      {/* AI generation */}
      {tab === 'generate' && (
        <Card>
          <h2 style={{ fontSize: 15, color: T.text, fontWeight: 800, marginBottom: 4 }}>🤖 AI Survey Generator</h2>
          <p style={{ color: T.muted, fontSize: 13, marginBottom: 20 }}>
            Describe a topic and Claude will generate a Family Feud–style survey with ranked answers.
          </p>
          <div className="adm-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <Input
              label="Topic"
              value={aiForm.topic}
              onChange={v => setAiForm(f => ({ ...f, topic: v }))}
              placeholder="e.g. A Jamaican Wedding, Beach Vacation, Office Life"
              style={{ gridColumn: '1 / -1' }}
            />
            <Select
              label="Number of Questions"
              value={String(aiForm.numQuestions)}
              onChange={v => setAiForm(f => ({ ...f, numQuestions: +v }))}
              options={[
                { value: '3',  label: '3 questions' },
                { value: '5',  label: '5 questions' },
                { value: '8',  label: '8 questions' },
                { value: '10', label: '10 questions' },
              ]}
            />
            <Select
              label="Answers per Question"
              value={String(aiForm.numAnswers)}
              onChange={v => setAiForm(f => ({ ...f, numAnswers: +v }))}
              options={[
                { value: '4', label: '4 answers' },
                { value: '5', label: '5 answers' },
                { value: '6', label: '6 answers (default)' },
                { value: '8', label: '8 answers' },
              ]}
            />
          </div>
          <Btn onClick={generateAi} disabled={loading || !aiForm.topic}>
            {loading ? '⏳ Generating survey…' : '✨ Generate Survey'}
          </Btn>
          {loading && (
            <p style={{ color: T.muted, fontSize: 12, marginTop: 12 }}>
              This may take 15–30 seconds. Claude is crafting your survey questions…
            </p>
          )}
        </Card>
      )}
    </div>
  );
}

// ─── America Says: Create + Manage Games ─────────────────────────────────────

function AmericaSaysGames({ surveys, onRefresh }) {
  const [asGames, setAsGames] = useState([]);
  const [form, setForm]       = useState({ surveyId: '', name: '', mode: 'solo', defaultTimerSeconds: 30, questionHoldSeconds: 10, revealMode: 'manual', autoAdvance: false, autoAdvanceDelay: 8 });
  const [asTeams, setAsTeams] = useState([
    { id: 'team-a', name: 'Team A', color: T.blue },
    { id: 'team-b', name: 'Team B', color: T.red },
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [created, setCreated] = useState(null);

  const loadAsGames = useCallback(async () => {
    try {
      const data = await apiFetch('/americasays');
      setAsGames(data.games || []);
    } catch {}
  }, []);

  useEffect(() => { loadAsGames(); }, [loadAsGames]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addAsTeam    = () => setAsTeams(t => [...t, { id: `team-${Date.now()}`, name: `Team ${t.length + 1}`, color: T.muted }]);
  const removeAsTeam = (id) => setAsTeams(t => t.filter(x => x.id !== id));
  const updateAsTeam = (id, field, val) => setAsTeams(t => t.map(x => x.id === id ? { ...x, [field]: val } : x));

  const createGame = async () => {
    if (!form.surveyId) return setError('Select a survey');
    if (form.mode === 'teams' && asTeams.length < 2) return setError('Add at least 2 teams');
    setLoading(true); setError('');
    try {
      const res = await apiFetch('/americasays', {
        method: 'POST',
        body: { ...form, teams: form.mode === 'teams' ? asTeams : [] },
      });
      setCreated(res.gameId);
      loadAsGames();
      onRefresh();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const action = async (gameId, endpoint) => {
    try {
      await apiFetch(`/americasays/${gameId}/${endpoint}`, { method: 'POST' });
      loadAsGames();
    } catch (e) { alert(e.message); }
  };

  const deleteGame = async (gameId) => {
    if (!window.confirm('Delete this game?')) return;
    try {
      await apiFetch(`/americasays/${gameId}`, { method: 'DELETE' });
      loadAsGames();
      onRefresh(); // ← also update the shared games state used by Dashboard
    }
    catch (e) { alert(e.message); }
  };

  const statusColors = { waiting: T.amber, active: T.green, completed: T.muted };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Create Game */}
      <Card>
        <h2 style={{ fontSize: 15, color: T.text, fontWeight: 800, marginBottom: 16 }}>
          🎯 Create America Says Game
        </h2>
        <p style={{ color: T.muted, fontSize: 13, marginBottom: 16 }}>
          Family Feud-style: show a prompt on screen, players text their guesses via WhatsApp, points awarded by rank.
        </p>
        <div className="adm-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <Select
            label="Survey"
            value={form.surveyId}
            onChange={v => set('surveyId', v)}
            options={[
              { value: '', label: '— Select survey —' },
              ...surveys.map(s => ({ value: s.id, label: `${s.title} (${s.questions?.length || 0}Q)` })),
            ]}
          />
          <Input label="Game Name" value={form.name} onChange={v => set('name', v)} placeholder="e.g. Wedding Trivia Night" />
          <Select
            label="Mode"
            value={form.mode}
            onChange={v => set('mode', v)}
            options={[
              { value: 'solo',  label: 'Solo — individuals compete' },
              { value: 'teams', label: 'Teams — grouped competition' },
            ]}
          />
          <Select
            label="⏱ Timer per Question"
            value={String(form.defaultTimerSeconds)}
            onChange={v => set('defaultTimerSeconds', Number(v))}
            options={[
              { value: '15',  label: '15 seconds' },
              { value: '20',  label: '20 seconds' },
              { value: '30',  label: '30 seconds (default)' },
              { value: '45',  label: '45 seconds' },
              { value: '60',  label: '60 seconds' },
              { value: '90',  label: '90 seconds' },
              { value: '120', label: '2 minutes' },
            ]}
          />
          <Select
            label="📺 Question Screen Hold Time"
            value={String(form.questionHoldSeconds)}
            onChange={v => set('questionHoldSeconds', Number(v))}
            options={[
              { value: '5',  label: '5 seconds' },
              { value: '8',  label: '8 seconds' },
              { value: '10', label: '10 seconds (default)' },
              { value: '15', label: '15 seconds' },
              { value: '20', label: '20 seconds' },
              { value: '30', label: '30 seconds' },
            ]}
          />
          <Select
            label="👁 Reveal Mode"
            value={form.revealMode}
            onChange={v => set('revealMode', v)}
            options={[
              { value: 'manual', label: 'Manual — admin presses Reveal All' },
              { value: 'auto',   label: 'Auto — reveal when timer expires' },
            ]}
          />
          <Select
            label="⏭ Advance Mode"
            value={form.autoAdvance ? 'auto' : 'manual'}
            onChange={v => set('autoAdvance', v === 'auto')}
            options={[
              { value: 'manual', label: 'Manual — admin presses Next Q' },
              { value: 'auto',   label: 'Auto — advance once all answers show' },
            ]}
          />
          {form.autoAdvance && (
            <Select
              label="⏱ Advance Delay"
              value={String(form.autoAdvanceDelay)}
              onChange={v => set('autoAdvanceDelay', Number(v))}
              options={[
                { value: '3',  label: '3 seconds' },
                { value: '5',  label: '5 seconds' },
                { value: '8',  label: '8 seconds (default)' },
                { value: '12', label: '12 seconds' },
                { value: '20', label: '20 seconds' },
              ]}
            />
          )}
        </div>

        {/* Team builder — shown when mode is 'teams' */}
        {form.mode === 'teams' && (
          <div style={{ marginBottom: 16, padding: '14px 16px', background: T.bg, borderRadius: 10, border: `1px solid ${T.amber}33` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <span style={{ fontSize: 13, color: T.amber, fontWeight: 700 }}>🏆 TEAMS</span>
              <Btn variant="ghost" onClick={addAsTeam} style={{ padding: '4px 10px', fontSize: 11 }}>+ Add Team</Btn>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {asTeams.map(team => (
                <div key={team.id} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input
                    type="color"
                    value={team.color}
                    onChange={e => updateAsTeam(team.id, 'color', e.target.value)}
                    style={{ width: 36, height: 36, border: 'none', borderRadius: 6, cursor: 'pointer', background: 'none' }}
                  />
                  <input
                    value={team.name}
                    onChange={e => updateAsTeam(team.id, 'name', e.target.value)}
                    style={{ flex: 1, background: T.raised, border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 12px', color: T.text, fontSize: 14, outline: 'none' }}
                  />
                  {asTeams.length > 2 && (
                    <Btn variant="ghost" onClick={() => removeAsTeam(team.id)} style={{ padding: '6px 10px', fontSize: 13 }}>✕</Btn>
                  )}
                </div>
              ))}
            </div>
            <p style={{ fontSize: 11, color: T.muted, marginTop: 10, marginBottom: 0 }}>
              Players join by texting <code style={{ color: T.amber }}>/join GAMEID Red</code> or <code style={{ color: T.amber }}>/join GAMEID 1</code>. Auto-assigned if no team specified.
            </p>
          </div>
        )}

        {error   && <p style={{ color: T.red, fontSize: 13, marginBottom: 12 }}>⚠ {error}</p>}
        {created && (
          <div style={{ background: `${T.green}14`, border: `1px solid ${T.green}44`, borderRadius: 10, padding: '13px 16px', marginBottom: 12, fontSize: 13, color: T.green }}>
            ✅ Game <strong>{created}</strong> created!
            <div style={{ marginTop: 8 }}>
              <Btn variant="primary" onClick={() => window.open(`/?game=${created}&type=americasays&view=display`, '_blank')} style={{ fontSize: 12, padding: '6px 14px' }}>
                📺 Open Display Board
              </Btn>
            </div>
          </div>
        )}
        <Btn onClick={createGame} disabled={loading || !form.surveyId}>
          {loading ? 'Creating…' : '+ Create Game'}
        </Btn>
      </Card>

      {/* Games List */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 15, color: T.text, fontWeight: 800 }}>America Says Games</h2>
          <Btn variant="ghost" onClick={loadAsGames} style={{ padding: '6px 12px', fontSize: 12 }}>↻ Refresh</Btn>
        </div>
        {asGames.length === 0 ? (
          <p style={{ color: T.muted, fontSize: 14 }}>No games yet. Create one above.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {asGames.map(game => (
              <div key={game.id} style={{ background: T.bg, borderRadius: 8, border: `1px solid ${statusColors[game.status] || '#222'}33` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', flexWrap: 'wrap' }}>
                  <code style={{ color: T.amber, fontSize: 13, background: '#1a1500', padding: '2px 8px', borderRadius: 4 }}>{game.id}</code>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: T.text, fontSize: 14, fontWeight: 600 }}>{game.name}</div>
                    <div style={{ color: T.muted, fontSize: 12 }}>{game.surveyTitle} · {Object.keys(game.scores || {}).length} players</div>
                  </div>
                  <Badge color={statusColors[game.status] || '#888'}>{game.status}</Badge>
                  {game.status !== 'completed' && (
                    <span style={{ fontSize: 12, color: T.muted }}>
                      Q{(game.currentQ || 0) + 1}{game.autoAdvance ? ' · auto' : ''}
                    </span>
                  )}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {game.status === 'waiting' && (
                      <Btn variant="primary" onClick={() => action(game.id, 'start')} style={{ padding: '5px 12px', fontSize: 12 }}>▶ Start</Btn>
                    )}
                    {game.status === 'active' && (
                      <>
                        <Btn variant="amber" onClick={() => action(game.id, 'next')} style={{ padding: '5px 12px', fontSize: 12 }}>⏭ Next Q</Btn>
                        <Btn variant="blue"  onClick={() => action(game.id, 'reveal-unanswered')} style={{ padding: '5px 12px', fontSize: 12 }} title="Reveal all unanswered answers for the current question">👁 Reveal All</Btn>
                        <Btn variant="danger" onClick={() => action(game.id, 'end')} style={{ padding: '5px 12px', fontSize: 12 }}>■ End</Btn>
                      </>
                    )}
                    <Btn variant="ghost" onClick={() => window.open(`/?game=${game.id}&type=americasays&view=display`, '_blank')} style={{ padding: '5px 12px', fontSize: 12 }}>📺 Display</Btn>
                    {game.status !== 'active' && (
                      <Btn variant="danger" onClick={() => deleteGame(game.id)} style={{ padding: '5px 10px', fontSize: 12, opacity: 0.7 }}>🗑</Btn>
                    )}
                  </div>
                </div>
                {/* Team roster for team-mode America Says games */}
                {game.mode === 'teams' && game.teams?.length > 0 && (
                  <div style={{ padding: '0 12px 10px' }}>
                    <TeamRosterPanel game={game} gameId={game.id} gameType="americasays" onRefresh={loadAsGames} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── GeoGuess Admin ───────────────────────────────────────────────────────────

const API_BASE    = process.env.REACT_APP_API_URL || '/api';
const SERVER_BASE = API_BASE.replace(/\/api\/?$/, ''); // server origin without /api, for static file URLs

function GeoGuessAdmin({ onRefresh }) {
  const [tab,       setTab]       = useState('sets');
  const [sets,      setSets]      = useState([]);
  const [ggGames,   setGgGames]   = useState([]);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');

  const loadSets  = useCallback(async () => {
    try { const d = await apiFetch('/location-sets'); setSets(d.sets || []); } catch {}
  }, []);
  const loadGames = useCallback(async () => {
    try { const d = await apiFetch('/geoguess'); setGgGames(d.games || []); } catch {}
  }, []);

  useEffect(() => { loadSets(); loadGames(); }, [loadSets, loadGames]);

  const tabs = [
    { id: 'sets',   label: '📍 Location Sets' },
    { id: 'games',  label: '🎮 Games' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 4 }}>🗺️ GeoGuess</h1>
        <p style={{ color: T.muted, fontSize: 13 }}>Indoor scavenger hunt — players find hidden codes around the room.</p>
      </div>

      {error   && <div style={{ background:`${T.red}14`,   border:`1px solid ${T.red}44`,   borderRadius:8, padding:'10px 14px', color:T.red,   fontSize:13 }}>⚠ {error}</div>}
      {success && <div style={{ background:`${T.green}14`, border:`1px solid ${T.green}44`, borderRadius:8, padding:'10px 14px', color:T.green, fontSize:13 }}>✅ {success}</div>}

      <div className="adm-tabs" style={{ display:'flex', gap:4, background:T.surface, borderRadius:10, padding:4, width:'fit-content', border:`1px solid ${T.border}` }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setError(''); setSuccess(''); }}
            style={{ background: tab===t.id ? '#222' : 'transparent', color: tab===t.id ? '#fff' : '#666',
              border:'none', borderRadius:8, padding:'7px 16px', fontSize:13, fontWeight:600, cursor:'pointer' }}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'sets'  && <LocationSetManager sets={sets} onRefresh={loadSets} setError={setError} setSuccess={setSuccess} />}
      {tab === 'games' && <GeoGuessGames sets={sets} games={ggGames} onRefresh={() => { loadGames(); onRefresh(); }} setError={setError} setSuccess={setSuccess} />}
    </div>
  );
}

function LocationSetManager({ sets, onRefresh, setError, setSuccess }) {
  const [expanded,  setExpanded]  = useState(null); // setId being edited
  const [creating,  setCreating]  = useState(false);
  const [newName,   setNewName]   = useState('');
  const [newDesc,   setNewDesc]   = useState('');
  const [saving,    setSaving]    = useState(false);

  const createSet = async () => {
    if (!newName.trim()) return setError('Name is required');
    setSaving(true); setError('');
    try {
      await apiFetch('/location-sets', { method: 'POST', body: { name: newName.trim(), description: newDesc.trim(), rounds: [] } });
      setNewName(''); setNewDesc(''); setCreating(false);
      setSuccess('Location set created!');
      onRefresh();
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const deleteSet = async (id, name) => {
    if (!window.confirm(`Delete location set "${name}"?`)) return;
    try { await apiFetch(`/location-sets/${id}`, { method: 'DELETE' }); onRefresh(); }
    catch (e) { setError(e.message); }
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      {/* Create new set */}
      <Card>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: creating ? 16 : 0 }}>
          <h2 style={{ fontSize:15, color:T.text, fontWeight:800 }}>Location Sets</h2>
          <Btn variant="primary" onClick={() => setCreating(c => !c)}>
            {creating ? '✕ Cancel' : '+ New Set'}
          </Btn>
        </div>
        {creating && (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <Input label="Set Name" value={newName} onChange={setNewName} placeholder="e.g. Office Hunt" />
            <Input label="Description (optional)" value={newDesc} onChange={setNewDesc} placeholder="Brief description…" />
            <Btn variant="primary" onClick={createSet} disabled={saving}>{saving ? 'Creating…' : 'Create Set'}</Btn>
          </div>
        )}
      </Card>

      {sets.length === 0 && (
        <div style={{ textAlign:'center', color:T.muted, fontSize:13, padding:'32px 0' }}>
          No location sets yet — create one above.
        </div>
      )}

      {sets.map(set => (
        <Card key={set.id}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <div style={{ fontWeight:700, fontSize:15, color:T.text }}>{set.name}</div>
              {set.description && <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>{set.description}</div>}
              <div style={{ fontSize:11, color:T.muted, marginTop:4 }}>
                {(set.rounds || []).length} round{(set.rounds||[]).length !== 1 ? 's' : ''} · ID: {set.id}
              </div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <Btn variant="blue" onClick={() => setExpanded(expanded === set.id ? null : set.id)} style={{ padding:'5px 12px', fontSize:12 }}>
                {expanded === set.id ? '▲ Close' : '✏️ Edit Rounds'}
              </Btn>
              <Btn variant="danger" onClick={() => deleteSet(set.id, set.name)} style={{ padding:'5px 10px', fontSize:12, opacity:.7 }}>🗑</Btn>
            </div>
          </div>
          {expanded === set.id && (
            <RoundEditor set={set} onRefresh={onRefresh} setError={setError} setSuccess={setSuccess} />
          )}
        </Card>
      ))}
    </div>
  );
}

function RoundEditor({ set, onRefresh, setError, setSuccess }) {
  const [rounds,   setRounds]   = useState(() => (set.rounds || []).map((r, i) => ({ ...r, _key: i })));
  const [saving,   setSaving]   = useState(false);
  const [uploading,setUploading]= useState({});

  const save = async () => {
    setSaving(true); setError('');
    try {
      await apiFetch(`/location-sets/${set.id}`, { method: 'PUT', body: { rounds } });
      setSuccess('Rounds saved!');
      onRefresh();
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const addRound = () => setRounds(r => [...r, {
    _key: Date.now(), hint: '', imageUrl: '', code: '',
    blurLevel: 14, zoomLevel: 1.5, cropX: 50, cropY: 50,
    basePoints: 500, timeLimit: 0,
  }]);

  const removeRound = (idx) => setRounds(r => r.filter((_, i) => i !== idx));

  const setField = (idx, field, val) => setRounds(r => r.map((item, i) => i === idx ? { ...item, [field]: val } : item));

  const uploadImage = async (idx, file) => {
    if (!file) return;
    setUploading(u => ({ ...u, [idx]: true }));
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await fetch(`${API_BASE}/location-sets/${set.id}/upload-image`, { method: 'POST', headers: adminHeaders(), body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setField(idx, 'imageUrl', data.imageUrl);
      setSuccess('Image uploaded!');
    } catch (e) { setError(e.message); }
    setUploading(u => ({ ...u, [idx]: false }));
  };

  return (
    <div style={{ marginTop:20, display:'flex', flexDirection:'column', gap:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ fontSize:13, color:T.muted, fontWeight:700, letterSpacing:1 }}>ROUNDS</div>
        <Btn variant="indigo" onClick={addRound} style={{ padding:'5px 14px', fontSize:12 }}>+ Add Round</Btn>
      </div>

      {rounds.map((round, idx) => (
        <div key={round._key} style={{
          background: T.raised, borderRadius:12, padding:'16px',
          border:`1px solid ${T.border}`,
        }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div style={{ fontWeight:700, fontSize:13, color:T.text }}>Round {idx + 1}</div>
            <Btn variant="danger" onClick={() => removeRound(idx)} style={{ padding:'4px 10px', fontSize:11, opacity:.7 }}>Remove</Btn>
          </div>

          <div className="adm-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            <Input label="Hint text (shown on screen)"
              value={round.hint} onChange={v => setField(idx,'hint',v)} placeholder="Near the main entrance…" />
            <div>
              <div style={{ fontSize:11, color:T.muted, fontWeight:700, letterSpacing:1, marginBottom:6 }}>CODE (secret)</div>
              <input
                type="text"
                value={round.code}
                onChange={e => setField(idx,'code',e.target.value.toUpperCase())}
                placeholder="e.g. X7K2"
                maxLength={12}
                style={{
                  width:'100%', background:T.surface, border:`1px solid ${T.border}`, borderRadius:8,
                  padding:'8px 12px', color:T.text, fontSize:14, fontWeight:800, letterSpacing:4,
                  outline:'none',
                }}
              />
            </div>
          </div>

          {/* Image upload */}
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11, color:T.muted, fontWeight:700, letterSpacing:1, marginBottom:6 }}>LOCATION PHOTO</div>
            <div style={{ display:'flex', gap:10, alignItems:'center' }}>
              <input type="file" accept="image/*" id={`img-${round._key}`} style={{ display:'none' }}
                onChange={e => uploadImage(idx, e.target.files[0])} />
              <label htmlFor={`img-${round._key}`} style={{
                background:T.raised, border:`1px dashed ${T.border}`, borderRadius:8,
                padding:'8px 16px', cursor:'pointer', fontSize:12, color:T.muted, fontWeight:600,
              }}>
                {uploading[idx] ? '⏳ Uploading…' : '📷 Upload Image'}
              </label>
              {round.imageUrl && (
                <span style={{ fontSize:11, color:T.green, fontWeight:600 }}>✓ Image set</span>
              )}
            </div>
            {round.imageUrl && (
              <div style={{ marginTop:8, position:'relative', display:'inline-block' }}>
                <img
                  src={`${SERVER_BASE}${round.imageUrl}`}
                  alt="preview"
                  style={{
                    width:180, height:120, objectFit:'cover', borderRadius:8,
                    filter:`blur(${round.blurLevel}px)`,
                    transform:`scale(${round.zoomLevel})`,
                    transformOrigin:`${round.cropX}% ${round.cropY}%`,
                    transition:'filter .4s,transform .4s',
                  }}
                />
              </div>
            )}
          </div>

          {/* Sliders */}
          <div className="adm-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:12 }}>
            <div>
              <div style={{ fontSize:11, color:T.muted, fontWeight:700, letterSpacing:1, marginBottom:4 }}>
                BLUR: {round.blurLevel}px
              </div>
              <input type="range" min={0} max={30} step={1} value={round.blurLevel}
                onChange={e => setField(idx,'blurLevel',Number(e.target.value))}
                style={{ width:'100%', accentColor: T.accent }} />
            </div>
            <div>
              <div style={{ fontSize:11, color:T.muted, fontWeight:700, letterSpacing:1, marginBottom:4 }}>
                ZOOM: {round.zoomLevel}×
              </div>
              <input type="range" min={1} max={4} step={0.1} value={round.zoomLevel}
                onChange={e => setField(idx,'zoomLevel',Number(e.target.value))}
                style={{ width:'100%', accentColor: T.accent }} />
            </div>
            <div>
              <div style={{ fontSize:11, color:T.muted, fontWeight:700, letterSpacing:1, marginBottom:4 }}>
                CROP X: {round.cropX}%
              </div>
              <input type="range" min={0} max={100} step={1} value={round.cropX}
                onChange={e => setField(idx,'cropX',Number(e.target.value))}
                style={{ width:'100%', accentColor: T.accent }} />
            </div>
            <div>
              <div style={{ fontSize:11, color:T.muted, fontWeight:700, letterSpacing:1, marginBottom:4 }}>
                CROP Y: {round.cropY}%
              </div>
              <input type="range" min={0} max={100} step={1} value={round.cropY}
                onChange={e => setField(idx,'cropY',Number(e.target.value))}
                style={{ width:'100%', accentColor: T.accent }} />
            </div>
            <div>
              <div style={{ fontSize:11, color:T.muted, fontWeight:700, letterSpacing:1, marginBottom:4 }}>
                BASE PTS: {round.basePoints}
              </div>
              <input type="range" min={100} max={1000} step={50} value={round.basePoints}
                onChange={e => setField(idx,'basePoints',Number(e.target.value))}
                style={{ width:'100%', accentColor: T.accent }} />
            </div>
            <div>
              <div style={{ fontSize:11, color:T.muted, fontWeight:700, letterSpacing:1, marginBottom:4 }}>
                TIME LIMIT: {round.timeLimit > 0 ? `${round.timeLimit}s` : 'None'}
              </div>
              <input type="range" min={0} max={300} step={10} value={round.timeLimit}
                onChange={e => setField(idx,'timeLimit',Number(e.target.value))}
                style={{ width:'100%', accentColor: T.accent }} />
            </div>
          </div>
        </div>
      ))}

      <Btn variant="primary" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : '💾 Save Rounds'}
      </Btn>
    </div>
  );
}

function GeoGuessGames({ sets, games, onRefresh, setError, setSuccess }) {
  const [form,    setForm]    = useState({
    locationSetId: '', name: '',
    autoAdvance: false, autoAdvanceDelay: 5,
    showReveal: true,  revealDuration: 3,
  });
  const [creating, setCreating] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [created,  setCreated]  = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const createGame = async () => {
    if (!form.locationSetId) return setError('Select a location set');
    setLoading(true); setError('');
    try {
      const res = await apiFetch('/geoguess', {
        method: 'POST',
        body: {
          locationSetId: form.locationSetId,
          name: form.name || undefined,
          settings: {
            autoAdvance:      form.autoAdvance,
            autoAdvanceDelay: Number(form.autoAdvanceDelay),
            showReveal:       form.showReveal,
            revealDuration:   Number(form.revealDuration),
          },
        },
      });
      setCreated(res.gameId);
      setCreating(false);
      setForm(f => ({ ...f, locationSetId: '', name: '' }));
      setSuccess(`Game ${res.gameId} created!`);
      onRefresh();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const action = async (gameId, endpoint) => {
    try { await apiFetch(`/geoguess/${gameId}/${endpoint}`, { method: 'POST' }); onRefresh(); }
    catch (e) { alert(e.message); }
  };

  const deleteGame = async (gameId) => {
    if (!window.confirm('Delete this GeoGuess game?')) return;
    try { await apiFetch(`/geoguess/${gameId}`, { method: 'DELETE' }); onRefresh(); }
    catch (e) { alert(e.message); }
  };

  const statusColors = { waiting: T.amber, active: T.green, completed: T.muted };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <Card>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: creating ? 16 : 0 }}>
          <h2 style={{ fontSize:15, color:T.text, fontWeight:800 }}>🎮 GeoGuess Games</h2>
          <Btn variant="primary" onClick={() => setCreating(c => !c)}>
            {creating ? '✕ Cancel' : '+ New Game'}
          </Btn>
        </div>
        {creating && (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <Select label="Location Set" value={form.locationSetId} onChange={v => set('locationSetId', v)}
              options={[
                { value: '', label: '— Select location set —' },
                ...sets.map(s => ({ value: s.id, label: `${s.name} (${(s.rounds||[]).length} rounds)` })),
              ]} />
            <Input label="Game Name (optional)" value={form.name} onChange={v => set('name', v)} placeholder="e.g. Office Hunt 2025" />
            <div className="adm-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <Select label="Auto-advance after round solved?" value={form.autoAdvance ? 'yes' : 'no'}
                onChange={v => set('autoAdvance', v === 'yes')}
                options={[{ value:'no', label:'Manual (host clicks Next)' }, { value:'yes', label:'Auto-advance' }]} />
              <Select label="Auto-advance delay" value={String(form.autoAdvanceDelay)}
                onChange={v => set('autoAdvanceDelay', Number(v))}
                options={['3','5','8','10','15','20'].map(s => ({ value:s, label:`${s} seconds` }))} />
              <Select label="Show photo reveal after solve?" value={form.showReveal ? 'yes' : 'no'}
                onChange={v => set('showReveal', v === 'yes')}
                options={[{ value:'yes', label:'Yes — unblur the photo' }, { value:'no', label:'No reveal' }]} />
              <Select label="Reveal duration" value={String(form.revealDuration)}
                onChange={v => set('revealDuration', Number(v))}
                options={['2','3','5','8','10'].map(s => ({ value:s, label:`${s} seconds` }))} />
            </div>
            {created && (
              <div style={{ background:`${T.green}14`, border:`1px solid ${T.green}44`, borderRadius:10, padding:'12px 16px', fontSize:13, color:T.green }}>
                ✅ Game <strong>{created}</strong> created!
                <div style={{ marginTop:8 }}>
                  <Btn variant="primary" onClick={() => window.open(`/?game=${created}&type=geoguess&view=display`, '_blank')} style={{ fontSize:12, padding:'6px 14px' }}>
                    📺 Open Display Board
                  </Btn>
                </div>
              </div>
            )}
            <Btn variant="primary" onClick={createGame} disabled={loading}>{loading ? 'Creating…' : 'Create Game'}</Btn>
          </div>
        )}
      </Card>

      {games.length === 0 && (
        <div style={{ textAlign:'center', color:T.muted, fontSize:13, padding:'24px 0' }}>
          No GeoGuess games yet.
        </div>
      )}

      {games.map(game => {
        const statusColor = statusColors[game.status] || T.muted;
        const scores = Object.values(game.scores || {}).sort((a, b) => b.score - a.score);
        return (
          <Card key={game.id} accent={game.status === 'active' ? T.green : undefined}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
              <div>
                <div style={{ fontWeight:800, fontSize:15, color:T.text }}>{game.name || game.locationSetTitle || game.id}</div>
                <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>
                  ID: {game.id} · Set: {game.locationSetTitle}
                  <span style={{ color:statusColor, marginLeft:8, fontWeight:700, textTransform:'uppercase', fontSize:10 }}>● {game.status}</span>
                </div>
                {game.status === 'active' && (
                  <div style={{ fontSize:12, color:T.muted, marginTop:4 }}>
                    Round {(game.currentRound ?? -1) + 1} of {game.settings ? '?' : '?'}
                  </div>
                )}
              </div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', justifyContent:'flex-end' }}>
                {game.status === 'waiting' && (
                  <Btn variant="primary" onClick={() => action(game.id, 'start')} style={{ padding:'5px 12px', fontSize:12 }}>▶ Start</Btn>
                )}
                {game.status === 'active' && (
                  <>
                    <Btn variant="blue" onClick={() => action(game.id, 'next')} style={{ padding:'5px 12px', fontSize:12 }}>⏭ Next Round</Btn>
                    <Btn variant="amber" onClick={() => action(game.id, 'reveal')} style={{ padding:'5px 12px', fontSize:12 }}>👁 Reveal Answer</Btn>
                    <Btn variant="danger" onClick={() => action(game.id, 'end')} style={{ padding:'5px 12px', fontSize:12 }}>■ End</Btn>
                  </>
                )}
                <Btn variant="ghost" onClick={() => window.open(`/?game=${game.id}&type=geoguess&view=display`, '_blank')} style={{ padding:'5px 12px', fontSize:12 }}>📺 Display</Btn>
                <Btn variant="ghost" onClick={() => window.open('/tv', '_blank')} style={{ padding:'5px 12px', fontSize:12 }} title="Open TV/projector screen">📡 TV</Btn>
                {game.status !== 'active' && (
                  <Btn variant="danger" onClick={() => deleteGame(game.id)} style={{ padding:'5px 10px', fontSize:12, opacity:.7 }}>🗑</Btn>
                )}
              </div>
            </div>
            {scores.length > 0 && (
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {scores.slice(0,5).map((s,i) => (
                  <div key={s.jid||i} style={{
                    background:T.raised, borderRadius:8, padding:'4px 10px',
                    fontSize:12, display:'flex', gap:6, alignItems:'center',
                  }}>
                    <span style={{ color: i===0?T.amber:T.muted }}>{['🥇','🥈','🥉'][i]||`${i+1}.`}</span>
                    <span style={{ fontWeight:600, color:T.text }}>{s.name}</span>
                    <span style={{ color:T.amber, fontWeight:700 }}>{s.score}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ─── Bracket Admin ────────────────────────────────────────────────────────────

const BK_COLOR = '#F2C94C';
const BK_ROUND_NAMES = {
  32: ['Round of 32', 'Round of 16', 'Quarter-finals', 'Semi-finals', 'Final'],
  16: ['Round of 16', 'Quarter-finals', 'Semi-finals', 'Final'],
  8:  ['Quarter-finals', 'Semi-finals', 'Final'],
  4:  ['Semi-finals', 'Final'],
  2:  ['Final'],
};

// Bracket math (mirrors bracketEngine — admin sees full results, so participants
// resolve from the fixture's real results, not a game's reveals)
function bkRoundStarts(teamCount) {
  const starts = []; let acc = 0;
  for (let n = teamCount / 2; n >= 1; n = n / 2) { starts.push(acc); acc += n; }
  return starts;
}
function bkRoundOf(teamCount, idx) {
  const starts = bkRoundStarts(teamCount);
  for (let r = starts.length - 1; r >= 0; r--) if (idx >= starts[r]) return r;
  return 0;
}
function bkParticipants(fixture, idx) {
  const r = bkRoundOf(fixture.teamCount, idx);
  if (r === 0) return { home: fixture.entries[2 * idx] || null, away: fixture.entries[2 * idx + 1] || null };
  const starts = bkRoundStarts(fixture.teamCount);
  const j = idx - starts[r];
  const f = [starts[r - 1] + 2 * j, starts[r - 1] + 2 * j + 1];
  return { home: fixture.results?.[f[0]]?.winner || null, away: fixture.results?.[f[1]]?.winner || null };
}
const bkFlag = (fx, code) => fx.teams?.[code]?.flag || '🏳️';

function BracketAdmin({ onRefresh }) {
  const [subTab,   setSubTab]   = useState('games');
  const [fixtures, setFixtures] = useState([]);
  const [bkGames,  setBkGames]  = useState([]);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');

  const loadFixtures = useCallback(async () => {
    try { const d = await apiFetch('/bracket/fixtures'); setFixtures(d.fixtures || []); } catch {}
  }, []);
  const loadGames = useCallback(async () => {
    try { const d = await apiFetch('/bracket'); setBkGames(d.games || []); } catch {}
  }, []);

  useEffect(() => { loadFixtures(); loadGames(); }, [loadFixtures, loadGames]);

  const tabs = [
    { id: 'games',    label: '🎮 Games' },
    { id: 'fixtures', label: '📋 Fixtures & Results' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 4 }}>🏆 Bracket Challenge</h1>
        <p style={{ color: T.muted, fontSize: 13 }}>
          Knockout-bracket quiz + prediction betting. Quiz rounds spotlight played matches (everyone
          correct in the window scores); markets open on unplayed matches and settle when you enter the real result.
        </p>
      </div>

      {error   && <div style={{ background: T.red + '22', border: `1px solid ${T.red}44`, borderRadius: 10, padding: '10px 16px', color: T.red, fontSize: 13 }}>{error}</div>}
      {success && <div style={{ background: T.green + '22', border: `1px solid ${T.green}44`, borderRadius: 10, padding: '10px 16px', color: T.green, fontSize: 13 }}>{success}</div>}

      <div className="adm-tabs" style={{ display: 'flex', gap: 6, borderBottom: `1px solid ${T.border}`, paddingBottom: 14 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} style={{
            padding: '7px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: subTab === t.id ? `${T.accent}22` : 'transparent',
            color: subTab === t.id ? T.text : T.muted,
            fontWeight: subTab === t.id ? 700 : 500,
            fontSize: 13, borderBottom: subTab === t.id ? `2px solid ${T.accent}` : '2px solid transparent',
            fontFamily: 'inherit',
          }}>{t.label}</button>
        ))}
      </div>

      {subTab === 'fixtures' && <BracketFixtures fixtures={fixtures} onRefresh={loadFixtures} setError={setError} setSuccess={setSuccess} />}
      {subTab === 'games'    && <BracketGames fixtures={fixtures} games={bkGames} onRefresh={() => { loadGames(); onRefresh?.(); }} setError={setError} setSuccess={setSuccess} />}
    </div>
  );
}

// One pending-match result entry row
function BkResultRow({ fixture, idx, onSaved, setError }) {
  const { home, away } = bkParticipants(fixture, idx);
  const [winner, setWinner] = useState('');
  const [hs, setHs] = useState('');
  const [as, setAs] = useState('');
  const [pens, setPens] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!winner) return setError('Pick the winner first');
    setSaving(true); setError('');
    try {
      await apiFetch(`/bracket/fixtures/${fixture.id}/matches/${idx}/result`, {
        method: 'POST',
        body: { winner, homeScore: Number(hs) || 0, awayScore: Number(as) || 0, pens },
      });
      onSaved();
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const teamBtn = (code) => (
    <button onClick={() => setWinner(code)} style={{
      padding: '5px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700,
      border: `1px solid ${winner === code ? T.green : T.border}`,
      background: winner === code ? `${T.green}22` : T.raised,
      color: winner === code ? T.green : T.text, fontFamily: 'inherit',
    }}>{bkFlag(fixture, code)} {code}</button>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      padding: '10px 12px', borderRadius: 10, background: T.raised, border: `1px solid ${T.border}` }}>
      <span style={{ fontSize: 10, color: T.muted, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '.4px', minWidth: 92 }}>
        {(BK_ROUND_NAMES[fixture.teamCount] || [])[bkRoundOf(fixture.teamCount, idx)]} · Pos {idx + 1}
      </span>
      {teamBtn(home)}
      <input value={hs} onChange={e => setHs(e.target.value)} placeholder="0" style={{
        width: 36, padding: '5px 6px', borderRadius: 6, border: `1px solid ${T.border}`,
        background: T.surface, color: T.text, fontSize: 12, textAlign: 'center' }} />
      <span style={{ color: T.muted, fontSize: 12 }}>–</span>
      <input value={as} onChange={e => setAs(e.target.value)} placeholder="0" style={{
        width: 36, padding: '5px 6px', borderRadius: 6, border: `1px solid ${T.border}`,
        background: T.surface, color: T.text, fontSize: 12, textAlign: 'center' }} />
      {teamBtn(away)}
      <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: T.muted, cursor: 'pointer' }}>
        <input type="checkbox" checked={pens} onChange={e => setPens(e.target.checked)} /> pens
      </label>
      <Btn variant="primary" onClick={save} disabled={saving} style={{ padding: '5px 12px', fontSize: 12, marginLeft: 'auto' }}>
        {saving ? 'Saving…' : '✓ Enter Result'}
      </Btn>
    </div>
  );
}

function BracketFixtures({ fixtures, onRefresh, setError, setSuccess }) {
  const [showPaste, setShowPaste] = useState(false);
  const [json,      setJson]      = useState('');
  const [expanded,  setExpanded]  = useState(null);

  const createFromJson = async () => {
    setError('');
    try {
      const body = JSON.parse(json);
      const res = await apiFetch('/bracket/fixtures', { method: 'POST', body });
      setSuccess(`Fixture ${res.id} created!`);
      setJson(''); setShowPaste(false);
      onRefresh();
    } catch (e) { setError(e.message); }
  };

  const del = async (id, nm) => {
    if (!window.confirm(`Delete fixture "${nm}"? Games playing it will break.`)) return;
    try { await apiFetch(`/bracket/fixtures/${id}`, { method: 'DELETE' }); onRefresh(); }
    catch (e) { setError(e.message); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 15, fontWeight: 700 }}>Fixtures ({fixtures.length})</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="ghost" onClick={onRefresh}>↻ Refresh</Btn>
            <Btn variant="primary" onClick={() => setShowPaste(s => !s)}>{showPaste ? '✕ Cancel' : '+ Paste JSON'}</Btn>
          </div>
        </div>
        <div style={{ fontSize: 12, color: T.muted, marginTop: 8 }}>
          Seed the World Cup 2026 bracket with <code style={{ color: BK_COLOR }}>node scripts/seedWorldCup2026.js</code>,
          or paste a custom fixture JSON — <code>{'{ name, teamCount, teams: { CODE: { name, flag } }, entries: [codes…], results? }'}</code>.
        </div>
        {showPaste && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <textarea value={json} onChange={e => setJson(e.target.value)} rows={8}
              placeholder='{"name":"AFCON 2027","teamCount":16,"teams":{...},"entries":[...]}'
              style={{ width: '100%', background: T.raised, border: `1px solid ${T.border}`,
                borderRadius: 10, color: T.text, fontSize: 12, padding: 12, fontFamily: 'monospace', resize: 'vertical' }} />
            <Btn variant="primary" onClick={createFromJson}>Create Fixture</Btn>
          </div>
        )}
      </Card>

      {fixtures.map(fx => {
        const total   = fx.teamCount - 1;
        const entered = Object.keys(fx.results || {}).length;
        const pending = [];
        for (let i = 0; i < total; i++) {
          if (fx.results?.[i]?.winner) continue;
          const { home, away } = bkParticipants(fx, i);
          if (home && away) pending.push(i);
        }
        const isOpen = expanded === fx.id;
        return (
          <Card key={fx.id} accent={isOpen ? BK_COLOR : undefined}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{fx.name}</div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>
                  {fx.teamCount} teams · {entered}/{total} results entered
                  {pending.length > 0 && <span style={{ color: T.amber, marginLeft: 8 }}>{pending.length} match(es) awaiting a result</span>}
                </div>
              </div>
              <Badge color="#666">{fx.id}</Badge>
              <Btn variant="blue" onClick={() => setExpanded(isOpen ? null : fx.id)} style={{ padding: '5px 12px', fontSize: 12 }}>
                {isOpen ? '▲ Close' : '▼ Enter Results'}
              </Btn>
              <Btn variant="danger" onClick={() => del(fx.id, fx.name)} style={{ padding: '5px 10px', fontSize: 12, opacity: .7 }}>🗑</Btn>
            </div>
            {isOpen && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
                {pending.length === 0 ? (
                  <div style={{ color: T.muted, fontSize: 13, textAlign: 'center', padding: '12px 0' }}>
                    No matches awaiting a result — enter earlier rounds first (participants must be known).
                  </div>
                ) : pending.map(i => (
                  <BkResultRow key={`${fx.id}-${i}-${entered}`} fixture={fx} idx={i}
                    onSaved={() => { setSuccess('Result entered — open markets settled.'); onRefresh(); }}
                    setError={setError} />
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function BracketGames({ fixtures, games, onRefresh, setError, setSuccess }) {
  const [creating, setCreating] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [created,  setCreated]  = useState(null);
  const [form,     setForm]     = useState({ fixtureId: '', name: '', quizWindowSecs: 45, autoAdvance: true, autoOpenBetting: true, claimsEnabled: true, claimPointsPerCandidate: 25 });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const createGame = async () => {
    if (!form.fixtureId) return setError('Select a fixture');
    setLoading(true); setError('');
    try {
      const res = await apiFetch('/bracket', {
        method: 'POST',
        body: {
          fixtureId: form.fixtureId,
          name: form.name || undefined,
          settings: {
            quizWindowSecs:  Number(form.quizWindowSecs),
            autoAdvance:     form.autoAdvance,
            autoOpenBetting: form.autoOpenBetting,
            claimsEnabled:   form.claimsEnabled,
            claimPointsPerCandidate: Number(form.claimPointsPerCandidate) || 25,
          },
        },
      });
      setCreated(res.gameId);
      setSuccess(`Game ${res.gameId} created!`);
      setForm(f => ({ ...f, fixtureId: '', name: '' }));
      onRefresh();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const action = async (gameId, endpoint) => {
    try { await apiFetch(`/bracket/${gameId}/${endpoint}`, { method: 'POST' }); onRefresh(); }
    catch (e) { alert(e.message); }
  };

  const deleteGame = async (gameId) => {
    if (!window.confirm('Delete this bracket game?')) return;
    try { await apiFetch(`/bracket/${gameId}`, { method: 'DELETE' }); onRefresh(); }
    catch (e) { alert(e.message); }
  };

  const statusColors = { waiting: T.amber, active: T.green, completed: T.muted };
  const phaseLabel   = { quiz: '🎯 QUIZ ROUND', betting: '💰 BETTING OPEN', idle: '⏸ IDLE' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: creating ? 16 : 0 }}>
          <h2 style={{ fontSize: 15, color: T.text, fontWeight: 800 }}>🎮 Bracket Games</h2>
          <Btn variant="primary" onClick={() => setCreating(c => !c)}>{creating ? '✕ Cancel' : '+ New Game'}</Btn>
        </div>
        {creating && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Select label="Fixture" value={form.fixtureId} onChange={v => set('fixtureId', v)}
              options={[
                { value: '', label: '— Select fixture —' },
                ...fixtures.map(f => ({ value: f.id, label: `${f.name} (${f.teamCount} teams, ${Object.keys(f.results || {}).length}/${f.teamCount - 1} results)` })),
              ]} />
            <Input label="Game Name (optional)" value={form.name} onChange={v => set('name', v)} placeholder="e.g. WC2026 Bracket Challenge" />
            <div className="adm-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <Select label="Quiz answer window" value={String(form.quizWindowSecs)}
                onChange={v => set('quizWindowSecs', Number(v))}
                options={['10', '20', '30', '45', '60', '90'].map(s => ({ value: s, label: `${s} seconds` }))} />
              <Select label="Auto-advance quiz rounds?" value={form.autoAdvance ? 'yes' : 'no'}
                onChange={v => set('autoAdvance', v === 'yes')}
                options={[{ value: 'yes', label: 'Auto (recommended)' }, { value: 'no', label: 'Manual (host clicks Next)' }]} />
              <Select label="Open betting after quiz?" value={form.autoOpenBetting ? 'yes' : 'no'}
                onChange={v => set('autoOpenBetting', v === 'yes')}
                options={[{ value: 'yes', label: 'Auto-open markets' }, { value: 'no', label: 'Manual' }]} />
            </div>
            <div className="adm-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <Select label="Fill-the-bracket claims (text “5 ESP”)?" value={form.claimsEnabled ? 'yes' : 'no'}
                onChange={v => set('claimsEnabled', v === 'yes')}
                options={[{ value: 'yes', label: 'On — call positions early' }, { value: 'no', label: 'Off' }]} />
              {form.claimsEnabled && (
                <Select label="Claim points per candidate team" value={String(form.claimPointsPerCandidate)}
                  onChange={v => set('claimPointsPerCandidate', Number(v))}
                  options={['10', '25', '50'].map(s => ({ value: s, label: `${s} pts × teams alive` }))} />
              )}
            </div>
            {created && (
              <div style={{ background: `${T.green}14`, border: `1px solid ${T.green}44`, borderRadius: 10, padding: '12px 16px', fontSize: 13, color: T.green }}>
                ✅ Game <strong>{created}</strong> created!
                <div style={{ marginTop: 8 }}>
                  <Btn variant="primary" onClick={() => window.open(`/?game=${created}&type=bracket&view=display`, '_blank')} style={{ fontSize: 12, padding: '6px 14px' }}>
                    📺 Open Display Board
                  </Btn>
                </div>
              </div>
            )}
            <Btn variant="primary" onClick={createGame} disabled={loading}>{loading ? 'Creating…' : 'Create Game'}</Btn>
          </div>
        )}
      </Card>

      {games.length === 0 && (
        <div style={{ textAlign: 'center', color: T.muted, fontSize: 13, padding: '24px 0' }}>
          No bracket games yet.
        </div>
      )}

      {games.map(game => {
        const statusColor = statusColors[game.status] || T.muted;
        const scores = Object.values(game.scores || {}).sort((a, b) => (b.score || 0) - (a.score || 0));
        return (
          <Card key={game.id} accent={game.status === 'active' ? BK_COLOR : undefined}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, color: T.text }}>{game.name || game.id}</div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                  ID: {game.id} · Fixture: {game.fixtureName}
                  <span style={{ color: statusColor, marginLeft: 8, fontWeight: 700, textTransform: 'uppercase', fontSize: 10 }}>● {game.status}</span>
                  {game.status === 'active' && (
                    <span style={{ color: BK_COLOR, marginLeft: 8, fontWeight: 700, fontSize: 10 }}>{phaseLabel[game.phase] || ''}</span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {game.status === 'waiting' && (
                  <Btn variant="primary" onClick={() => action(game.id, 'start')} style={{ padding: '5px 12px', fontSize: 12 }}>▶ Start</Btn>
                )}
                {game.status === 'active' && (
                  <>
                    <Btn variant="blue" onClick={() => action(game.id, 'next')} style={{ padding: '5px 12px', fontSize: 12 }}>⏭ Next Quiz</Btn>
                    <Btn variant="amber" onClick={() => action(game.id, 'betting')} style={{ padding: '5px 12px', fontSize: 12 }}>💰 Open Betting</Btn>
                    <Btn variant="danger" onClick={() => action(game.id, 'end')} style={{ padding: '5px 12px', fontSize: 12 }}>■ End</Btn>
                  </>
                )}
                <Btn variant="ghost" onClick={() => window.open(`/?game=${game.id}&type=bracket&view=display`, '_blank')} style={{ padding: '5px 12px', fontSize: 12 }}>📺 Display</Btn>
                <Btn variant="ghost" onClick={() => window.open('/tv', '_blank')} style={{ padding: '5px 12px', fontSize: 12 }} title="Open TV/projector screen">📡 TV</Btn>
                {game.status !== 'active' && (
                  <Btn variant="danger" onClick={() => deleteGame(game.id)} style={{ padding: '5px 10px', fontSize: 12, opacity: .7 }}>🗑</Btn>
                )}
              </div>
            </div>
            {scores.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {scores.slice(0, 5).map((s, i) => (
                  <div key={s.jid || i} style={{
                    background: T.raised, borderRadius: 8, padding: '4px 10px',
                    fontSize: 12, display: 'flex', gap: 6, alignItems: 'center', opacity: s.left ? .5 : 1,
                  }}>
                    <span style={{ color: i === 0 ? T.amber : T.muted }}>{['🥇', '🥈', '🥉'][i] || `${i + 1}.`}</span>
                    <span style={{ fontWeight: 600, color: T.text }}>{s.name}</span>
                    <span style={{ color: T.amber, fontWeight: 700 }}>{s.score}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ─── Chain Reaction Admin ─────────────────────────────────────────────────────

const CR_COLOR = '#A78BFA';

function ChainReactionAdmin({ onRefresh, crRefreshTick = 0 }) {
  const [subTab, setSubTab] = useState('games');
  const [chains, setChains] = useState([]);
  const [crGames, setCrGames] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [prefillChainId, setPrefillChainId] = useState('');
  const [editChain, setEditChain] = useState(null); // chain being edited in the Builder

  const loadChains = useCallback(async () => {
    try { const d = await apiFetch('/chainreaction/chains'); setChains(d.chains || []); } catch {}
  }, []);
  const loadCrGames = useCallback(async () => {
    try { const d = await apiFetch('/chainreaction'); setCrGames(d.games || []); } catch {}
  }, []);

  useEffect(() => { loadChains(); loadCrGames(); }, [loadChains, loadCrGames]);

  // Refresh games list when Ably signals a speed-chain or bonus-round state change
  const prevTickRef = useRef(0);
  useEffect(() => {
    if (crRefreshTick !== prevTickRef.current) {
      prevTickRef.current = crRefreshTick;
      loadCrGames();
    }
  }, [crRefreshTick, loadCrGames]);

  const tabs = [
    { id: 'chains', label: '🔗 Chain Library' },
    { id: 'build',  label: '✏️ Builder'        },
    { id: 'json',   label: '{ } JSON Import'   },
    { id: 'ai',     label: '🤖 AI Generate'    },
    { id: 'games',  label: '🎮 Games'           },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 4 }}>🔗 Chain Reaction</h1>
        <p style={{ color: T.muted, fontSize: 13 }}>Word-chain game — each link connects to the next. Players race to find bridging words.</p>
      </div>

      {error   && <div style={{ background: T.red + '22',   border: `1px solid ${T.red}44`,   borderRadius: 10, padding: '10px 16px', color: T.red,   fontSize: 13 }}>{error}</div>}
      {success && <div style={{ background: T.green + '22', border: `1px solid ${T.green}44`, borderRadius: 10, padding: '10px 16px', color: T.green, fontSize: 13 }}>{success}</div>}

      <div className="adm-tabs" style={{ display: 'flex', gap: 6, borderBottom: `1px solid ${T.border}`, paddingBottom: 14 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => { if (t.id === 'build') setEditChain(null); setSubTab(t.id); setError(''); setSuccess(''); }} style={{
            padding: '7px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: subTab === t.id ? `${CR_COLOR}22` : 'transparent',
            color: subTab === t.id ? T.text : T.muted,
            fontWeight: subTab === t.id ? 700 : 500,
            fontSize: 13, borderBottom: subTab === t.id ? `2px solid ${CR_COLOR}` : '2px solid transparent',
            fontFamily: 'inherit',
          }}>{t.label}</button>
        ))}
      </div>

      {subTab === 'chains' && (
        <CrChainLibrary
          chains={chains}
          onRefresh={loadChains}
          setError={setError}
          setSuccess={setSuccess}
          onUseInGame={(chainId) => { setPrefillChainId(chainId); setSubTab('games'); }}
          onEdit={(c) => { setEditChain(c); setSubTab('build'); }}
        />
      )}
      {subTab === 'build' && (
        <CrManualBuilder
          onRefresh={loadChains}
          setError={setError}
          setSuccess={setSuccess}
          editChain={editChain}
          onClearEdit={() => setEditChain(null)}
        />
      )}
      {subTab === 'json' && (
        <CrJsonImport
          onRefresh={loadChains}
          setError={setError}
          setSuccess={setSuccess}
        />
      )}
      {subTab === 'ai' && (
        <CrAiGenerator
          onRefresh={loadChains}
          setError={setError}
          setSuccess={setSuccess}
        />
      )}
      {subTab === 'games' && (
        <CrGames
          chains={chains}
          games={crGames}
          prefillChainId={prefillChainId}
          onRefresh={() => { loadCrGames(); onRefresh(); }}
          setError={setError}
          setSuccess={setSuccess}
        />
      )}
    </div>
  );
}

function CrChainLibrary({ chains, onRefresh, setError, setSuccess, onUseInGame, onEdit }) {
  const deleteChain = async (id) => {
    if (!window.confirm('Delete this chain?')) return;
    try {
      await apiFetch(`/chainreaction/chains/${id}`, { method: 'DELETE' });
      setSuccess('Chain deleted.');
      onRefresh();
    } catch (e) { setError(e.message); }
  };

  const diffColor = { easy: T.green, medium: T.amber, hard: T.red };

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 15, fontWeight: 800 }}>Chain Library ({chains.length})</h2>
        <Btn variant="ghost" onClick={onRefresh} style={{ padding: '6px 12px', fontSize: 12 }}>↻ Refresh</Btn>
      </div>
      {chains.length === 0 ? (
        <div style={{ color: T.muted, fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
          No chains yet — create one using the Builder or AI Generator.
        </div>
      ) : chains.map(c => (
        <div key={c.id} style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
          borderRadius: 10, marginBottom: 8, background: T.raised, border: `1px solid ${T.border}`,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: T.text }}>{c.title}</div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>
              {c.category && <span style={{ marginRight: 8 }}>{c.category}</span>}
              {c.links?.length || 0} words
              {c.links?.length > 0 && (
                <span style={{ marginLeft: 8, color: T.muted }}>
                  {c.links.slice(0, 4).join(' → ')}{c.links.length > 4 ? ' …' : ''}
                </span>
              )}
            </div>
          </div>
          {c.difficulty && (
            <Badge color={diffColor[c.difficulty] || T.muted}>{c.difficulty}</Badge>
          )}
          <Badge color={T.muted}>{c.id}</Badge>
          <Btn variant="indigo" onClick={() => onUseInGame(c.id)} style={{ padding: '4px 10px', fontSize: 11 }}>Use in Game</Btn>
          <Btn variant="ghost" onClick={() => onEdit(c)} style={{ padding: '4px 10px', fontSize: 11 }}>✏️ Edit</Btn>
          <Btn variant="danger" onClick={() => deleteChain(c.id)} style={{ padding: '4px 10px', fontSize: 11, opacity: 0.7 }}>🗑</Btn>
        </div>
      ))}
    </Card>
  );
}

function CrManualBuilder({ onRefresh, setError, setSuccess, editChain, onClearEdit }) {
  const [title,      setTitle]      = useState('');
  const [category,   setCategory]   = useState('');
  const [difficulty, setDifficulty] = useState('medium');
  const [links,      setLinks]      = useState(['', '', '', '']);
  const [loading,    setLoading]    = useState(false);
  const [editingId,  setEditingId]  = useState(null);

  // Prefill the form when the library hands us a chain to edit.
  useEffect(() => {
    if (!editChain) return;
    setEditingId(editChain.id);
    setTitle(editChain.title || '');
    setCategory(editChain.category || '');
    setDifficulty(editChain.difficulty || 'medium');
    setLinks((editChain.links && editChain.links.length >= 4 ? editChain.links : ['', '', '', '']).map(w => String(w || '')));
  }, [editChain]);

  const updateLink = (i, val) => {
    const next = [...links];
    next[i] = val.toUpperCase();
    setLinks(next);
  };

  const addLink = () => {
    if (links.length < 12) setLinks(l => [...l, '']);
  };

  const removeLink = (i) => {
    if (links.length > 4) setLinks(l => l.filter((_, idx) => idx !== i));
  };

  const pairPhrases = links
    .filter(l => l.trim())
    .map((w, i, arr) => i < arr.length - 1 ? `${w} + ${arr[i + 1]}` : null)
    .filter(Boolean);

  const submit = async () => {
    const cleanLinks = links.map(l => l.trim()).filter(Boolean);
    if (!title) return setError('Enter a title');
    if (cleanLinks.length < 4) return setError('At least 4 words required');
    setLoading(true); setError(''); setSuccess('');
    try {
      const body = { title, category, difficulty, links: cleanLinks, pairPhrases };
      if (editingId) body.id = editingId; // update in place
      const res = await apiFetch('/chainreaction/chains', { method: 'POST', body });
      setSuccess(`Chain "${title}" ${editingId ? 'updated' : 'saved'}!`);
      setEditingId(res.chainId);
      onRefresh();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const resetForm = () => {
    setEditingId(null); setTitle(''); setCategory(''); setDifficulty('medium'); setLinks(['', '', '', '']);
    setError(''); setSuccess(''); if (onClearEdit) onClearEdit();
  };

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <h2 style={{ fontSize: 15, fontWeight: 800 }}>
          {editingId ? 'Edit Chain' : 'Manual Chain Builder'}
          {editingId && <code style={{ marginLeft: 10, color: CR_COLOR, fontSize: 12, background: `${CR_COLOR}18`, padding: '2px 8px', borderRadius: 4 }}>{editingId}</code>}
        </h2>
        {editingId && <Btn variant="ghost" onClick={resetForm} style={{ fontSize: 12 }}>+ New chain</Btn>}
      </div>
      <div className="adm-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <Input label="Title" value={title} onChange={setTitle} placeholder="e.g. Caribbean Vibes" />
        <Input label="Category" value={category} onChange={setCategory} placeholder="e.g. Geography" />
        <Select
          label="Difficulty"
          value={difficulty}
          onChange={setDifficulty}
          options={[
            { value: 'easy',   label: 'Easy' },
            { value: 'medium', label: 'Medium' },
            { value: 'hard',   label: 'Hard' },
          ]}
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', marginBottom: 8 }}>
          Chain Links ({links.length} / 12)
        </div>
        {links.map((link, i) => (
          <div key={i} style={{ marginBottom: 4 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: T.muted, width: 20, textAlign: 'right', flexShrink: 0 }}>{i + 1}.</span>
              <input
                value={link}
                onChange={e => updateLink(i, e.target.value)}
                placeholder={`Word ${i + 1}…`}
                style={{
                  flex: 1, background: T.raised, border: `1px solid ${T.border}`, borderRadius: 8,
                  padding: '7px 10px', color: T.text, fontSize: 14, fontWeight: 700,
                  letterSpacing: 2, outline: 'none', textTransform: 'uppercase',
                }}
              />
              {links.length > 4 && (
                <Btn variant="ghost" onClick={() => removeLink(i)} style={{ padding: '4px 8px', fontSize: 11 }}>✕</Btn>
              )}
            </div>
            {i < links.length - 1 && links[i].trim() && links[i + 1].trim() && (
              <div style={{ fontSize: 10, color: CR_COLOR, paddingLeft: 30, marginTop: 2, fontWeight: 600, letterSpacing: 1 }}>
                → {links[i].trim()} + {links[i + 1].trim()}
              </div>
            )}
          </div>
        ))}
        {links.length < 12 && (
          <Btn variant="ghost" onClick={addLink} style={{ marginTop: 8, padding: '5px 12px', fontSize: 11 }}>+ Add Word</Btn>
        )}
      </div>

      {pairPhrases.length > 0 && (
        <div style={{ background: T.bg, borderRadius: 8, padding: '10px 14px', marginBottom: 16, border: `1px solid ${CR_COLOR}33` }}>
          <div style={{ fontSize: 10, color: CR_COLOR, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>PAIR PREVIEW</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {pairPhrases.map((p, i) => (
              <span key={i} style={{ background: `${CR_COLOR}14`, color: CR_COLOR, border: `1px solid ${CR_COLOR}33`, borderRadius: 14, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>{p}</span>
            ))}
          </div>
        </div>
      )}

      <Btn onClick={submit} disabled={loading}>{loading ? 'Saving…' : editingId ? '💾 Update Chain' : '💾 Save Chain'}</Btn>
    </Card>
  );
}

function CrJsonImport({ onRefresh, setError, setSuccess }) {
  const exampleJson = JSON.stringify({
    chains: [
      {
        title: 'Caribbean Vibes',
        category: 'Geography',
        difficulty: 'medium',
        links: ['BLUE', 'SEA', 'SIDE', 'WALK', 'OUT', 'DOOR'],
      },
    ],
  }, null, 2);

  const [jsonText, setJsonText] = useState(exampleJson);
  const [loading,  setLoading]  = useState(false);

  const submit = async () => {
    setLoading(true); setError(''); setSuccess('');
    try {
      const body = JSON.parse(jsonText);
      if (!body.chains || !Array.isArray(body.chains) || body.chains.length === 0)
        throw new Error('JSON must have a "chains" array with at least one chain');
      const res = await apiFetch('/chainreaction/chains/import', { method: 'POST', body });
      setSuccess(`Imported ${res.count || body.chains.length} chain(s) successfully!`);
      onRefresh();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <Card>
      <h2 style={{ fontSize: 15, fontWeight: 800, marginBottom: 4 }}>JSON Import</h2>
      <p style={{ color: T.muted, fontSize: 13, marginBottom: 16 }}>
        Paste a JSON object with a <code style={{ color: CR_COLOR }}>chains</code> array. Each chain needs <code style={{ color: CR_COLOR }}>title</code>, <code style={{ color: CR_COLOR }}>links</code> (array of words), and optional <code style={{ color: CR_COLOR }}>category</code>, <code style={{ color: CR_COLOR }}>difficulty</code>.
      </p>
      <textarea
        value={jsonText}
        onChange={e => setJsonText(e.target.value)}
        rows={18}
        style={{
          width: '100%', background: T.bg, border: `1px solid ${T.border}`,
          borderRadius: 8, padding: 12, color: T.text, fontSize: 12,
          fontFamily: 'monospace', outline: 'none', resize: 'vertical',
        }}
      />
      <div style={{ marginTop: 12 }}>
        <Btn onClick={submit} disabled={loading}>{loading ? 'Importing…' : '📥 Import Chains'}</Btn>
      </div>
    </Card>
  );
}

function CrAiGenerator({ onRefresh, setError, setSuccess }) {
  const [form, setForm] = useState({
    topWord: '', bottomWord: '', theme: '',
    chainLength: 6, culturalContext: 'general',
  });
  const [loading,  setLoading]  = useState(false);
  const [generated, setGenerated] = useState(null);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const generate = async () => {
    setLoading(true); setError(''); setSuccess(''); setGenerated(null);
    try {
      const res = await apiFetch('/chainreaction/chains/generate', { method: 'POST', body: form });
      setGenerated(res.chain || res);
      setSuccess('Chain generated and saved!');
      onRefresh();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <Card>
      <h2 style={{ fontSize: 15, fontWeight: 800, marginBottom: 4 }}>🤖 AI Chain Generator</h2>
      <p style={{ color: T.muted, fontSize: 13, marginBottom: 20 }}>
        Claude will generate a word chain. Provide top/bottom anchor words, or just a theme.
      </p>
      <div className="adm-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <Input label="Top Word (optional)" value={form.topWord} onChange={v => upd('topWord', v.toUpperCase())} placeholder="e.g. BLUE" />
        <Input label="Bottom Word (optional)" value={form.bottomWord} onChange={v => upd('bottomWord', v.toUpperCase())} placeholder="e.g. DOOR" />
        <Input label="Theme (optional)" value={form.theme} onChange={v => upd('theme', v)} placeholder="e.g. Caribbean island life" style={{ gridColumn: '1 / -1' }} />
        <Select
          label="Chain Length"
          value={String(form.chainLength)}
          onChange={v => upd('chainLength', Number(v))}
          options={['5','6','7','8','9','10'].map(n => ({ value: n, label: `${n} words` }))}
        />
        <Select
          label="Cultural Context"
          value={form.culturalContext}
          onChange={v => upd('culturalContext', v)}
          options={[
            { value: 'general',   label: 'General' },
            { value: 'caribbean', label: 'Caribbean' },
            { value: 'jamaican',  label: 'Jamaican' },
          ]}
        />
      </div>

      <Btn onClick={generate} disabled={loading} variant="indigo">
        {loading ? '⏳ Generating…' : '✨ Generate with Claude'}
      </Btn>
      {loading && (
        <p style={{ color: T.muted, fontSize: 12, marginTop: 12 }}>
          This may take 10–20 seconds. Claude is crafting your chain…
        </p>
      )}

      {generated && (
        <div style={{ marginTop: 20, background: T.bg, borderRadius: 10, padding: '14px 16px', border: `1px solid ${CR_COLOR}33` }}>
          <div style={{ fontSize: 12, color: CR_COLOR, fontWeight: 700, letterSpacing: 1, marginBottom: 10 }}>
            GENERATED: {generated.title}
            {generated.category && <span style={{ color: T.muted, marginLeft: 8 }}>{generated.category}</span>}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {(generated.links || []).map((w, i, arr) => (
              <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ background: `${CR_COLOR}22`, color: CR_COLOR, border: `1px solid ${CR_COLOR}44`, borderRadius: 8, padding: '4px 12px', fontWeight: 700, fontSize: 13 }}>{w}</span>
                {i < arr.length - 1 && <span style={{ color: T.muted, fontSize: 12 }}>→</span>}
              </span>
            ))}
          </div>
          {generated.pairPhrases && generated.pairPhrases.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {generated.pairPhrases.map((p, i) => (
                <span key={i} style={{ background: `${CR_COLOR}14`, color: T.muted, border: `1px solid ${CR_COLOR}22`, borderRadius: 14, padding: '2px 9px', fontSize: 10, fontWeight: 600 }}>{p}</span>
              ))}
            </div>
          )}
          <p style={{ fontSize: 12, color: T.green, marginTop: 10, marginBottom: 0 }}>✅ Chain was automatically saved to the library.</p>
        </div>
      )}
    </Card>
  );
}

function CrGames({ chains, games: gamesProp, prefillChainId, onRefresh, setError, setSuccess }) {
  const [games, setGames] = useState(gamesProp);
  useEffect(() => { setGames(gamesProp); }, [gamesProp]);

  const [form, setForm] = useState({
    chainIds: prefillChainId ? [prefillChainId] : [],
    mode: 'buzzin',
    lettersRevealInterval: 6,
    pointsPerLink: 100,
    autoAdvance: false,
    autoAdvanceDelay: 8,
    useTeams: false,
    name: '',
    coopHintInterval: 60,
    coopHintPrize: 25,
    fillOrder: 'sequential',
    speedChainSetup: { enabled: false, chainIds: [], prize: 150 },
    bonusRoundSetup: { enabled: false, chainIds: ['', '', ''], prize: 300 },
    answersVisible: false,
  });
  const [crTeams, setCrTeams] = useState([
    { id: 'team-a', name: 'Team A', color: T.blue },
    { id: 'team-b', name: 'Team B', color: T.red },
  ]);
  const [creating,        setCreating]        = useState(false);
  const [created,         setCreated]         = useState(null);
  const [addingChain,     setAddingChain]     = useState('');
  const [speedChainForm,  setSpeedChainForm]  = useState({}); // { [gameId]: { chainId, prize, open } }
  const [bonusRoundForm,  setBonusRoundForm]  = useState({}); // { [gameId]: { chainIds: [], prize, open } }
  const [scSubmitting,    setScSubmitting]    = useState({}); // { [gameId]: bool }
  const [brSubmitting,    setBrSubmitting]    = useState({}); // { [gameId]: bool }
  const [scReveal,        setScReveal]        = useState({}); // { [gameId]: bool } — answers visible toggle
  const [brReveal,        setBrReveal]        = useState({}); // { [gameId]: bool }

  const toggleScReveal = async (gid, show) => {
    setScReveal(r => ({ ...r, [gid]: show }));
    try { await apiFetch(`/chainreaction/${gid}/speed-chain/set-reveal`, { method: 'POST', body: { show } }); }
    catch (e) { setError(`Reveal toggle failed: ${e.message}`); setScReveal(r => ({ ...r, [gid]: !show })); }
  };
  const toggleBrReveal = async (gid, show) => {
    setBrReveal(r => ({ ...r, [gid]: show }));
    try { await apiFetch(`/chainreaction/${gid}/bonus/set-reveal`, { method: 'POST', body: { show } }); }
    catch (e) { setError(`Reveal toggle failed: ${e.message}`); setBrReveal(r => ({ ...r, [gid]: !show })); }
  };

  const openSpeedChainForm  = (gid) => setSpeedChainForm(f => ({ ...f, [gid]: f[gid]?.open ? undefined : { chainId: '', prize: 150, open: true } }));
  const openBonusRoundForm  = (gid) => setBonusRoundForm(f => ({ ...f, [gid]: f[gid]?.open ? undefined : { chainIds: ['', '', ''], prize: 300, open: true } }));

  const startSpeedChain = async (gid) => {
    const form = speedChainForm[gid];
    if (!form?.chainId) return setError('Select a chain for the speed round');
    if (scSubmitting[gid]) return;
    setScSubmitting(s => ({ ...s, [gid]: true }));
    setError('');
    try {
      await apiFetch(`/chainreaction/${gid}/speed-chain/start`, { method: 'POST', body: { chainId: form.chainId, prize: Number(form.prize) || 150 } });
      setSuccess('Speed chain started!');
      setSpeedChainForm(f => ({ ...f, [gid]: undefined }));
      onRefresh();
    } catch (e) { setError(`Speed chain failed: ${e.message}`); }
    setScSubmitting(s => ({ ...s, [gid]: false }));
  };

  const startBonusRound = async (gid) => {
    const form = bonusRoundForm[gid];
    if (!form?.chainIds || form.chainIds.filter(Boolean).length !== 3) return setError('Select exactly 3 chains for the bonus round');
    if (brSubmitting[gid]) return;
    setBrSubmitting(s => ({ ...s, [gid]: true }));
    setError('');
    try {
      await apiFetch(`/chainreaction/${gid}/bonus/start`, { method: 'POST', body: { chainIds: form.chainIds, prize: Number(form.prize) || 300 } });
      setSuccess('Bonus round started!');
      setBonusRoundForm(f => ({ ...f, [gid]: undefined }));
      onRefresh();
    } catch (e) { setError(`Bonus round failed: ${e.message}`); }
    setBrSubmitting(s => ({ ...s, [gid]: false }));
  };

  useEffect(() => {
    if (prefillChainId) setForm(f => ({
      ...f, chainIds: f.chainIds.includes(prefillChainId) ? f.chainIds : [...f.chainIds, prefillChainId],
    }));
  }, [prefillChainId]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addRound    = (cid) => { if (!cid || form.chainIds.includes(cid)) return; upd('chainIds', [...form.chainIds, cid]); setAddingChain(''); };
  const removeRound = (idx) => upd('chainIds', form.chainIds.filter((_, i) => i !== idx));
  const moveRound   = (from, to) => {
    const arr = [...form.chainIds];
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    upd('chainIds', arr);
  };

  const addCrTeam    = () => setCrTeams(t => [...t, { id: `team-${Date.now()}`, name: `Team ${t.length + 1}`, color: T.muted }]);
  const removeCrTeam = (id) => setCrTeams(t => t.filter(x => x.id !== id));
  const updateCrTeam = (id, field, val) => setCrTeams(t => t.map(x => x.id === id ? { ...x, [field]: val } : x));

  const createGame = async () => {
    if (form.chainIds.length === 0) return setError('Select at least one chain');
    const sc = form.speedChainSetup;
    const br = form.bonusRoundSetup;
    if (sc.enabled && (sc.chainIds || []).filter(Boolean).length !== form.chainIds.length) return setError(`Select a Speed Chain for every round (${form.chainIds.length} needed)`);
    if (br.enabled && br.chainIds.filter(Boolean).length !== 3) return setError('Select exactly 3 chains for the Bonus Round');
    setCreating(true); setError('');
    try {
      const res = await apiFetch('/chainreaction', {
        method: 'POST',
        body: {
          chainIds: form.chainIds,
          mode: form.mode,
          name: form.name || undefined,
          pointsPerLink: Number(form.pointsPerLink),
          timePerReveal: Number(form.lettersRevealInterval),
          autoAdvance: form.autoAdvance,
          autoAdvanceDelay: Number(form.autoAdvanceDelay),
          teams: form.useTeams ? crTeams : [],
          coopHintInterval: Number(form.coopHintInterval),
          coopHintPrize: Number(form.coopHintPrize),
          fillOrder: form.fillOrder || 'sequential',
          speedChainConfig: sc.enabled && (sc.chainIds || []).filter(Boolean).length === form.chainIds.length
            ? { chainIds: sc.chainIds, prize: Number(sc.prize) || 150 }
            : null,
          bonusRoundConfig: br.enabled && br.chainIds.filter(Boolean).length === 3
            ? { chainIds: br.chainIds, prize: Number(br.prize) || 300 }
            : null,
          answersVisible: form.answersVisible,
        },
      });
      setCreated(res.gameId);
      setSuccess(`Game ${res.gameId} created!`);
      onRefresh();
    } catch (e) { setError(e.message); }
    setCreating(false);
  };

  const action = async (gameId, endpoint) => {
    try {
      await apiFetch(`/chainreaction/${gameId}/${endpoint}`, { method: 'POST' });
      setSuccess(`Done — ${gameId}`);
      onRefresh();
    } catch (e) { setError(e.message); }
  };

  const patchAction = async (id, endpoint, body, optimistic) => {
    if (optimistic) {
      setGames(prev => prev.map(g => g.id === id ? { ...g, ...optimistic } : g));
    }
    try {
      await apiFetch(`/chainreaction/${id}/${endpoint}`, { method: 'PATCH', body });
      onRefresh();
    } catch (e) {
      onRefresh(); // revert optimistic update on failure
      setError(e.message);
    }
  };

  const deleteGame = async (gameId) => {
    if (!window.confirm(`Delete game ${gameId}?`)) return;
    try {
      await apiFetch(`/chainreaction/${gameId}`, { method: 'DELETE' });
      onRefresh();
    } catch (e) { setError(e.message); }
  };

  const statusColors  = { waiting: T.amber, active: T.green, completed: T.muted };
  const availableChains = chains.filter(c => !form.chainIds.includes(c.id));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Card>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Create Game</h2>

        {/* ── Rounds / Chain selector ── */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
            Chains / Rounds
            {form.chainIds.length > 0 && <span style={{ color: CR_COLOR, marginLeft: 8 }}>{form.chainIds.length} round{form.chainIds.length !== 1 ? 's' : ''}</span>}
          </label>

          {form.chainIds.length === 0 && (
            <p style={{ fontSize: 12, color: T.muted, margin: '0 0 10px' }}>Add at least one chain below. Add multiple chains to create a multi-round game.</p>
          )}

          {/* Ordered round list */}
          {form.chainIds.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
              {form.chainIds.map((cid, idx) => {
                const c = chains.find(x => x.id === cid);
                return (
                  <div key={cid} style={{ display: 'flex', alignItems: 'center', gap: 8, background: `${CR_COLOR}12`, border: `1px solid ${CR_COLOR}33`, borderRadius: 8, padding: '8px 12px' }}>
                    <span style={{ color: CR_COLOR, fontWeight: 800, fontSize: 12, minWidth: 26, flexShrink: 0 }}>R{idx + 1}</span>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c?.title || cid}</span>
                    <span style={{ fontSize: 11, color: T.muted, flexShrink: 0 }}>{c?.links?.length || 0}w · {c?.difficulty || ''}</span>
                    <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                      {idx > 0 && <Btn variant="ghost" onClick={() => moveRound(idx, idx - 1)} style={{ padding: '2px 6px', fontSize: 11 }}>↑</Btn>}
                      {idx < form.chainIds.length - 1 && <Btn variant="ghost" onClick={() => moveRound(idx, idx + 1)} style={{ padding: '2px 6px', fontSize: 11 }}>↓</Btn>}
                      <Btn variant="ghost" onClick={() => removeRound(idx)} style={{ padding: '2px 6px', fontSize: 11, color: T.red }}>✕</Btn>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add chain picker — selecting immediately adds the chain */}
          {availableChains.length > 0 ? (
            <select
              value=""
              onChange={e => { if (e.target.value) addRound(e.target.value); }}
              style={{ width: '100%', background: T.raised, border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 12px', color: T.muted, fontSize: 13, outline: 'none', cursor: 'pointer' }}
            >
              <option value="">— Select a chain to add —</option>
              {availableChains.map(c => (
                <option key={c.id} value={c.id}>{c.title} ({c.links?.length || 0} words · {c.difficulty})</option>
              ))}
            </select>
          ) : chains.length === 0 ? (
            <p style={{ fontSize: 12, color: T.muted, margin: 0 }}>No chains in library — build or import chains first.</p>
          ) : (
            <p style={{ fontSize: 12, color: T.muted, margin: 0 }}>All chains added.</p>
          )}
        </div>

        <div className="adm-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input label="Game Name (optional)" value={form.name} onChange={v => upd('name', v)} placeholder="e.g. Friday Night Chain" style={{ gridColumn: '1 / -1' }} />
          <Select
            label="Mode"
            value={form.mode}
            onChange={v => upd('mode', v)}
            options={[
              { value: 'buzzin',      label: 'Buzz In — race to answer first' },
              { value: 'cooperative', label: 'Cooperative — team works together' },
            ]}
          />
          <Input label="Points Per Link" value={form.pointsPerLink} onChange={v => upd('pointsPerLink', v)} type="number" />
          {form.mode === 'buzzin' && (
            <Select
              label="Letters Reveal Interval (seconds)"
              value={String(form.lettersRevealInterval)}
              onChange={v => upd('lettersRevealInterval', Number(v))}
              options={['3','4','5','6','7','8','9','10','12','15'].map(n => ({ value: n, label: `${n}s` }))}
            />
          )}
          {(form.mode === 'cooperative') && (
            <Input label="Coop Hint Interval (seconds)" value={form.coopHintInterval} onChange={v => upd('coopHintInterval', v)} type="number" />
          )}
          {(form.mode === 'cooperative') && (
            <Input label="Coop Hint Prize (pts)" value={form.coopHintPrize} onChange={v => upd('coopHintPrize', v)} type="number" />
          )}
        </div>

        {/* Letter fill order — only shown in buzzin mode */}
        {form.mode === 'buzzin' && (
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', flexShrink: 0 }}>Letter Fill Order</span>
            <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${CR_COLOR}44` }}>
              {[['sequential', '▶ Sequential'], ['random', '🔀 Random']].map(([val, label]) => {
                const active = (form.fillOrder || 'sequential') === val;
                return (
                  <button key={val} onClick={() => upd('fillOrder', val)}
                    style={{ padding: '6px 12px', fontSize: 11, fontWeight: 700, fontFamily: 'inherit', border: 'none', cursor: active ? 'default' : 'pointer', background: active ? CR_COLOR : 'transparent', color: active ? '#fff' : CR_COLOR, transition: 'all .2s', whiteSpace: 'nowrap' }}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Auto-advance — only shown when multiple rounds */}
        {form.chainIds.length > 1 && (
          <div style={{ marginTop: 14, background: `${CR_COLOR}08`, border: `1px solid ${CR_COLOR}22`, borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', marginBottom: 10 }}>Round Advance ({form.chainIds.length} rounds)</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button
                onClick={() => upd('autoAdvance', !form.autoAdvance)}
                style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${form.autoAdvance ? CR_COLOR + '66' : T.border}`, cursor: 'pointer', fontFamily: 'inherit', background: form.autoAdvance ? `${CR_COLOR}22` : T.raised, color: form.autoAdvance ? CR_COLOR : T.muted, fontSize: 12, fontWeight: 700 }}
              >
                {form.autoAdvance ? '⚡ Auto-Advance ON' : '✋ Manual Advance'}
              </button>
              {form.autoAdvance && (
                <Select
                  label=""
                  value={String(form.autoAdvanceDelay)}
                  onChange={v => upd('autoAdvanceDelay', Number(v))}
                  options={['5','6','7','8','10','12','15','20','30'].map(n => ({ value: n, label: `after ${n}s` }))}
                  style={{ width: 130 }}
                />
              )}
            </div>
            <p style={{ fontSize: 11, color: T.muted, margin: '8px 0 0' }}>
              {form.autoAdvance
                ? `Next round starts automatically ${form.autoAdvanceDelay}s after each chain completes.`
                : 'Click ⏭ Next Round in this panel between each chain.'}
            </p>
          </div>
        )}

        {/* Speed Chain Setup */}
        {(() => {
          const sc4   = chains.filter(c => (c.links?.length || 0) >= 4);
          const sc    = form.speedChainSetup;
          const updSc = (k, v) => upd('speedChainSetup', { ...sc, [k]: v });
          const setScChain = (i, v) => {
            const ids = [...(sc.chainIds || [])];
            ids[i] = v;
            updSc('chainIds', ids);
          };
          const numRounds = form.chainIds.length || 1;
          return (
            <div style={{ marginTop: 14, background: '#1a1a2e', border: `1px solid ${sc.enabled ? '#f59e0b55' : T.border}`, borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: sc.enabled ? 12 : 0 }}>
                <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase' }}>⚡ Speed Chain (after each round)</div>
                <button
                  onClick={() => updSc('enabled', !sc.enabled)}
                  style={{ padding: '5px 12px', borderRadius: 8, border: `1px solid ${sc.enabled ? '#f59e0b66' : T.border}`, cursor: 'pointer', background: sc.enabled ? '#f59e0b22' : T.raised, color: sc.enabled ? '#f59e0b' : T.muted, fontSize: 11, fontWeight: 700, fontFamily: 'inherit' }}
                >
                  {sc.enabled ? 'ON' : 'OFF'}
                </button>
              </div>
              {sc.enabled && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {Array.from({ length: numRounds }, (_, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 800, minWidth: 28, flexShrink: 0 }}>R{i + 1}</span>
                      <select
                        value={sc.chainIds?.[i] || ''}
                        onChange={e => setScChain(i, e.target.value)}
                        style={{ flex: 1, background: T.raised, border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 10px', color: sc.chainIds?.[i] ? T.text : T.muted, fontSize: 12, outline: 'none' }}
                      >
                        <option value="">— Round {i + 1} speed chain —</option>
                        {sc4.map(c => <option key={c.id} value={c.id}>{c.title} ({c.links?.length}w)</option>)}
                      </select>
                    </div>
                  ))}
                  <div style={{ marginTop: 4 }}>
                    <label style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Prize per speed chain (pts)</label>
                    <input type="number" value={sc.prize} onChange={e => updSc('prize', e.target.value)} style={{ width: 140, background: T.raised, border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 10px', color: T.text, fontSize: 12, outline: 'none' }} />
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Bonus Round Setup */}
        {(() => {
          const br4 = chains.filter(c => (c.links?.length || 0) >= 4);
          const br = form.bonusRoundSetup;
          const updBr = (k, v) => upd('bonusRoundSetup', { ...br, [k]: v });
          const setBrChain = (idx, val) => {
            const ids = [...br.chainIds];
            ids[idx] = val;
            updBr('chainIds', ids);
          };
          return (
            <div style={{ marginTop: 14, background: '#1a1a2e', border: `1px solid ${br.enabled ? '#8b5cf655' : T.border}`, borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: br.enabled ? 12 : 0 }}>
                <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase' }}>💎 Bonus Round (final)</div>
                <button
                  onClick={() => updBr('enabled', !br.enabled)}
                  style={{ padding: '5px 12px', borderRadius: 8, border: `1px solid ${br.enabled ? '#8b5cf666' : T.border}`, cursor: 'pointer', background: br.enabled ? '#8b5cf622' : T.raised, color: br.enabled ? '#8b5cf6' : T.muted, fontSize: 11, fontWeight: 700, fontFamily: 'inherit' }}
                >
                  {br.enabled ? 'ON' : 'OFF'}
                </button>
              </div>
              {br.enabled && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[0, 1, 2].map(idx => (
                    <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: '#8b5cf6', fontWeight: 800, minWidth: 20 }}>C{idx + 1}</span>
                      <select value={br.chainIds[idx] || ''} onChange={e => setBrChain(idx, e.target.value)} style={{ flex: 1, background: T.raised, border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 10px', color: br.chainIds[idx] ? T.text : T.muted, fontSize: 12, outline: 'none' }}>
                        <option value="">— Select chain {idx + 1} —</option>
                        {br4.map(c => <option key={c.id} value={c.id}>{c.title} ({c.links?.length}w)</option>)}
                      </select>
                    </div>
                  ))}
                  <div style={{ marginTop: 4 }}>
                    <label style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Prize (pts)</label>
                    <input type="number" value={br.prize} onChange={e => updBr('prize', e.target.value)} style={{ width: 140, background: T.raised, border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 10px', color: T.text, fontSize: 12, outline: 'none' }} />
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Teams */}
        <div style={{ marginTop: 14 }}>
          <label style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Teams</label>
          <button onClick={() => upd('useTeams', !form.useTeams)} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${form.useTeams ? CR_COLOR + '55' : T.border}`, cursor: 'pointer', background: form.useTeams ? `${CR_COLOR}22` : T.raised, fontFamily: 'inherit', color: form.useTeams ? CR_COLOR : T.muted, fontSize: 12, fontWeight: 700 }}>
            {form.useTeams ? '🏆 Teams ON' : 'Solo / Individual'}
          </button>
        </div>

        {form.useTeams && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              <span style={{ fontSize: 13, color: T.muted, fontWeight: 600 }}>TEAMS</span>
              <Btn variant="ghost" onClick={addCrTeam} style={{ padding: '4px 10px', fontSize: 11 }}>+ Add Team</Btn>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {crTeams.map(team => (
                <div key={team.id} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input type="color" value={team.color} onChange={e => updateCrTeam(team.id, 'color', e.target.value)} style={{ width: 34, height: 34, border: 'none', borderRadius: 6, cursor: 'pointer', background: 'none' }} />
                  <input value={team.name} onChange={e => updateCrTeam(team.id, 'name', e.target.value)} style={{ flex: 1, background: T.raised, border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 12px', color: T.text, fontSize: 14, outline: 'none' }} />
                  {crTeams.length > 1 && <Btn variant="ghost" onClick={() => removeCrTeam(team.id)} style={{ padding: '6px 10px', fontSize: 13 }}>✕</Btn>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Display Options */}
        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: T.raised, border: `1px solid ${T.border}`, borderRadius: 10 }}>
          <ToggleSwitch
            value={!!form.answersVisible}
            onChange={v => upd('answersVisible', v)}
            label="Show answers on display screen"
          />
          <span style={{ fontSize: 11, color: T.muted }}>
            {form.answersVisible ? 'Solved words will be visible on the TV during speed chain & bonus round.' : 'Solved words stay hidden on the TV during speed chain & bonus round.'}
          </span>
        </div>

        {created && (
          <div style={{ background: `${T.green}14`, border: `1px solid ${T.green}44`, borderRadius: 10, padding: '12px 16px', marginTop: 14, fontSize: 13, color: T.green }}>
            ✅ Game <strong>{created}</strong> created!
            <div style={{ marginTop: 8 }}>
              <Btn variant="primary" onClick={() => window.open('/tv', '_blank')} style={{ fontSize: 12, padding: '6px 14px' }}>📡 Open TV Display</Btn>
            </div>
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <Btn onClick={createGame} disabled={creating || form.chainIds.length === 0}>
            {creating ? 'Creating…' : form.chainIds.length > 1 ? `+ Create Game (${form.chainIds.length} rounds)` : '+ Create Game'}
          </Btn>
        </div>
      </Card>

      {/* Games list */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700 }}>Chain Reaction Games ({games.length})</h2>
          <Btn variant="ghost" onClick={onRefresh}>↻ Refresh</Btn>
        </div>
        {games.length === 0 ? (
          <div style={{ color: T.muted, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No games yet — create one above.</div>
        ) : games.map(g => {
          const statusColor  = statusColors[g.status] || T.muted;
          const isMultiRound = g.totalRounds > 1;
          const roundIdx     = g.currentRound ?? 0;
          const hasNextRound = isMultiRound && g.status === 'active' && roundIdx + 1 < g.totalRounds && !g.autoAdvance;
          return (
            <div key={g.id} style={{ padding: '14px 16px', borderRadius: 12, marginBottom: 8, background: T.raised, border: `1px solid ${g.status === 'active' ? CR_COLOR + '44' : T.border}`, boxShadow: g.status === 'active' ? `0 0 12px ${CR_COLOR}18` : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{g.name || g.id}</div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ color: statusColor, fontWeight: 700, textTransform: 'uppercase' }}>{g.status}</span>
                    {g.mode && <span>{g.mode}</span>}
                    {isMultiRound && (
                      <span style={{ color: CR_COLOR, fontWeight: 600, background: `${CR_COLOR}18`, borderRadius: 4, padding: '1px 6px' }}>
                        Round {roundIdx + 1}/{g.totalRounds}{g.autoAdvance ? ' · auto' : ' · manual'}
                      </span>
                    )}
                    {!isMultiRound && g.chainTitle && <span style={{ color: CR_COLOR }}>{g.chainTitle}</span>}
                    {g.speedChainConfig && <span style={{ color: '#f59e0b', fontWeight: 700, background: '#f59e0b18', borderRadius: 4, padding: '1px 6px' }}>⚡ SC</span>}
                    {g.bonusRoundConfig && <span style={{ color: '#8b5cf6', fontWeight: 700, background: '#8b5cf618', borderRadius: 4, padding: '1px 6px' }}>💎 BR</span>}
                  </div>
                </div>
                <Badge color={T.muted}>{g.id}</Badge>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
                  {g.status === 'waiting' && (
                    <Btn variant="primary" onClick={() => action(g.id, 'start')}>▶ Start</Btn>
                  )}
                  {hasNextRound && (
                    <Btn onClick={() => action(g.id, 'next-round')} style={{ background: `${CR_COLOR}22`, color: CR_COLOR, border: `1px solid ${CR_COLOR}55`, borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                      ⏭ Next Round
                    </Btn>
                  )}
                  {g.status === 'active' && !g.speedChain && !g.bonusRound && (
                    <Btn variant="amber" onClick={() => action(g.id, 'reveal-active')} style={{ fontSize: 12 }} title="Reveal the current word (no points) and move to the next one">
                      👁 Reveal Word
                    </Btn>
                  )}
                  {(g.status === 'active' || g.status === 'completed') && !g.speedChain && !g.bonusRound && !g.speedChainConfig && (
                    <Btn variant="ghost" onClick={() => openSpeedChainForm(g.id)} style={{ fontSize: 12 }}>⚡ Speed Chain</Btn>
                  )}
                  {g.speedChain?.status === 'active' && (
                    <>
                      <Btn variant="ghost" onClick={() => action(g.id, 'speed-chain/next-player')} style={{ fontSize: 12 }}>⏭ Next Player</Btn>
                      <Btn variant="ghost" onClick={() => action(g.id, 'speed-chain/reveal')} style={{ fontSize: 12 }}>👁 Reveal</Btn>
                    </>
                  )}
                  {g.speedChain && ['active', 'revealed', 'no-answer'].includes(g.speedChain.status) && (
                    <ToggleSwitch value={!!scReveal[g.id]} onChange={v => toggleScReveal(g.id, v)} label="Show Answers" />
                  )}
                  {(g.status === 'completed' || g.speedChain?.status === 'completed' || g.speedChain?.status === 'revealed') && !g.bonusRound && !g.bonusRoundConfig && (
                    <Btn variant="ghost" onClick={() => openBonusRoundForm(g.id)} style={{ fontSize: 12 }}>💎 Bonus Round</Btn>
                  )}
                  {g.bonusRound?.status === 'active' && (
                    <>
                      <Btn variant="ghost" onClick={() => action(g.id, 'bonus/next-player')} style={{ fontSize: 12 }}>⏭ Next Player</Btn>
                      <Btn variant="ghost" onClick={() => action(g.id, 'bonus/reveal')} style={{ fontSize: 12 }}>👁 Reveal</Btn>
                    </>
                  )}
                  {g.bonusRound && ['active', 'revealed'].includes(g.bonusRound.status) && (
                    <ToggleSwitch value={!!brReveal[g.id]} onChange={v => toggleBrReveal(g.id, v)} label="Show Answers" />
                  )}
                  {g.status === 'active' && g.mode === 'buzzin' && (
                    <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${CR_COLOR}44`, flexShrink: 0 }}>
                      {[['sequential', '▶ Seq'], ['random', '🔀 Rnd']].map(([val, label]) => {
                        const active = (g.fillOrder || 'sequential') === val;
                        return (
                          <button key={val} onClick={() => !active && patchAction(g.id, 'fill-order', { fillOrder: val }, { fillOrder: val })} style={{ padding: '6px 10px', fontSize: 11, fontWeight: 700, fontFamily: 'inherit', border: 'none', cursor: active ? 'default' : 'pointer', background: active ? CR_COLOR : 'transparent', color: active ? '#fff' : CR_COLOR, transition: 'all .2s', whiteSpace: 'nowrap' }}>
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {g.status === 'active' && (
                    <Btn variant="danger" onClick={() => action(g.id, 'force-end')}>■ End</Btn>
                  )}
                  {g.status !== 'active' && (
                    <Btn variant="ghost" onClick={() => deleteGame(g.id)}>🗑</Btn>
                  )}
                </div>
              </div>
              {/* Speed chain inline form */}
              {speedChainForm[g.id]?.open && (
                <div style={{ marginTop: 12, padding: '12px 14px', background: `rgba(255,179,0,.06)`, border: `1px solid rgba(255,179,0,.25)`, borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#FFB300', letterSpacing: 1 }}>⚡ Start Speed Chain</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ flex: 2, minWidth: 160 }}>
                      <div style={{ fontSize: 10, color: T.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.5px' }}>Chain</div>
                      <select value={speedChainForm[g.id]?.chainId || ''} onChange={e => setSpeedChainForm(f => ({ ...f, [g.id]: { ...f[g.id], chainId: e.target.value } }))} style={{ width: '100%', background: T.raised, border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 10px', color: T.text, fontSize: 12, outline: 'none' }}>
                        <option value="">— Select chain (4+ words) —</option>
                        {chains.filter(c => (c.links?.length || 0) >= 4).map(c => <option key={c.id} value={c.id}>{c.title} ({c.links?.length || 0}w)</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 1, minWidth: 80 }}>
                      <div style={{ fontSize: 10, color: T.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.5px' }}>Prize pts</div>
                      <input type="number" value={speedChainForm[g.id]?.prize || 150} onChange={e => setSpeedChainForm(f => ({ ...f, [g.id]: { ...f[g.id], prize: e.target.value } }))} style={{ width: '100%', background: T.raised, border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 10px', color: T.text, fontSize: 12, outline: 'none' }} />
                    </div>
                    <Btn variant="primary" onClick={() => startSpeedChain(g.id)} disabled={scSubmitting[g.id]} style={{ fontSize: 12, padding: '8px 14px', opacity: scSubmitting[g.id] ? 0.5 : 1 }}>{scSubmitting[g.id] ? '…' : 'Start ⚡'}</Btn>
                    <Btn variant="ghost" onClick={() => setSpeedChainForm(f => ({ ...f, [g.id]: undefined }))} style={{ fontSize: 12, padding: '8px 10px' }}>✕</Btn>
                  </div>
                </div>
              )}

              {/* Bonus round inline form */}
              {bonusRoundForm[g.id]?.open && (
                <div style={{ marginTop: 12, padding: '12px 14px', background: `rgba(124,77,255,.06)`, border: `1px solid rgba(124,77,255,.25)`, borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#7C4DFF', letterSpacing: 1 }}>💎 Start Bonus Round — select 3 chains</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {[0, 1, 2].map(idx => (
                      <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <div style={{ fontSize: 11, color: T.muted, minWidth: 50 }}>Chain {idx + 1}</div>
                        <select value={bonusRoundForm[g.id]?.chainIds?.[idx] || ''} onChange={e => setBonusRoundForm(f => { const ids = [...(f[g.id]?.chainIds || ['','',''])]; ids[idx] = e.target.value; return { ...f, [g.id]: { ...f[g.id], chainIds: ids } }; })} style={{ flex: 1, background: T.raised, border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 10px', color: T.text, fontSize: 12, outline: 'none' }}>
                          <option value="">— Select (4+ words) —</option>
                          {chains.filter(c => (c.links?.length || 0) >= 4).map(c => <option key={c.id} value={c.id}>{c.title} ({c.links?.length || 0}w)</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, color: T.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.5px' }}>Prize pts</div>
                      <input type="number" value={bonusRoundForm[g.id]?.prize || 300} onChange={e => setBonusRoundForm(f => ({ ...f, [g.id]: { ...f[g.id], prize: e.target.value } }))} style={{ width: '100%', background: T.raised, border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 10px', color: T.text, fontSize: 12, outline: 'none' }} />
                    </div>
                    <Btn variant="primary" onClick={() => startBonusRound(g.id)} disabled={brSubmitting[g.id]} style={{ fontSize: 12, padding: '8px 14px', opacity: brSubmitting[g.id] ? 0.5 : 1 }}>{brSubmitting[g.id] ? '…' : 'Start 💎'}</Btn>
                    <Btn variant="ghost" onClick={() => setBonusRoundForm(f => ({ ...f, [g.id]: undefined }))} style={{ fontSize: 12, padding: '8px 10px' }}>✕</Btn>
                  </div>
                </div>
              )}

              {g.scores && Object.keys(g.scores).length > 0 && (
                <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {Object.values(g.scores).sort((a, b) => b.score - a.score).slice(0, 5).map((s, i) => (
                    <div key={s.name + i} style={{ background: 'rgba(255,255,255,.04)', borderRadius: 6, padding: '3px 10px', fontSize: 11 }}>
                      <span style={{ color: i === 0 ? '#FFD700' : T.muted }}>{['🥇', '🥈', '🥉'][i] || `#${i + 1}`}</span>
                      <span style={{ marginLeft: 5, color: T.text }}>{s.name}</span>
                      <span style={{ marginLeft: 5, color: CR_COLOR }}>{s.score}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </Card>
    </div>
  );
}

// ─── Organizations: print + detail views ─────────────────────────────────────
// Opens a clean, print-friendly window with a single table (avoids fighting the
// SPA layout with @media print) and triggers the browser print dialog.
function printTable(title, headers, rows) {
  const w = window.open('', '_blank');
  if (!w) return;
  const esc = (s) => String(s ?? '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
  w.document.write(`<!doctype html><html><head><title>${esc(title)}</title><style>
    body{font-family:system-ui,Arial,sans-serif;padding:28px;color:#111}
    h1{font-size:20px;margin:0 0 14px}
    table{border-collapse:collapse;width:100%}
    th,td{border:1px solid #bbb;padding:6px 10px;text-align:left;font-size:12.5px}
    th{background:#eee} tr:nth-child(even) td{background:#f7f7f7}
  </style></head><body><h1>${esc(title)}</h1>
    <table><thead><tr>${headers.map(h => `<th>${esc(h)}</th>`).join('')}</tr></thead>
    <tbody>${rows.map(r => `<tr>${r.map(c => `<td>${esc(c)}</td>`).join('')}</tr>`).join('')}</tbody></table>
    <script>window.onload=function(){window.print()}</script></body></html>`);
  w.document.close();
}

const CR_TYPE_LABEL = { crossword: 'Crossword', americasays: 'America Says', geoguess: 'GeoGuess', droppan: 'Drop Pan', chainreaction: 'Chain Reaction' };

function WinnerCard({ orgId, winner, onChange }) {
  const [notes, setNotes]   = useState(winner.notes || '');
  const [saving, setSaving] = useState(false);
  const dirty = notes !== (winner.notes || '');

  const patch = async (body) => { await apiFetch(`/organizations/${orgId}/winners/${winner.gameId}`, { method: 'PUT', body }); onChange(); };
  const saveNotes = async () => { setSaving(true); try { await patch({ notes }); } catch {} setSaving(false); };

  return (
    <div style={{ padding: '14px 16px', borderRadius: 12, marginBottom: 10, background: T.raised, border: `1px solid ${winner.awarded ? T.green + '55' : T.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{winner.gameName} <span style={{ color: T.muted, fontWeight: 500, fontSize: 12 }}>· {CR_TYPE_LABEL[winner.gameType] || winner.gameType}</span></div>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{winner.endedAt ? new Date(winner.endedAt).toLocaleString() : ''}</div>
        </div>
        <ToggleSwitch value={!!winner.awarded} onChange={(v) => patch({ awarded: v })} label={winner.awarded ? 'Awarded' : 'Mark awarded'} />
      </div>
      {winner.teams?.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: 5 }}>🏆 Winning team{winner.teams.length > 1 ? 's' : ''}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {winner.teams.map(t => (
              <div key={t.rank} style={{ display: 'flex', alignItems: 'center', gap: 8, background: t.rank === 1 ? 'rgba(255,215,0,.1)' : T.surface, border: `1px solid ${t.rank === 1 ? '#FFD70044' : T.border}`, borderRadius: 8, padding: '6px 10px' }}>
                <span style={{ fontWeight: 800, color: t.rank === 1 ? '#FFD700' : t.rank === 2 ? '#C0C0C0' : t.rank === 3 ? '#CD7F32' : T.muted }}>#{t.rank}</span>
                <span style={{ fontWeight: 700, fontSize: 13 }}>{t.name}</span>
                <span style={{ fontSize: 10, color: T.muted }}>{t.playerCount}p</span>
                <Badge color={T.green}>{t.score}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: 5 }}>Top players</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        {(winner.top || []).map(t => (
          <div key={t.rank} style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: '6px 10px' }}>
            <span style={{ fontWeight: 800, color: t.rank === 1 ? '#FFD700' : t.rank === 2 ? '#C0C0C0' : t.rank === 3 ? '#CD7F32' : T.muted }}>#{t.rank}</span>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</span>
            {t.team && <span style={{ fontSize: 10, color: T.muted, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 4, padding: '1px 6px' }}>{t.team}</span>}
            <span style={{ fontSize: 11, color: T.muted }}>{t.phone}</span>
            <Badge color={T.accent}>{t.score}</Badge>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <Input label="Prize notes" value={notes} onChange={setNotes} placeholder="e.g. $50 voucher to #1 — awarded at town hall" />
        </div>
        <Btn variant="ghost" onClick={saveNotes} disabled={!dirty || saving} style={{ fontSize: 12 }}>{saving ? 'Saving…' : 'Save'}</Btn>
      </div>
    </div>
  );
}

function OrgDetail({ orgId, onBack }) {
  const [org, setOrg] = useState(null);
  const [tab, setTab] = useState('leaderboard');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try { setOrg(await apiFetch(`/organizations/${orgId}`)); } catch (e) { setError(e.message); }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const leaderboard = org ? Object.values(org.leaderboard || {}).sort((a, b) => (b.totalScore || 0) - (a.totalScore || 0)) : [];
  const winners     = org ? Object.values(org.winners || {}).sort((a, b) => (b.endedAt || 0) - (a.endedAt || 0)) : [];
  const exportCsv = (kind) =>
    downloadCsv(
      `/organizations/${orgId}/${kind}.csv`,
      `${org?.slug || orgId}-${kind}.csv`,
    ).catch((e) => setError(e.message));

  const printLeaderboard = () => printTable(`${org.name} — Lifetime Leaderboard`,
    ['Rank', 'Name', 'Phone', 'Total', 'Games', 'Wins', 'Podiums'],
    leaderboard.map((p, i) => [i + 1, p.name, p.phone, p.totalScore || 0, p.gamesPlayed || 0, p.wins || 0, p.podiums || 0]));
  const printWinners = () => printTable(`${org.name} — Game Winners`,
    ['Game', 'Type', 'Date', 'Rank', 'Name', 'Phone', 'Score', 'Awarded', 'Notes'],
    winners.flatMap(w => (w.top || []).map(t => [w.gameName, w.gameType, w.endedAt ? new Date(w.endedAt).toLocaleDateString() : '', t.rank, t.name, t.phone, t.score, w.awarded ? 'yes' : 'no', w.notes || ''])));

  const empty = (text) => <div style={{ color: T.muted, fontSize: 13, textAlign: 'center', padding: '24px 0' }}>{text}</div>;

  if (!org) return <div style={{ color: T.muted, fontSize: 13 }}>{error || 'Loading…'}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Btn variant="ghost" onClick={onBack}>← Back</Btn>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', flex: 1 }}>🏢 {org.name}</h1>
      </div>

      <div className="adm-tabs" style={{ display: 'flex', gap: 6, borderBottom: `1px solid ${T.border}`, paddingBottom: 12 }}>
        {[['leaderboard', '🏆 Lifetime Leaderboard'], ['winners', '🎖 Game Winners']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{ padding: '7px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', background: tab === id ? 'rgba(255,179,0,.14)' : 'transparent', color: tab === id ? T.text : T.muted, fontWeight: tab === id ? 700 : 500, fontSize: 13, fontFamily: 'inherit' }}>{label}</button>
        ))}
      </div>

      {tab === 'leaderboard' && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700 }}>Lifetime Leaderboard ({leaderboard.length})</h2>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn variant="ghost" style={{ fontSize: 12 }} onClick={() => exportCsv('leaderboard')}>⬇ CSV</Btn>
              <Btn variant="ghost" onClick={printLeaderboard} style={{ fontSize: 12 }}>🖨 Print</Btn>
            </div>
          </div>
          {leaderboard.length === 0 ? empty('No scores yet — finish a game tagged to this org.') : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {leaderboard.map((p, i) => (
                <div key={p.jid || i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10, background: T.raised, border: `1px solid ${T.border}` }}>
                  <span style={{ fontWeight: 800, fontSize: 16, minWidth: 32, color: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : T.muted }}>#{i + 1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: T.muted }}>{p.phone} · {p.gamesPlayed || 0} games · {p.wins || 0} wins · {p.podiums || 0} podiums</div>
                  </div>
                  <Badge color={T.green}>{p.totalScore || 0} pts</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === 'winners' && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700 }}>Game Winners ({winners.length})</h2>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn variant="ghost" style={{ fontSize: 12 }} onClick={() => exportCsv('winners')}>⬇ CSV</Btn>
              <Btn variant="ghost" onClick={printWinners} style={{ fontSize: 12 }}>🖨 Print</Btn>
            </div>
          </div>
          {winners.length === 0 ? empty('No completed games yet for this org.') : (
            winners.map(w => <WinnerCard key={w.gameId} orgId={orgId} winner={w} onChange={load} />)
          )}
        </Card>
      )}
    </div>
  );
}

// ─── Organizations Admin ──────────────────────────────────────────────────────
function OrganizationsAdmin() {
  const [orgs, setOrgs]         = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [name, setName]         = useState('');
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [viewOrg, setViewOrg]   = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch('/organizations');
      setOrgs(d.organizations || []);
      setActiveId(d.activeOrganizationId || null);
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 2500); };

  const setActive = async (id) => {
    setError('');
    try {
      await apiFetch(`/organizations/active/${id || 'none'}`, { method: 'PUT' });
      setActiveId(id || null);
      flash(id ? 'Active organization set' : 'Active organization cleared');
    } catch (e) { setError(e.message); }
  };

  const create = async () => {
    if (!name.trim()) return setError('Enter an organization name');
    setBusy(true); setError('');
    try {
      const wasEmpty = orgs.length === 0;
      const org = await apiFetch('/organizations', { method: 'POST', body: { name: name.trim() } });
      setName('');
      await load();
      if (wasEmpty && org?.id) await setActive(org.id); // first org → auto-activate
      flash(`Created "${org.name}"`);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const remove = async (org) => {
    if (!window.confirm(`Delete "${org.name}"? Its leaderboard and stored winners will be removed.`)) return;
    setError('');
    try {
      await apiFetch(`/organizations/${org.id}`, { method: 'DELETE' });
      await load();
      flash('Organization deleted');
    } catch (e) { setError(e.message); }
  };

  if (viewOrg) return <OrgDetail orgId={viewOrg} onBack={() => { setViewOrg(null); load(); }} />;

  const activeOrg = orgs.find(o => o.id === activeId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 4 }}>🏢 Organizations</h1>
        <p style={{ color: T.muted, fontSize: 13, maxWidth: 720 }}>
          Rent the system per organization for games-day events. New games are auto-tagged to the
          active organization, and their scores roll up into that org's lifetime leaderboard.
        </p>
      </div>

      {error   && <div style={{ background: T.red + '22',   border: `1px solid ${T.red}44`,   borderRadius: 10, padding: '10px 16px', color: T.red,   fontSize: 13 }}>{error}</div>}
      {success && <div style={{ background: T.green + '22', border: `1px solid ${T.green}44`, borderRadius: 10, padding: '10px 16px', color: T.green, fontSize: 13 }}>{success}</div>}

      <Card accent={activeOrg ? T.green : undefined}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase' }}>Active organization</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: activeOrg ? T.green : T.muted, marginTop: 4 }}>
              {activeOrg ? activeOrg.name : 'None — new games are not tagged'}
            </div>
          </div>
          {activeOrg && <Btn variant="ghost" onClick={() => setActive(null)}>Clear</Btn>}
        </div>
      </Card>

      <Card>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>New organization</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Input label="Organization name" value={name} onChange={setName} placeholder="e.g. Acme Corp" />
          </div>
          <Btn onClick={create} disabled={busy || !name.trim()}>{busy ? 'Creating…' : '+ Create'}</Btn>
        </div>
      </Card>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700 }}>Organizations ({orgs.length})</h2>
          <Btn variant="ghost" onClick={load}>↻ Refresh</Btn>
        </div>
        {orgs.length === 0 ? (
          <div style={{ color: T.muted, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No organizations yet — create one above.</div>
        ) : orgs.map(o => {
          const isActive = o.id === activeId;
          return (
            <div key={o.id} style={{ padding: '12px 16px', borderRadius: 12, marginBottom: 8, background: T.raised, border: `1px solid ${isActive ? T.green + '55' : T.border}`, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {o.name}
                  {isActive && <Badge color={T.green}>ACTIVE</Badge>}
                </div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>
                  {o.slug} · created {o.createdAt ? new Date(o.createdAt).toLocaleDateString() : '—'}
                </div>
              </div>
              <Badge color={T.muted}>{o.id}</Badge>
              <Btn variant="ghost" onClick={() => setViewOrg(o.id)} style={{ fontSize: 12 }}>📊 View</Btn>
              {!isActive && <Btn variant="ghost" onClick={() => setActive(o.id)} style={{ fontSize: 12 }}>Set Active</Btn>}
              <Btn variant="ghost" onClick={() => remove(o)} style={{ fontSize: 12 }}>🗑</Btn>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

// ─── Main Admin Panel ─────────────────────────────────────────────────────────
export default function AdminPanel() {
  const [tab,             setTab]             = useState('dashboard');
  const [games,           setGames]           = useState({});
  const [puzzles,         setPuzzles]         = useState([]);
  const [surveys,         setSurveys]         = useState([]);
  const [bridgeConnected, setBridgeConnected] = useState(false);
  const [liveEvents,      setLiveEvents]      = useState([]);
  const [crRefreshTick,   setCrRefreshTick]   = useState(0);
  const [navOpen,         setNavOpen]         = useState(false); // mobile drawer (≤768px only)

  // Stable Ably client + set of subscribed gameIds (persists across renders)
  const ablyClientRef      = useRef(null);
  const subscribedGamesRef = useRef(new Set());

  // Refresh a single game without reloading all four game-type lists
  const refreshGame = useCallback(async (gameId, gameType) => {
    try {
      let data;
      if (gameType === 'americasays')   data = await apiFetch(`/americasays/${gameId}`);
      else if (gameType === 'droppan')  data = await apiFetch(`/droppan/${gameId}`);
      else if (gameType === 'geoguess') return;
      else if (gameType === 'chainreaction') data = await apiFetch(`/chainreaction/${gameId}`);
      else data = await apiFetch(`/games/${gameId}`);
      setGames(prev => ({ ...prev, [gameId]: { ...prev[gameId], ...data, id: gameId, gameType } }));
    } catch {}
  }, []);

  const loadGames = useCallback(async () => {
    try {
      const [crossData, asData, ggData, dpData, crData] = await Promise.all([
        apiFetch('/games'),
        apiFetch('/americasays'),
        apiFetch('/geoguess'),
        apiFetch('/droppan'),
        apiFetch('/chainreaction').catch(() => ({ games: [] })),
      ]);
      const crossGames = Object.fromEntries(
        Object.entries(crossData.games || {}).map(([k, v]) => [k, { ...v, gameType: 'crossword' }])
      );
      const asGames = Object.fromEntries(
        (asData.games || []).map(g => [g.id, { ...g, gameType: 'americasays' }])
      );
      const ggGames = Object.fromEntries(
        (ggData.games || []).map(g => [g.id, { ...g, gameType: 'geoguess' }])
      );
      const dpGames = Object.fromEntries(
        (dpData.games || []).map(g => [g.id, { ...g, gameType: 'droppan' }])
      );
      const crGames = Object.fromEntries(
        (crData.games || []).map(g => [g.id, { ...g, gameType: 'chainreaction' }])
      );
      setGames({ ...crossGames, ...asGames, ...ggGames, ...dpGames, ...crGames });
    } catch {}
  }, []);

  const loadPuzzles = useCallback(async () => {
    try { const data = await apiFetch('/puzzles'); setPuzzles(data.puzzles || []); } catch {}
  }, []);

  const loadSurveys = useCallback(async () => {
    try { const data = await apiFetch('/surveys'); setSurveys(data.surveys || []); } catch {}
  }, []);

  useEffect(() => { loadGames(); loadPuzzles(); loadSurveys(); }, []);

  // ── Effect A: create a stable Ably client once ───────────────────────────────
  // Handles admin channel (game create/delete) and bridge presence.
  // Never destroyed due to game data changes.
  useEffect(() => {
    const client = new Ably.Realtime({ authUrl: ABLY_AUTH_URL, autoConnect: true });
    ablyClientRef.current = client;

    const adminCh = client.channels.get('crossword:admin');
    adminCh.subscribe('bridge-status', msg => setBridgeConnected(msg.data?.connected || false));
    adminCh.subscribe('game-created',           () => loadGames());
    adminCh.subscribe('americasays-created',    () => loadGames());
    adminCh.subscribe('geoguess-created',       () => loadGames());
    adminCh.subscribe('droppan-game-created',   () => loadGames());
    adminCh.subscribe('droppan-deleted',        () => loadGames());
    adminCh.subscribe('chainreaction-created',  () => loadGames());
    adminCh.subscribe('chainreaction-deleted',  () => loadGames());

    const bridgePresence = client.channels.get('signal:wa:bridge').presence;
    bridgePresence.get().then(members => setBridgeConnected(members.length > 0)).catch(() => {});
    bridgePresence.subscribe('enter', () => setBridgeConnected(true));
    bridgePresence.subscribe('leave', () => {
      bridgePresence.get().then(members => { if (members.length === 0) setBridgeConnected(false); }).catch(() => {});
    });

    return () => {
      client.close();
      ablyClientRef.current = null;
      subscribedGamesRef.current.clear();
    };
  }, [loadGames]); // loadGames is a stable useCallback — this runs once

  // ── Effect B: subscribe to game channels as games appear ─────────────────────
  // Depends on `games` but only ADDS subscriptions — never recreates the Ably client.
  // Uses refreshGame (single-game fetch) for roster/score events to avoid
  // the 4-API-call churn of loadGames() on every player join.
  useEffect(() => {
    const client = ablyClientRef.current;
    if (!client) return;

    const addEvent = (ev) => setLiveEvents(prev => [ev, ...prev].slice(0, 100));

    Object.entries(games).forEach(([gameId, game]) => {
      if (subscribedGamesRef.current.has(gameId)) return; // already wired up
      subscribedGamesRef.current.add(gameId);

      const gt = game.gameType;

      if (gt === 'crossword' || !gt) {
        const ch = client.channels.get(`crossword:game:${gameId}`);
        ch.subscribe('cell-filled', msg => {
          const d = msg.data;
          addEvent({
            type: 'correct', player: d.player.name, initials: d.player.initials,
            teamColor: d.team?.color, answer: d.answer,
            clue: `${d.clueNumber} ${d.direction === 'across' ? 'Across' : 'Down'}`,
            pts: d.pointsAwarded, ts: d.ts || Date.now(), gameId, gameType: 'crossword',
          });
          refreshGame(gameId, 'crossword');
        });
        ch.subscribe('incorrect-answer', msg => {
          const d = msg.data;
          addEvent({
            type: 'incorrect', player: d.playerName, initials: d.playerInitials,
            teamColor: d.teamColor, answer: d.attempt,
            clue: d.clueRef, ts: d.ts || Date.now(), gameId, gameType: 'crossword',
          });
        });
        ch.subscribe('player-joined', msg => {
          addEvent({
            type: 'player-joined', player: msg.data.name, initials: msg.data.initials,
            teamColor: msg.data.teamColor, ts: msg.data.ts || Date.now(), gameId, gameType: 'crossword',
          });
          refreshGame(gameId, 'crossword');
        });
        ch.subscribe('player-left', msg => {
          addEvent({
            type: 'player-left', player: msg.data.name, initials: msg.data.initials,
            teamColor: msg.data.teamColor, ts: msg.data.ts || Date.now(), gameId, gameType: 'crossword',
          });
          refreshGame(gameId, 'crossword');
        });
        ch.subscribe('score-update', () => refreshGame(gameId, 'crossword'));
        ch.subscribe('game-state',   () => loadGames());
      }

      if (gt === 'americasays') {
        const ch = client.channels.get(`americasays:game:${gameId}`);
        ch.subscribe('answer-revealed', msg => {
          const d = msg.data;
          if (d.revealedByAdmin) return;
          addEvent({
            type: 'as-answer', player: d.playerName, initials: d.playerInitials,
            teamColor: d.teamColor, answer: d.answerText || `Answer #${d.answerIdx + 1}`,
            pts: d.pointsAwarded, ts: d.ts || Date.now(), gameId, gameType: 'americasays',
          });
          refreshGame(gameId, 'americasays');
        });
        ch.subscribe('player-joined', msg => {
          addEvent({
            type: 'player-joined', player: msg.data.name, initials: msg.data.initials,
            teamColor: msg.data.teamColor, ts: msg.data.ts || Date.now(), gameId, gameType: 'americasays',
          });
          refreshGame(gameId, 'americasays');
        });
        ch.subscribe('player-left', msg => {
          addEvent({
            type: 'player-left', player: msg.data.name, initials: msg.data.initials,
            teamColor: msg.data.teamColor, ts: msg.data.ts || Date.now(), gameId, gameType: 'americasays',
          });
          refreshGame(gameId, 'americasays');
        });
        ch.subscribe('score-update',    () => refreshGame(gameId, 'americasays'));
        ch.subscribe('question-changed', msg => {
          addEvent({
            type: 'question', player: 'HOST', initials: '🎯',
            answer: msg.data.prompt || `Question ${(msg.data.questionIdx || 0) + 1}`,
            ts: msg.data.ts || Date.now(), gameId, gameType: 'americasays',
          });
        });
        ch.subscribe('game-state', () => loadGames());
      }

      if (gt === 'droppan') {
        const ch = client.channels.get(`droppan:game:${gameId}`);
        ch.subscribe('round-winner', msg => {
          const d = msg.data;
          addEvent({
            type: 'droppan-win', player: d.playerName, initials: d.initials,
            answer: d.tier === 'symbol' ? '✦ SYMBOL' : d.tier === 'main' ? '● MAIN' : '○ MINOR',
            pts: d.points, ts: Date.now(), gameId, gameType: 'droppan',
          });
        });
        ch.subscribe('bet-placed', msg => {
          const d = msg.data;
          addEvent({
            type: 'droppan-bet', player: d.playerName, initials: d.initials,
            answer: `Bet ${d.amount} on #${d.guess}`, ts: Date.now(), gameId, gameType: 'droppan',
          });
        });
        ch.subscribe('player-joined', msg => {
          addEvent({
            type: 'player-joined', player: msg.data.name, initials: msg.data.initials,
            teamColor: msg.data.teamColor, ts: msg.data.ts || Date.now(), gameId, gameType: 'droppan',
          });
          refreshGame(gameId, 'droppan');
        });
        ch.subscribe('player-left', msg => {
          addEvent({
            type: 'player-left', player: msg.data.name, initials: msg.data.initials,
            teamColor: msg.data.teamColor, ts: msg.data.ts || Date.now(), gameId, gameType: 'droppan',
          });
          refreshGame(gameId, 'droppan');
        });
        ch.subscribe('score-update', () => refreshGame(gameId, 'droppan'));
        ch.subscribe('round-start',  () => refreshGame(gameId, 'droppan'));
        ch.subscribe('game-state',   () => loadGames());
        ch.subscribe('game-end',     () => loadGames());
      }

      if (gt === 'chainreaction') {
        const ch = client.channels.get(`chainreaction:game:${gameId}`);
        const crRefresh = () => { loadGames(); setCrRefreshTick(t => t + 1); };
        ch.subscribe('speed-chain-start',    crRefresh);
        ch.subscribe('speed-chain-complete', crRefresh);
        ch.subscribe('bonus-round-start',    crRefresh);
        ch.subscribe('bonus-complete',       crRefresh);
        ch.subscribe('game-state',           crRefresh);
        ch.subscribe('slot-solved',          () => refreshGame(gameId, 'chainreaction'));
      }
    });
  }, [games, loadGames, refreshGame]); // safe: only adds subscriptions, never recreates client

  const navItems = [
    { id: 'dashboard',      label: 'Dashboard',       icon: '🏠' },
    { id: 'create',         label: 'Crossword Game',  icon: '✏️' },
    { id: 'puzzles',        label: 'Puzzles',         icon: '📚' },
    { id: 'americasays',    label: 'America Says',    icon: '🎯' },
    { id: 'surveys',        label: 'Surveys',         icon: '📝' },
    { id: 'geoguess',       label: 'GeoGuess',        icon: '🗺️' },
    { id: 'droppan',        label: 'Drop Pan',        icon: '🎴' },
    { id: 'chainreaction',  label: 'Chain Reaction',  icon: '🔗' },
    { id: 'bracket',        label: 'Bracket',         icon: '🏆' },
    { id: 'organizations',  label: 'Organizations',   icon: '🏢' },
    { id: 'players',        label: 'Players',         icon: '👥' },
    { id: 'monitor',        label: 'Live Feed',       icon: '📡' },
    { id: 'settings',       label: 'Settings',        icon: '⚙️' },
  ];

  const activeNav = navItems.find(n => n.id === tab);

  return (
    <div className="adm-shell" style={{
      minHeight: '100vh',
      background: T.bg,
      fontFamily: "'Inter', 'Sora', sans-serif",
      color: T.text,
      display: 'flex',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Inter:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        input, select, textarea, button { font-family: inherit; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,.1); border-radius: 3px; }
        input::placeholder { color: rgba(255,255,255,.2); }
        textarea::placeholder { color: rgba(255,255,255,.2); }
        tr:hover td { background: rgba(255,255,255,.02); }
        @keyframes adminFadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes liveIn { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:translateX(0)} }
        @keyframes bridgePulse { 0%,100%{opacity:1} 50%{opacity:.3} }

        /* ══ Responsive layer ═══════════════════════════════════════════════
           The panel is styled entirely with inline styles, which normally beat
           stylesheet rules — so these overrides use !important, which wins over
           non-important inline styles. Everything that changes layout lives
           inside a media query, so desktop (>1024px) renders unchanged.
           NOTE: html,body are globally overflow:hidden (for the TV boards), so
           .adm-main must stay the scroll container at every width — never swap
           it for body scrolling.
        ═══════════════════════════════════════════════════════════════════ */

        /* Horizontal escape hatch for wide tables. Unconditional: a scrollbar
           only appears if content actually overflows, so desktop is unaffected. */
        .adm-tablewrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .adm-topbar, .adm-backdrop { display: none; }

        /* ── Touch ergonomics (≤1024px: phones AND tablets) ──
           16px minimum stops iOS from auto-zooming the page on field focus,
           plus a 44px tap-target floor (iOS HIG / Android 48dp).
           range/color/checkbox are excluded — they never trigger the zoom and
           a height floor distorts their native rendering. */
        @media (max-width: 1024px) {
          input:not([type="range"]):not([type="color"]):not([type="checkbox"]),
          select, textarea {
            font-size: 16px !important;
            min-height: 44px;
          }
          input[type="range"]    { min-height: 34px; }
          input[type="checkbox"] { min-width: 20px; min-height: 20px; }
          input[type="color"]    { min-height: 38px; }
          button { min-height: 44px; }
          .adm-navbtn { min-height: 46px; }

          /* A <select> is sized by its widest <option>, and a 1fr grid track has
             min-width:auto — so a long option ("Manual — admin presses Reveal
             All") stretched the track past the card and the text was clipped
             unreachably. Let tracks shrink, then clamp the field itself. */
          .adm-grid > *, .adm-grid-stats > * { min-width: 0; }
          input, select, textarea { max-width: 100%; }
          select { text-overflow: ellipsis; }
        }

        /* ── Tablet (769–1024px): sidebar collapses to an icon rail ── */
        @media (min-width: 769px) and (max-width: 1024px) {
          .adm-sidebar { width: 68px !important; }
          .adm-brand, .adm-navlabel, .adm-bridge-label { display: none !important; }
          .adm-logo { justify-content: center !important; }
          .adm-navbtn { justify-content: center !important; padding: 11px 0 !important; gap: 0 !important; }
          .adm-bridge { justify-content: center !important; padding: 14px 0 !important; }
          .adm-main { padding: 20px 18px !important; max-height: 100dvh !important; }
          .adm-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .adm-grid-stats { grid-template-columns: repeat(4, 1fr) !important; }
        }

        /* ── Phone / small tablet (≤768px): sidebar becomes a slide-over drawer ── */
        @media (max-width: 768px) {
          .adm-shell { min-height: 100dvh !important; }
          .adm-sidebar {
            position: fixed !important; top: 0; bottom: 0; left: 0;
            width: 264px !important; z-index: 120;
            transform: translateX(-100%);
            transition: transform .24s ease;
            overflow-y: auto;
          }
          .adm-sidebar.adm-open { transform: translateX(0); }
          .adm-backdrop {
            display: block; position: fixed; inset: 0; z-index: 110;
            background: rgba(0,0,0,.62); backdrop-filter: blur(2px);
            animation: adminFadeIn .2s ease;
          }
          .adm-main {
            padding: 0 !important;
            max-height: 100dvh !important;
            width: 100%;
          }
          /* Sticky mobile header lives inside the scroll container */
          .adm-topbar {
            display: flex; align-items: center; gap: 12px;
            position: sticky; top: 0; z-index: 60;
            margin: 0 0 14px; padding: 11px 14px;
            background: rgba(13,19,32,.95);
            backdrop-filter: blur(10px);
            border-bottom: 1px solid rgba(255,255,255,.07);
          }
          .adm-body { padding: 0 14px 28px; }

          /* Collapse every multi-column grid to a single column */
          .adm-grid { grid-template-columns: 1fr !important; }
          .adm-grid-stats { grid-template-columns: repeat(2, 1fr) !important; }

          /* Tab strips scroll sideways instead of overflowing the viewport.
             The right-edge fade is the only affordance that off-screen tabs
             exist — without it a hidden sub-tab (e.g. Chain Reaction › Games)
             is unreachable, since the scrollbar is suppressed on touch. */
          .adm-tabs {
            width: 100% !important;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            -webkit-mask-image: linear-gradient(to right, #000 82%, transparent 100%);
                    mask-image: linear-gradient(to right, #000 82%, transparent 100%);
          }
          .adm-tabs::-webkit-scrollbar { display: none; }
          .adm-tabs > * { flex-shrink: 0; }

          /* Keep table columns legible inside the scroll wrapper */
          .adm-tablewrap table { min-width: 640px; }
        }
      `}</style>

      {/* Drawer backdrop — CSS-hidden above 768px, so desktop never sees it */}
      {navOpen && <div className="adm-backdrop" onClick={() => setNavOpen(false)} />}

      {/* ── Sidebar ── */}
      <div className={`adm-sidebar${navOpen ? ' adm-open' : ''}`} style={{
        width: 228, background: T.surface,
        borderRight: `1px solid ${T.border}`,
        padding: '0 0 20px', display: 'flex', flexDirection: 'column', flexShrink: 0,
        boxShadow: '4px 0 24px rgba(0,0,0,.3)',
      }}>
        {/* Logo */}
        <div style={{ padding: '22px 22px 20px', borderBottom: `1px solid ${T.border}`, marginBottom: 8 }}>
          <div className="adm-logo" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: `linear-gradient(135deg, ${T.accent}, #818CF8)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, boxShadow: `0 4px 16px ${T.accent}44`, flexShrink: 0,
            }}>✏️</div>
            <div className="adm-brand">
              <div style={{ fontSize: 15, fontWeight: 800, color: T.text, fontFamily: "'Sora', sans-serif", letterSpacing: '.2px' }}>Crossword</div>
              <div style={{ fontSize: 10, color: T.muted, marginTop: 1, fontWeight: 600, letterSpacing: '.5px', textTransform: 'uppercase' }}>Game Admin</div>
            </div>
          </div>
        </div>

        <nav style={{ padding: '6px 10px', flex: 1 }}>
          {navItems.map(item => {
            const active = tab === item.id;
            return (
              <button key={item.id} className="adm-navbtn"
                onClick={() => { setTab(item.id); setNavOpen(false); }} style={{
                display: 'flex', width: '100%', textAlign: 'left', alignItems: 'center', gap: 10,
                padding: '9px 12px', marginBottom: 2,
                background: active ? `${T.accent}22` : 'transparent',
                color: active ? T.text : T.muted,
                border: 'none',
                borderRadius: 10,
                borderLeft: `3px solid ${active ? T.accent : 'transparent'}`,
                fontSize: 13, fontWeight: active ? 700 : 500,
                cursor: 'pointer', transition: 'all .15s',
                fontFamily: 'inherit',
              }}>
                <span style={{ fontSize: 15, opacity: active ? 1 : .6 }}>{item.icon}</span>
                <span className="adm-navlabel">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Bridge status */}
        <div className="adm-bridge" style={{ padding: '14px 22px', borderTop: `1px solid ${T.border}`, margin: '0 0 0', display: 'flex', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
              background: bridgeConnected ? T.green : T.red,
              boxShadow: bridgeConnected ? `0 0 8px ${T.green}` : 'none',
              animation: bridgeConnected ? 'none' : 'bridgePulse 1.5s ease infinite',
            }} />
            <span className="adm-bridge-label" style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>
              WA Bridge {bridgeConnected ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      {/* Stays the scroll container at every width: html,body are globally
          overflow:hidden for the TV boards, so body scrolling is not available. */}
      <div className="adm-main" style={{ flex: 1, minWidth: 0, padding: '28px 32px', overflowY: 'auto', maxHeight: '100vh', animation: 'adminFadeIn .3s ease' }}>
        {/* Mobile header — CSS-hidden above 768px */}
        <div className="adm-topbar">
          <button onClick={() => setNavOpen(true)} aria-label="Open navigation" style={{
            background: T.raised, border: `1px solid ${T.border}`, borderRadius: 10,
            color: T.text, fontSize: 17, width: 42, height: 42, flexShrink: 0,
            cursor: 'pointer', lineHeight: 1,
          }}>☰</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span style={{ fontSize: 16 }}>{activeNav?.icon}</span>
            <span style={{
              fontSize: 15, fontWeight: 800, color: T.text, fontFamily: "'Sora', sans-serif",
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{activeNav?.label}</span>
          </div>
          <div style={{
            marginLeft: 'auto', flexShrink: 0, width: 9, height: 9, borderRadius: '50%',
            background: bridgeConnected ? T.green : T.red,
            boxShadow: bridgeConnected ? `0 0 8px ${T.green}` : 'none',
            animation: bridgeConnected ? 'none' : 'bridgePulse 1.5s ease infinite',
          }} title={`WA Bridge ${bridgeConnected ? 'Online' : 'Offline'}`} />
        </div>

        <div className="adm-body">
        <BroadcastPanel />
        {tab === 'dashboard'     && <Dashboard games={games} bridgeConnected={bridgeConnected} onRefresh={loadGames} />}
        {tab === 'create'        && <CreateGame puzzles={puzzles} onCreated={loadGames} />}
        {tab === 'puzzles'       && <Puzzles puzzles={puzzles} onRefresh={loadPuzzles} />}
        {tab === 'americasays'   && <AmericaSaysGames surveys={surveys} onRefresh={loadGames} />}
        {tab === 'surveys'       && <SurveyBuilder surveys={surveys} onRefresh={loadSurveys} />}
        {tab === 'geoguess'      && <GeoGuessAdmin onRefresh={loadGames} />}
        {tab === 'droppan'       && <DropPanAdmin onRefresh={loadGames} />}
        {tab === 'chainreaction' && <ChainReactionAdmin onRefresh={loadGames} crRefreshTick={crRefreshTick} />}
        {tab === 'bracket'       && <BracketAdmin onRefresh={loadGames} />}
        {tab === 'organizations' && <OrganizationsAdmin />}
        {tab === 'players'       && <Players games={games} ablyClientRef={ablyClientRef} />}
        {tab === 'monitor'       && <LiveMonitor events={liveEvents} />}
        {tab === 'settings'      && <Settings ablyClientRef={ablyClientRef} />}
        </div>
      </div>
    </div>
  );
}
